import { ApiError, errorText, get } from '@/api/client';
/**
 * Vaktplan: the week grid of plan 0018 part two, against the same routes the
 * old timeline calls. Rows are people, columns are days, the first row is the
 * open shifts. The grid model is built once per data change (model.ts); a
 * cell renders from lookups.
 */
import {
  type ApiShift,
  type ApiShiftType,
  useAbsenceTypes,
  useAbsenceWrites,
  useAbsences,
  useApprove,
  useAvailabilities,
  usePending,
  useRequestWrites,
  useShiftTypes,
  useShiftWrites,
  useShifts,
  useShiftsPlain,
  useTeams,
  useTemplateWrites,
  useTemplates,
  useUsers,
} from '@/api/data';
import type {
  ApiAbsence,
  ApiAbsenceType,
  ApiTemplate,
  ApplyTemplateBody,
  CopyWindowBody,
  ShiftWrite,
} from '@/api/data';
import { useForecast, useLabour, useLabourCost, useMonthBudget, useSaveDaySales } from '@/api/labour';
import { projectLabel, useProjectRequired, useProjectWord, useProjects, useProjectsOn } from '@/api/projects';
import { useSakerStates } from '@/api/saker';
import { invalidateShifts } from '@/api/shifts';
import { useCan } from '@/auth/caps';
import { useAuth } from '@/auth/store';
import { T, useT } from '@/i18n';
import {
  type AbsenceDraft,
  AbsenceDrawer,
  draftOfAbsence,
  newAbsenceDraft,
} from '@/pages/fravaer/AbsenceDrawer';
import { useDeductionState } from '@/pages/fravaer/Deduction';
import { draftForHugin } from '@/poc-chat/Panel';
import { type ActionCard, cancelAction, confirmAction, loadPending } from '@/poc-chat/actions';
import { openHugin, useDock, useProposalAsk } from '@/poc-chat/attach';
import {
  type Entry,
  breakPolicy,
  hours,
  oslo,
  osloISO,
  today as osloToday,
  paidHours,
  plannedHoursOn,
  ruleText,
} from '@/rules';
import { type Paid, costOf, hourlyRateOf, ratesOf } from '@/rules/labour';
import { askBeforeLeaving, hasDirty } from '@/shell/context';
import { type Tenant, useTenant } from '@/shell/tenant';
import { Button, Drawer, Menu, Tip, toast, useToast } from '@/ui';
import { DateInput } from '@/ui/DateInput';
import { PeriodControl } from '@/ui/PeriodControl';
import {
  DN,
  DNL,
  MONTHS,
  NO_VALUE,
  avTone,
  fmtD,
  fmtDs,
  fullName,
  initials,
  monthLabel,
  num,
} from '@/ui/format';
import { HuginMark, Icon } from '@/ui/icons';
import { trapTab } from '@/ui/page';
import { PrintSheet, usePrint } from '@/ui/print';
import * as Dialog from '@radix-ui/react-dialog';
import * as Popover from '@radix-ui/react-popover';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { addDays, dow, isoWeek, mondayOf } from '@timefly/core';
import {
  Fragment,
  type ReactElement,
  type ReactNode,
  cloneElement,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePayMonth } from '../lonn/Month';
import { useFollowUpQueue } from '../oversikt/useQueue';
import { splitParts } from '../timeliste/review';
import { monthOf, prevMonth } from '../timeliste/summary';
import { AbsenceAbout, AbsenceImpact } from './AbsenceImpact';
import ab from './AlertBadge.module.css';
import { AbsenceCard, ShiftCard } from './Card';
import cardCss from './Card.module.css';
import { CoverPanel, type CoverResult } from './CoverPanel';
import { DayDetails } from './DayDetails';
import { DayFigures } from './DayFigures';
import dh from './DayHead.module.css';
import { DayView, type LaneChange } from './DayView';
import { FilterBar } from './FilterBar';
import { FirstPlanHint } from './FirstPlan';
import gc from './GapChip.module.css';
import { AdviceLine, HuginEval, HuginNotes } from './Hugin';
import { weekFigures } from './MoneyRow';
import { MonthFigures } from './MonthFigures';
import { MoveConflict, overlapping } from './MoveConflict';
import nc from './NameCol.module.css';
import {
  AllProposalsPanel,
  FilterPanel,
  type Outcome,
  ProposalPanel,
  PublishPanel,
  type ReviewItem,
  ReviewPanel,
  RulePanel,
} from './Panel';
import { NameDivider, PanelDivider, useNameWidth, usePanelWidth } from './PanelDivider';
import { PendingCreates } from './PendingCreates';
import { PersonPanel } from './PersonPanel';
import { DayStrip, MonthCal, usePhone } from './Phone';
import { PlanDrawer } from './PlanDrawer';
import { Quick, type QuickAt } from './Quick';
import { ReviewBar, type SakerNav } from './ReviewBar';
import {
  type DraftShift,
  type EditFocus,
  ShiftDrawer,
  askDeleteReason,
  bodyOf,
  carryTo,
  draftOf,
  newDraft,
} from './ShiftDrawer';
import sp from './SidePanel.module.css';
import { StatusIcon } from './StatusIcon';
import { defOf, tasksBodyOf } from './TaskEditor';
import tb from './Toolbar.module.css';
import { SHEET_QUERY, WarningPopover } from './WarningPopover';
import { MonthBurn } from './WeekCharts';
import { WeekFigures } from './WeekFigures';
import { type Apply, adviceFor, evaluate, nudges, ranked } from './advice';
import { fairMark, fairSpread } from './chartFigures';
import { type PlanState, copyMachine, idle, newKey, signatureOf } from './copyMachine';
import { type Seen, changedSince, seenOf, tiersOf, verdictKey } from './coverFlow';
import { type Section, sectionDays, sectionsOf } from './departments';
import { fromSearch } from './link';
import { BUSY, UNKNOWN, useShiftLock } from './lock';
import { movedLunch, plannedLunch } from './lunch';
import {
  type Cell,
  type Row,
  SORT,
  buildGrid,
  dateOf,
  daySummary,
  earliestStart,
  focusDay,
  friendlyType,
  monthLength,
  monthStart,
  panelContextOf,
  readSort,
  snapStart,
  timeText,
  typeLabel,
  writeSort,
} from './model';
import { paperRows } from './paper';
import { BasisRow, ChangeStrip, PlanningPanel } from './planning/PlanningPanel';
import { usePlanning } from './planning/usePlanning';
import { onGrid } from './proposals';
import { reviewOf } from './review';
import {
  type ActInput,
  CATS as CASE_CATS,
  type CaseAction,
  type CaseOutcome,
  type ReviewCase,
} from './reviewBar.types';
import { breakdown, casesForKeys, casesOf, gapsOf } from './reviewBarAdapter';
import { deferralsOf, dueDeferrals, hoursMin, isArea, isTab, listsOf } from './saker';
import { hourGaps, needHours, salesFor } from './staffing';
import { type TMode, asMode, readTMode, writeTMode } from './timeMode';
import type { Issue, Scope } from './warningPopover.types';
import { type AdapterContext, CATS, issuesOf, planned, reviewItemsOf } from './warningPopoverAdapter';

interface SaveForm {
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  /** the period on screen, or only the shifts the manager picked */
  scope: 'window' | 'selected';
  busy: boolean;
  problem: string;
}
/** How far a week is from the agreed hours; -1 when the agreement is withheld, so those rows sort last. */
/** Marks the week grid while days lie right of what shows, so its edge fades as the cue to scroll (#272 A-S05). */
export const moreDays = (el: HTMLElement) =>
  el.classList.toggle('more', el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
const dev = (w: { hours: number; norm: number | null }) => (w.norm == null ? -1 : Math.abs(w.hours - w.norm));

/** What the server said about the employee's notice, apart from whether the shift was published. */
const NOTICE: Record<string, string> = {
  processed: 'Publisert. Den ansatte er varslet.',
  failed: 'Publisert, men varselet til den ansatte feilet.',
  not_requested: 'Publisert. Ingen varsel sendt.',
};

/** "19. okt." the way V19 writes a date in the week label */
const okt = (d: string) =>
  `${Number(d.slice(8, 10))}. ${(MONTHS[Number(d.slice(5, 7)) - 1] ?? '').slice(0, 3)}.`;

/** «Uke 45, 2.–8. nov.» for a Monday, as Saker's «Endre» lists the weeks (#286) */
const weekLabel = (m: string) => {
  const sun = addDays(m, 6);
  return `${T('Uke')} ${isoWeek(m)}, ${m.slice(5, 7) === sun.slice(5, 7) ? `${Number(m.slice(8, 10))}.` : okt(m)}–${okt(sun)}`;
};

/** Saker's place from the address (#286): ?sak=, ?saker=, ?omrade=, ?periode=andre, ?sok= */
function sakerNavOf(p: URLSearchParams): SakerNav {
  const tab = p.get('saker');
  const area = p.get('omrade');
  return {
    tab: isTab(tab) ? tab : 'open',
    period: p.get('periode') === 'andre' ? 'other' : 'current',
    area: isArea(area) ? area : null,
    key: p.get('sak') || null,
    q: (p.get('sok') ?? '').slice(0, 80),
  };
}
const SAKER_KEYS = ['sak', 'saker', 'omrade', 'periode', 'sok'];
/**
 * Saker's place into the address without a new history entry (#286): with the week and the team it is about, so a
 * reload and Kontroll's «Tilbake til saken» come back to the same task in the same scope. Nothing else moves.
 */
function writeSakerNav(n: SakerNav, at: { monday: string; teamId: number | null }) {
  const u = new URL(window.location.href);
  if (!u.pathname.startsWith('/vaktplan')) return;
  for (const k of SAKER_KEYS) u.searchParams.delete(k);
  const any = n.key || n.tab !== 'open' || n.area || n.period === 'other' || n.q;
  if (n.key) u.searchParams.set('sak', n.key);
  if (n.tab !== 'open') u.searchParams.set('saker', n.tab);
  if (n.area) u.searchParams.set('omrade', n.area);
  if (n.period === 'other') u.searchParams.set('periode', 'andre');
  if (n.q) u.searchParams.set('sok', n.q);
  if (any) {
    u.searchParams.set('uke', at.monday);
    if (at.teamId != null) u.searchParams.set('lag', String(at.teamId));
    else u.searchParams.delete('lag');
  }
  const next = `${u.pathname}${u.search}${u.hash}`;
  if (next !== `${window.location.pathname}${window.location.search}${window.location.hash}`)
    window.history.replaceState(window.history.state, '', next);
}

/**
 * Kontroll on exactly a worklist's records (#286 S09): ?poster= the ids, ?fra=saker, the period that holds them,
 * the team, and ?tilbake= this address, so «Tilbake til saken» opens the same task in the same scope.
 */
function kontrollOf(c: ReviewCase, team: string | null): string {
  const ids = (c.members ?? []).map((m) => m.id);
  const dates = (c.members ?? []).map((m) => m.date).sort();
  const first = dates[0] ?? '';
  const lastDate = dates.at(-1) ?? first;
  const q = new URLSearchParams({ fane: 'registreringer', fra: 'saker', poster: ids.join(',') });
  if (first && mondayOf(first) === mondayOf(lastDate)) q.set('uke', mondayOf(first));
  else if (first) q.set('mnd', first.slice(0, 7));
  if (team) q.set('team', team);
  q.set('tilbake', `${window.location.pathname}${window.location.search}`);
  return `/timeliste?${q}`;
}

const STATUS: [string, string][] = [
  ['upub', 'Ikke publisert'],
  ['brudd', 'Brudd'],
  ['advarsel', 'Advarsler'],
  ['vent', 'Venter på godkjenning'],
  ['bytte', 'Bytte og tilbud'],
];
const RULES: [string, string][] = [
  // the week is measured the way the server warns: the person's agreed week, else 37,5 t (M5)
  ['Alminnelig arbeidstid', 'maks 9 t/dag; uken mot avtalt tid, ellers 37,5 t (AML §10-4)'],
  ['Hvile', 'minst 11 t mellom vakter (§10-8)'],
  ['Ukehvile', '35 t sammenhengende per 7 dager'],
  ['Søndag', 'annenhver søndag fri (§10-8)'],
  ['Pause', '30 min fra 5,5 t (§10-9)'],
  ['Toleranse', '15 min på stempling'],
];

/**
 * The week the Vaktplan opens on. A demo from Friday to Sunday opens on next week: its week is built
 * around today, so late in the week nearly every shift is clocked and cannot be dragged, which reads as
 * a broken screen to a prospect (owner, 2026-09-27). A real company always opens on this week.
 */
export function startWeek(today: string, isDemo: boolean, nextWeekHasShifts = true): string {
  const weekday = new Date(`${today}T12:00:00Z`).getUTCDay(); // 0 Sunday, 5 Friday, 6 Saturday
  return isDemo && (weekday === 0 || weekday >= 5) && nextWeekHasShifts
    ? mondayOf(addDays(today, 7))
    : mondayOf(today);
}
/**
 * Whether the weekend demo rule above applies today, so next week's shifts must be read before the
 * opening week is settled. Next week is left empty for Hugin (issue #39); when it has no shifts the
 * demo opens on this week instead, so an empty page does not greet the visitor (issue #62, decided
 * 2026-10-12 overnight, recommended option; owner may reverse).
 */
const demoWeekend = (today: string, isDemo: boolean) => startWeek(today, isDemo) !== mondayOf(today);

// one empty list, so the planning overlay's memo holds while the week loads
const NONE: ApiShift[] = [];
/** Below this the Saker and Hugin column is a sheet over the plan, not a column beside it (SidePanel.module.css). */
const SIDE_SHEET = '(max-width: 1100px)';

export default function Vaktplan() {
  const t = useT();
  const can = useCan();
  const { tenant, mode } = useTenant();
  /**
   * Planning on is Vaktplan; off is «Uka» (#118, plan 0033 decision 2): the same week grid, its cards
   * what the clock-ins made, with publishing, review, need and cover and Hugin's grid proposals hidden.
   * Switching the flag in Innstillinger re-reads the company, so the page follows without a reload.
   */
  const planning = mode === 'rota';
  const labour = useLabour();
  const [params] = useSearchParams();
  // the address, read once (link.ts): the week navigator owns the week after this
  const [link] = useState(() => fromSearch(params));
  // where Saker was, from the address (#286): a reload, Back or Kontroll's «Tilbake til saken» comes back there
  const [sakerNav0] = useState(() => sakerNavOf(params));
  const [monday, setMonday] = useState(
    () => link.day ?? link.week ?? startWeek(osloToday(), Boolean(tenant?.is_demo)),
  );
  // Opened without a week in the address, the page settles its week once the company is known: a
  // demo at a weekend opens on next week only when next week has shifts (issue #62). Reading next
  // week uses the week view's own query key, so the grid reuses the answer.
  const settling = useRef(!link.day && !link.week);
  const settleToday = osloToday();
  const settleNext = mondayOf(addDays(settleToday, 7));
  const weekendDemo = settling.current && !!tenant && demoWeekend(settleToday, Boolean(tenant.is_demo));
  const nextQ = useShifts(addDays(settleNext, -1), addDays(settleNext, 7), weekendDemo);
  useEffect(() => {
    if (!settling.current || !tenant) return;
    if (weekendDemo && !nextQ.data && !nextQ.isError) return;
    settling.current = false;
    const has = nextQ.data?.shifts.some((x) => {
      const d = dateOf(x);
      return d >= settleNext && d <= addDays(settleNext, 6);
    });
    setMonday(startWeek(settleToday, Boolean(tenant.is_demo), has !== false));
  }, [tenant, weekendDemo, nextQ.data, nextQ.isError, settleNext, settleToday]);
  // Day, week, two weeks and month, the views the original schedule had
  // (TF-SCHEDULE-COMPLETE). `monday` is the first day of whatever window is shown.
  const [view, setView] = useState<'day' | 'week' | 'two' | 'month'>(() => (link.day ? 'day' : 'week'));
  const [teamF, setTeamF] = useState<Set<number>>(new Set());
  // ?lag=<id>, from the global search (#50): the plan opens with that team filtered
  const lagParam = Number(params.get('lag')) || 0;
  useEffect(() => {
    if (lagParam) setTeamF(new Set([lagParam]));
  }, [lagParam]);
  const [statusF, setStatusF] = useState<Set<string>>(new Set());
  // the chosen order survives a reload, as it did on the old timeline (model.ts SORT_KEY)
  const [sort, setSort] = useState(readSort);
  const [legend, setLegend] = useState(false);
  // plan 0029 round 3: on a phone the page lists one day; a tap on a card opens a sheet
  const phone = usePhone();
  const warnSheet = usePhone(SHEET_QUERY);
  const navigate = useNavigate();
  // the toolbar folds to one row up to 700 px, wider than the phone list's 560 (issue #86)
  const narrow = usePhone('(max-width:700px)');
  // at 1100 px and down the side column is a modal drawer over the plan (Saker V1.2, #286)
  const sideSheet = usePhone(SIDE_SHEET);
  // a task put off or taken up in Saker changes the count beside «Saker»; the company's, from the server (#286)
  const sakerStatesQ = useSakerStates();
  const [pday, setPday] = useState('');
  // on a phone the filters sit behind one button, so the grid starts near the top (Codex acceptance, 2026-09-15)
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [draft, setDraft] = useState<DraftShift | null>(null);
  // the absence drawer is the Fravær screen's own, opened here on a card or an empty cell
  const [absDraft, setAbsDraft] = useState<AbsenceDraft | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [unresolved, setUnresolved] = useState<number[]>([]);
  const [ledger, setLedger] = useState<{
    what: string;
    ok: number;
    unchanged: number;
    failed: { id: number; message: string }[];
    unknown: number;
    /** published, but the employee's notice failed (publication and notice are told apart) */
    noticeFailed?: number;
  } | null>(null);
  const [quick, setQuick] = useState<QuickAt | null>(null);
  // V19 (owner, 2026-10-08): one panel beside the week, a shift or the week's review; desktop only
  // the shift as it was before the last change from this page, for Hugin's evaluation (#131)
  const [changed, setChangedState] = useState<{ s: ApiShift; week: string } | null>(null);
  const setChanged = (s: ApiShift | null) => setChangedState(s ? { s, week: monday } : null);
  const [panel, setPanel] = useState<
    | {
        kind: 'move-conflict';
        id: number;
        body: Record<string, unknown>;
        start: string;
        end: string;
        userId: number | null;
      }
    | { kind: 'day-details'; date: string }
    | { kind: 'shift'; id: number }
    // Finn erstatter (issue #147 F1)
    | { kind: 'cover'; shiftId: number }
    // a warning popover's Gjennomgå keeps its person, day or shift (issue #146)
    | { kind: 'review'; scope?: { label: string; of: Scope; keys: string[] } }
    | { kind: 'proposal'; id: string }
    | { kind: 'filter' }
    | { kind: 'ai-all' }
    | { kind: 'publish'; ids: number[] }
    | { kind: 'person'; id: number }
    | { kind: 'rule'; person: number; rule: Entry }
    | { kind: 'plan' }
    | { kind: 'save' }
    | null
  >(null);
  const [aiBusy, setAiBusy] = useState(false);
  // Saker and Hugin beside the week on a desktop (handoff 2026-10-24 §4.7, owner 2026-10-25 Q1, Q10): open by
  // default where it docks beside the plan (from 1101 px); narrower it is a sheet over the plan, so it waits for its
  // button (SH-E1). A shift, Filter or Planlegg takes the place while it is open, and closing it brings Saker back
  const [sideOpen, setSideOpen] = useState(() => !window.matchMedia?.(SIDE_SHEET).matches);
  const [sideTab, setSideTab] = useState<'saker' | 'hugin'>('saker');
  // what opened Saker or Hugin, so Escape can hand focus back to it (#272 A-03, A-R10)
  const sideOpener = useRef<HTMLElement | null>(null);
  const rememberSideOpener = () => {
    const a = document.activeElement;
    if (a instanceof HTMLElement && a !== document.body && !a.closest('.vpanel')) sideOpener.current = a;
  };
  // every exit from an edited panel or editor comes through guard(): dates, view, Filter, review,
  // publish, another shift, Merk flere, close and Escape (review F01). The shell asks the one leave
  // question (shell/leave.tsx, issue #72); leaving the page is the shell's own route blocker. `stay`
  // undoes a started exit; a discard closes the shift editor, whose fields would otherwise still
  // show the edit.
  // the side holds one thing at a time, a shift or an absence (UI01-7): a discard clears both
  const guard = (to: string, go: () => void, stay?: () => void) =>
    askBeforeLeaving(to, go, {
      stay,
      discard: () => {
        setDraft(null);
        setAbsDraft(null);
      },
    });
  /** An absence opens in the side in place of the shift editor, through the same guard (UI01-7). */
  const openAbsence = (a: AbsenceDraft, to = T('Fravær')) =>
    guard(to, () => {
      setDraft(null);
      setAbsDraft(a);
    });
  // V19 "Vis i planen" (owner, 2026-10-08): availability off by default, tasks on, money off
  // shown from the start (owner 2026-10-25); Filter's panel turns it off
  const [showAvail, setShowAvail] = useState(true);
  const [showTasks, setShowTasks] = useState(true);
  const [showMoney, setShowMoney] = useState(false);
  const [aiOut, setAiOut] = useState('');
  const [pubOut, setPubOut] = useState<Outcome | null>(null);
  // "Finn ansatt": narrows the rows by name, and so what Publiser covers; its panel names the scope
  // and lists every shift before anything is written (handoff 2026-10-08)
  const [q, setQ] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const shortcut = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        e.key !== '/' ||
        e.ctrlKey ||
        e.metaKey ||
        e.altKey ||
        target.closest('input,textarea,select,[contenteditable="true"],[role="dialog"]')
      )
        return;
      if (searchRef.current) {
        e.preventDefault();
        searchRef.current.focus();
      }
    };
    // in the capture phase, before the global search's own «/» (shell/Search.tsx), which then steps aside:
    // on Vaktplan «/» is «Finn ansatt», as its box shows; ⌘K and Ctrl+K still open the global search
    window.addEventListener('keydown', shortcut, true);
    return () => window.removeEventListener('keydown', shortcut, true);
  }, []);

  // «Uka»'s project filter (#118): a project id, 'uten' for none, or null for all; it narrows the cards
  const [projF, setProjF] = useState<string | null>(null);
  const printer = usePrint();
  const nd = view === 'day' ? 1 : view === 'week' ? 7 : view === 'two' ? 14 : monthLength(monday);
  const days = useMemo(() => Array.from({ length: nd }, (_, i) => addDays(monday, i)), [monday, nd]);
  const last = days[nd - 1] as string;
  /** the period in words, the toolbar's and Saker's (#286): «Uke 45, 2.–8. nov.» */
  const rangeText =
    view === 'month'
      ? monthLabel(monday.slice(0, 7))
      : view === 'day'
        ? `${DNL[dow(monday)]} ${fmtD(monday)}`
        : `${T('Uke')} ${isoWeek(monday)}, ${monday.slice(5, 7) === last.slice(5, 7) ? `${Number(monday.slice(8, 10))}.` : okt(monday)}–${okt(last)}`;

  const shiftsQ = useShifts(addDays(monday, -1), addDays(last, 1));
  // the cards first: the week without the rule check answers in about 0.1 s, the checked read about 0.3 s
  // later adds the marks (owner 2026-10-28: "loading the cards" is what feels slow)
  const plainQ = useShiftsPlain(addDays(monday, -1), addDays(last, 1), !shiftsQ.data);
  const weekData = shiftsQ.data ?? plainQ.data;
  // the reads the cards do not need wait for the plan's own shifts: one server process answers them all, so
  // six checked reads at once kept the week waiting about 1.2 s instead of 0.3 s at BK (RC, 2026-10-28); the
  // previous week's figures, Saker's requests, the templates and Hugin's proposals wait too
  const shown = shiftsQ.isSuccess;
  const fairQ = useShifts(addDays(monday, -21), addDays(monday, -1), view === 'week' && planning && shown);
  // Bemanning mot behov (owner's mockup, 2026-09-27): the week views show need, cost and wage share
  const staffView = view === 'week' || view === 'two';
  // Plan | Faktisk (#201): the week in planned or clocked time, remembered in this browser
  const [tmodePicked, setTModeState] = useState<TMode>(readTMode);
  const setTMode = (m: TMode) => {
    setTModeState(m);
    writeTMode(m);
  };
  const tmode: TMode | undefined = staffView ? tmodePicked : undefined;
  const forecast = useForecast(
    days[0] ?? monday,
    days[days.length - 1] ?? monday,
    (staffView || view === 'month' || view === 'day') && planning,
  );
  const saveDaySales = useSaveDaySales();
  const costWeek = useLabourCost(
    days[0] ?? monday,
    days[days.length - 1] ?? monday,
    (staffView || view === 'day') && planning,
  );
  const prevFrom = addDays(days[0] ?? monday, -7);
  const prevTo = addDays(days[0] ?? monday, -1);
  const costPrev = useLabourCost(prevFrom, prevTo, staffView && planning && view === 'week' && shown);
  const salesPrev = useForecast(prevFrom, prevTo, staffView && planning && view === 'week' && shown);
  // «Lønn mot budsjett» under the month, folded until opened (owner 2026-10-27), remembered in this browser
  const [burnOpen, setBurnOpenState] = useState(() => {
    try {
      return localStorage.getItem('vaktplan.burn') === '1';
    } catch {
      return false;
    }
  });
  const setBurnOpen = (v: boolean) => {
    setBurnOpenState(v);
    try {
      localStorage.setItem('vaktplan.burn', v ? '1' : '0');
    } catch {}
  };
  // the month's «Lønn mot budsjett» (#207): the month's cost, and its budget from PowerOffice Go where connected
  const monthMoney = view === 'month' && planning;
  const costMonth = useLabourCost(days[0] ?? monday, days[days.length - 1] ?? monday, monthMoney);
  const monthBudget = useMonthBudget(
    monday.slice(0, 7),
    monthMoney && tenant?.payroll_system === 'poweroffice',
  );
  // the fair spread under each name (#207): this week and the three before it
  // Over a week, each Monday after the first draws a stronger line and says its week (plan 0029)
  const weekLine = (d: string) => nd > 7 && dow(d) === 0 && d !== days[0];
  /** Moving the window: a day, a week, a fortnight or a month at a time. */
  const step = (dir: 1 | -1) =>
    guard(dir < 0 ? T('forrige periode') : T('neste periode'), () =>
      setMonday(view === 'month' ? monthStart(monday, dir) : addDays(monday, dir * nd)),
    );
  const goToday = () =>
    guard(T('I dag'), () => {
      const t = osloToday();
      setMonday(view === 'day' ? t : view === 'month' ? monthStart(t) : mondayOf(t));
      setPday('');
    });
  const today = osloToday();
  // the day in focus: the one picked on a phone while it is on screen, else today or the first day
  const focus = () => (days.includes(pday) ? pday : focusDay(days, today));
  // The month opens scrolled so today sits first after the names, or at its first day (plan 0029)
  const scroller = useRef<HTMLDivElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: the scroller mounts anew when the view or the phone layout changes
  useEffect(() => {
    const el = scroller.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      moreDays(el);
    });
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [phone, view]);
  // The grid scrolls inside the window: its height is what is left below where it starts, measured,
  // not a guessed 250 px (owner, 2026-09-27: 118 px stood empty under the plan at 900 px high). Where
  // the floating Spør Hugin button shows, it stops 64 px short of the bottom (43 px + 16 px).
  useEffect(() => {
    const fit = () => {
      const el = scroller.current;
      // a height, not only a ceiling (owner 2026-10-22): with few people the plan still fills the
      // window, and the rows grow into it (styles.css .gscroll > .grid) rather than ending mid-screen
      if (!el) return;
      // Vaktplan's side column holds Hugin, so no floating button sits over the plan: 16 px is enough;
      // and the stylesheet's guessed ceiling (styles.css .gscroll) no longer caps the measured height
      // (owner 2026-10-28: "why is there space at the bottom", 76 px stood empty under it); since 2026-11-02 it
      // runs to the window's bottom edge ("until the bottom")
      el.style.maxHeight = 'none';
      el.style.height = `${Math.max(320, window.innerHeight - el.getBoundingClientRect().top - (sideDock.current ? 0 : 64))}px`;
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: another month has to scroll again
  useEffect(() => {
    const box = scroller.current;
    if (view !== 'month' || !box) return;
    const col = box.querySelector<HTMLElement>(`[data-day="${today}"]`);
    const names = box.querySelector<HTMLElement>('.gh > div');
    const nw = names?.offsetWidth ?? 0;
    // near the month's end the scroll stops short of today; it then starts on a whole day, never half a
    // column under the names (#272 A-12)
    const want = Math.min(col ? col.offsetLeft - nw : 0, box.scrollWidth - box.clientWidth);
    box.scrollLeft = snapStart(
      [...box.querySelectorAll<HTMLElement>('.gh [data-day]')].map((c) => c.offsetLeft - nw),
      want,
    );
  }, [view, monday, today]);
  const switchView = (next: 'day' | 'week' | 'two' | 'month') =>
    guard(T('en annen visning'), () => {
      // the focused date is kept: the same day stays in view, the filters and the company do not move
      const keep = focus();
      setView(next);
      setMonday(next === 'day' ? keep : next === 'month' ? monthStart(keep) : mondayOf(keep));
    });
  const usersQ = useUsers();
  const absQ = useAbsences();
  // the empty-cell picker offers these beside the shift types; not asked for at all when
  // this role cannot register an absence for somebody
  const mayAbsence = can('fravaer.registrere');
  const absTypesQ = useAbsenceTypes(mayAbsence);
  const typesQ = useShiftTypes();
  const teamsQ = useTeams();
  const availQ = useAvailabilities();
  const w = useShiftWrites();
  // the project the quick add offers (#115)
  const projectsOn = useProjectsOn();
  const projectRequired = useProjectRequired();
  const projectWord = useProjectWord();
  const projects = useProjects().data ?? [];
  const byProject = !planning && projectsOn;
  const inProject = (s: ApiShift) =>
    !byProject || projF == null || (projF === 'uten' ? s.project_id == null : s.project_id === Number(projF));
  // A status filter narrows the cards, not the rows: a person is kept for the shifts that match, and only those
  // show; several states are chained, a shift has to hold every one (owner 2026-10-28)
  const statesOfShift = (s: ApiShift) => {
    const es = grid.entries.get(s.id) ?? [];
    const st = new Set<string>();
    if (!s.published) st.add('upub');
    if (es.some((e) => e.sev === 'd')) st.add('brudd');
    if (es.some((e) => e.sev === 'w')) st.add('advarsel');
    if (s.punch_in_time && s.punch_out_time && !s.is_approved) st.add('vent');
    if (s.is_swap_requested || s.is_offer_requested) st.add('bytte');
    return st;
  };
  const holdsAll = (s: ApiShift, keys: Set<string>) => {
    if (!keys.size) return true;
    const st = statesOfShift(s);
    return [...keys].every((k) => st.has(k));
  };
  /** what a cell draws: the project filter and the status filter, chained */
  const inView = (s: ApiShift) => inProject(s) && holdsAll(s, statusF);
  const projectOf = (s: ApiShift) =>
    s.project_id == null
      ? projectWord.none
      : (projects.find((p) => p.id === s.project_id)?.name ?? `${projectWord.one} ${s.project_id}`);
  const approve = useApprove();
  /**
   * The one lock every write to an existing shift goes through (review F03, retest R02, issue
   * #71): a move, a resize, a task or edge move, the editor's save, publishing, making open,
   * approving, duplicating and deleting. The rule itself lives in lock.ts; this page only hands
   * it the read that settles an unknown outcome, which must reject when the read failed.
   */
  const lock = useShiftLock(async () => {
    const r = await shiftsQ.refetch();
    if (r.status === 'error') throw r.error;
    return r.data ?? null;
  });
  const { hold, op, busy: busyShift } = lock;
  const pendingQ = usePending(shown);
  const qc = useQueryClient();
  // Hugin's waiting proposals, from the server only (V19 slice 5); the chat panel says when its list changed
  const aiQ = useQuery({
    queryKey: ['poc-actions'],
    queryFn: loadPending,
    enabled: can('assistent.bruke') && planning && shown,
    refetchInterval: 20_000,
  });
  useEffect(() => {
    const f = () => void qc.invalidateQueries({ queryKey: ['poc-actions'] });
    window.addEventListener('timefly:ai-pending', f);
    return () => window.removeEventListener('timefly:ai-pending', f);
  }, [qc]);
  const rw = useRequestWrites();
  const templatesQ = useTemplates(shown);
  const tw = useTemplateWrites();

  const users = usersQ.data ?? [];
  // the global fallback type is shown under a readable name; nothing stored changes (model.ts typeLabel)
  const types = useMemo(() => (typesQ.data ?? []).map(friendlyType), [typesQ.data]);
  // the AI planning flow (#163): while a proposal is shown, the grid draws it over the saved shifts
  const aiPlan = usePlanning({
    week: mondayOf(monday),
    teamId: teamF.size === 1 ? ([...teamF][0] ?? null) : null,
    shifts: weekData?.shifts ?? NONE,
    users,
    company: tenant?.name ?? '',
    onSaved: () => {
      void shiftsQ.refetch();
      // on a phone the sheet steps aside so the receipt above the list shows
      if (phone) setPanel(null);
    },
  });
  const shifts = aiPlan.shifts;
  // Hugin docked in this side panel (owner 18.10): on a desktop, for a person with the assistant, the one
  // conversation opens in the panel's Hugin tab; the phone keeps its sheet and every other page floats
  const huginDock = !phone && can('assistent.bruke');
  // read by the dock listener, which is set up once
  const sideDock = useRef(false);
  /**
   * A ✦ button's question, left in Hugin's box for the manager to send (owner: keep the cost down, nothing is
   * asked until Send). The tab opens first and the words follow once the docked conversation is on screen;
   * handed over earlier, a conversation that was then replaced took them with it.
   */
  const askInHugin = (text: string) => {
    if (sideDock.current) {
      setPanel(null);
      setSideOpen(true);
      setSideTab('hugin');
      setTimeout(() => draftForHugin(text), 120);
    } else draftForHugin(text);
  };
  const panelKind = useRef<string | null>(null);
  useEffect(() => {
    useDock.getState().set({ dockable: huginDock });
    return () => useDock.getState().set({ dockable: false, target: null });
  }, [huginDock]);
  useEffect(() => {
    useDock
      .getState()
      .set({ line: `${T('uke {n}', { n: isoWeek(mondayOf(monday)) })}, ${tenant?.name ?? ''}` });
  }, [monday, tenant?.name]);
  // the saved week as Hugin should know it: published, drafts and open shifts (owner 18.10)
  const savedShifts = shiftsQ.data?.shifts;
  useEffect(() => {
    const from = mondayOf(monday);
    const to = addDays(from, 7);
    const inWeek = (savedShifts ?? []).filter((x) => {
      const d = dateOf(x);
      return d >= from && d < to;
    });
    useDock.getState().set({
      week: {
        week_start: from,
        published: inWeek.filter((x) => x.published).length,
        drafts: inWeek.filter((x) => !x.published).length,
        open: inWeek.filter((x) => x.user_id == null).length,
      },
    });
    return () => useDock.getState().set({ week: null });
  }, [savedShifts, monday]);
  // Hugin planned a week (plan_week): the grid moves to it and Planlegg opens; usePlanning reads the three
  const askedPlan = useProposalAsk((x) => x.asked);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new ask is the trigger; the setters are stable
  useEffect(() => {
    if (!askedPlan) return;
    if (mondayOf(monday) !== askedPlan.week_start) setMonday(askedPlan.week_start);
    aiPlan.setTab('plan');
    setPanel({ kind: 'plan' });
  }, [askedPlan]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: the tab and the panel setters are stable
  useEffect(() => {
    const dock = () => {
      if (sideDock.current && panelKind.current !== 'plan') {
        rememberSideOpener();
        setPanel(null);
        setSideOpen(true);
        setSideTab('hugin');
        return;
      }
      aiPlan.setTab('hugin');
      setPanel({ kind: 'plan' });
    };
    window.addEventListener('timefly:hugin-dock', dock);
    return () => window.removeEventListener('timefly:hugin-dock', dock);
  }, []);
  const grid = useMemo(
    () => buildGrid(days, shifts, users, absQ.data ?? [], availQ.data ?? [], tenant),
    [days, shifts, users, absQ.data, availQ.data, tenant],
  );
  /**
   * Use or drop one proposal: exactly its action id, the change the grid drew. A lost answer is
   * settled by reading the list again: a proposal that is no longer waiting was decided, and the
   * week is read to show what is there.
   */
  const decideAi = async (id: string, use: boolean) => {
    setAiBusy(true);
    setAiOut('');
    try {
      await (use ? confirmAction(id) : cancelAction(id));
      toast(use ? T('Forslaget er lagret') : T('Forslaget er forkastet'));
      if (panel?.kind === 'proposal' && panel.id === id) setPanel(null);
    } catch (e) {
      const code = e instanceof ApiError ? e.status : undefined;
      if (code != null && code >= 400 && code < 500 && code !== 499) setAiOut((e as Error).message);
      else {
        const still = await loadPending().then(
          (l) => l.some((c) => c.action_id === id),
          () => null,
        );
        if (still === false) {
          // decided after all: the card is gone from the list, so the panel closes and says so
          toast(T('Forslaget venter ikke lenger. Se vaktplanen.'));
          setPanel(null);
        } else
          setAiOut(
            still == null
              ? T('Vi vet ikke om forslaget ble brukt. Prøv igjen om litt.')
              : T('Forslaget ble ikke brukt. Prøv igjen.'),
          );
      }
    } finally {
      setAiBusy(false);
      await Promise.all([qc.invalidateQueries({ queryKey: ['poc-actions'] }), invalidateShifts(qc)]);
      window.dispatchEvent(new Event('timefly:ai-refresh'));
    }
  };
  /** "Bruk alle" / "Forkast alle": one decision per proposal, in the server's order. */
  const decideAllAi = async (use: boolean) => {
    for (const id of hg.gridActions) await decideAi(id, use);
  };
  const usage = useMemo(() => {
    const m = new Map<number, number>();
    for (const s of shifts)
      if (s.shift_type_id != null) m.set(s.shift_type_id, (m.get(s.shift_type_id) ?? 0) + 1);
    return m;
  }, [shifts]);
  /** What "Ny vakt" proposes: the type most used in the window, the global fallback last (usability review 2026-09-15). */
  const defaultType = useMemo(
    () =>
      [...types].sort(
        (a, b) => (usage.get(b.id) ?? 0) - (usage.get(a.id) ?? 0) || Number(a.id === 1) - Number(b.id === 1),
      )[0],
    [types, usage],
  );
  const teams = (teamsQ.data ?? []).filter((x) => x.name);
  /** Active absence types in the company's own order, which the query already sorts. */
  const absenceTypes = useMemo(() => (absTypesQ.data ?? []).filter((t) => t.is_active), [absTypesQ.data]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: inProject reads projF and byProject, listed
  const { rows, statusCounts } = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase('nb');
    const statusCounts = new Map<string, number>();
    let r = grid.rows.filter((row) => {
      if (needle && !fullName(row.user).toLocaleLowerCase('nb').includes(needle)) return false;
      if (byProject && projF != null && ![...row.cells.values()].some((c) => c.shifts.some(inProject)))
        return false;
      if (teamF.size && !(row.user.teams ?? []).some((x) => teamF.has(x.id))) return false;
      const mine = [...row.cells.values()].flatMap((c) => c.shifts).filter(inProject);
      // each chip counts the shifts it would leave, chained with the chips already on
      for (const [k] of STATUS) {
        const keys = new Set(statusF).add(k);
        statusCounts.set(k, (statusCounts.get(k) ?? 0) + mine.filter((x) => holdsAll(x, keys)).length);
      }
      return !statusF.size || mine.some((x) => holdsAll(x, statusF));
    });
    // the open shifts count too
    for (const d of days)
      for (const x of (grid.open.get(d) ?? []).filter(inProject))
        for (const [k] of STATUS)
          if (holdsAll(x, new Set(statusF).add(k))) statusCounts.set(k, (statusCounts.get(k) ?? 0) + 1);
    if (sort === 'timer') r = [...r].sort((a, b) => b.week.hours - a.week.hours);
    else if (sort === 'start')
      // earliest shift in the window first, and a person with nothing in it last
      r = [...r].sort((a, b) => {
        const x = earliestStart(a);
        const y = earliestStart(b);
        // two empty rows are both Infinity, and Infinity - Infinity is NaN: a comparator may not return that
        return (x === y ? 0 : x - y) || fullName(a.user).localeCompare(fullName(b.user), 'nb');
      });
    else if (sort === 'avvik')
      // a week with no agreed hours has no deviation to sort by; it goes last
      r = [...r].sort((a, b) => dev(b.week) - dev(a.week));
    else r = [...r].sort((a, b) => fullName(a.user).localeCompare(fullName(b.user), 'nb'));
    return { rows: r, statusCounts };
  }, [grid, teamF, statusF, sort, q, projF, byProject, days]);
  const hg = useMemo(
    () =>
      onGrid(
        planning ? (aiQ.data ?? []) : [],
        users.map((u) => ({ id: u.id, name: fullName(u) })),
        shifts.map((x) => ({
          id: x.id,
          user_id: x.user_id,
          date: dateOf(x),
          start: oslo(x.start_time)?.time ?? '',
          published: x.published,
        })),
        days,
        new Set(rows.map((r) => r.user.id)),
      ),
    [aiQ.data, users, shifts, days, rows, planning],
  );

  /** Availability entries on screen, which the Tilgjengelighet toggle shows or hides (V19). */
  const availCount = useMemo(
    () => rows.reduce((a, r) => a + days.filter((d) => r.cells.get(d)?.avail).length, 0),
    [rows, days],
  );
  const closeQuick = useCallback(() => setQuick(null), []);
  const toggle = <T,>(set: Set<T>, v: T) => {
    const n = new Set(set);
    n.has(v) ? n.delete(v) : n.add(v);
    return n;
  };
  const run = async (label: string, f: () => Promise<unknown>) => {
    try {
      await f();
      toast(label);
    } catch (e) {
      toast(T('{what} feilet: {m}', { what: label.split(' ')[0] ?? '', m: (e as Error).message }));
    }
  };

  // ── actions ──
  /** bodyOf refuses an ambiguous edited punch pair; run()'s catch puts the reason on screen. */
  const writeBody = (d: DraftShift): ShiftWrite => {
    // the same three authorities the drawer offers, so a drag or a resize sends the schedule
    // fields and not a punch this person may not write (custom roles, 2026-09-16)
    const b = bodyOf(d, {
      punches: can('timeliste.rette'),
      approval: can('timeliste.godkjenne'),
    });
    if ('problem' in b) throw new Error(b.problem);
    return b;
  };
  const [editFocus, setEditFocus] = useState<EditFocus | undefined>(undefined);
  useEffect(() => {
    if (!draft) setEditFocus(undefined);
  }, [draft]);
  // beside the plan, another panel (Filter, review, publish, Hugin) takes the editor's place
  // Only a real change of panel closes the editor: a second run of the effect for the same panel
  // (React's development double mount) cleared the editor Oversikt had just opened with ?ny=1
  const shownPanel = useRef(panel);
  // biome-ignore lint/correctness/useExhaustiveDependencies: only a change of panel closes the editor
  useEffect(() => {
    if (shownPanel.current === panel) return;
    shownPanel.current = panel;
    // another panel opening takes the editor's place; a panel closing to open a shift does not (#153)
    if (!phone && panel) setDraft(null);
  }, [panel]);
  // a shift opens in place of an open absence: the absence used to stay on top, the editor behind it
  // (UI01-7)
  const openShift = (s: ApiShift, focus?: EditFocus) =>
    guard(T('Rediger vakt'), () => {
      setAbsDraft(null);
      setEditFocus(focus);
      setDraft(draftOf(s));
    });
  /**
   * An empty cell (or a row's +): with a new shift open, the shift follows the click and keeps what
   * was typed (owner, 2026-10-20, a prospect saved today's draft while aiming for Saturday). Otherwise
   * the quick picker, asked about first when an edited shift is open, since a pick there writes.
   */
  const emptyCell = (q: QuickAt) => {
    const next = newDraft(q.date, q.userId, defaultType);
    if (draft && draft.id == null && carryTo(next)) {
      setQuick(null);
      setEditFocus(undefined);
      setDraft(next);
      return;
    }
    guard(T('ny vakt for {who} {d}', { who: q.who, d: fmtDs(q.date) }), () => setQuick(q));
  };
  /** Every "new shift" entry replaces the open editor only through the guard (retest R01). */
  const newShift = (date: string, userId: number | null, to = T('Ny vakt')) =>
    guard(to, () => {
      setAbsDraft(null);
      setEditFocus(undefined);
      setDraft(newDraft(date, userId, defaultType));
    });
  /**
   * «Legg til registrering» in «Uka»: Timer's own manual registration (Timeliste.tsx addRegistration),
   * the company's «uten plan» type, published, opened in the same editor; offered with timeliste.rette.
   */
  const mayRegister = !planning && can('timeliste.rette');
  const unplannedType = types.find((x) => /uten plan/i.test(x.name ?? '')) ?? types[0];
  const addRegistration = (date: string, userId: number | null) =>
    guard(T('Legg til registrering'), () => {
      setAbsDraft(null);
      setEditFocus(undefined);
      setDraft({ ...newDraft(date, userId, unplannedType), published: true });
    });
  // a cover action from Oversikt arrives as ?ny=1 with the day and the interval; it opens once,
  // when the types are known, and the address is left alone so Back returns to Oversikt
  const asked = useRef(link.newShift);
  useEffect(() => {
    const a = asked.current;
    if (!a || !types.length) return;
    asked.current = null;
    setEditFocus(undefined);
    setDraft({
      ...newDraft(a.date, a.userId, defaultType),
      from: a.from,
      to: a.to,
    });
  }, [types.length, defaultType]);
  const quickMake = async (ty: ApiShiftType, projectId: number | null = null) => {
    if (!quick) return;
    // the project the quick add was made with (#115); the body carries it on a new draft
    const d = {
      ...newDraft(quick.date, quick.userId, ty),
      project_id: projectId,
    };
    setQuick(null);
    await run(T('{name} lagt til {date} som utkast', { name: ty.name, date: fmtD(d.date) }), () =>
      w.create.mutateAsync(writeBody(d)),
    );
  };
  /** A move or a resize of one shift, under the lock; the refusal names the thing that failed. */
  const write = async (id: number, label: string, body: Record<string, unknown>): Promise<boolean> => {
    const before = shifts.find((x) => x.id === id) ?? null;
    const r = await hold([id], () => w.update.mutateAsync({ id, body }));
    if (r.kind === 'done') {
      toast(label);
      if (before) setChanged(before);
    } else if (r.kind === 'refused')
      toast(T('{what} feilet: {m}', { what: label.split(' ')[0] ?? '', m: r.message }));
    else if (r.kind === 'unknown') {
      toast(T(UNKNOWN));
      await rereadShifts();
    }
    return r.kind === 'done';
  };
  /**
   * The way out of an unknown outcome: read the window and see where things are. Only a read that
   * succeeds unlocks, both the single-shift locks (lock.ts) and a bulk action's unresolved ones.
   */
  const rereadShifts = async () => {
    const data = await lock.reread();
    if (data) setUnresolved([]);
    return data;
  };
  /**
   * Publish exactly this shift (PUT /admin/shifts/:id/publish). The notice is said apart from the
   * publish; a refusal says the server's reason; an answer that never came is settled by reading
   * the window, and only what that read shows is said (V19, owner 2026-10-08).
   */
  const publishOne = async (s: ApiShift) => {
    const say = (o: Outcome) => {
      setPubOut(o);
      if (!panel || panel.kind !== 'shift' || panel.id !== s.id) toast(o.text);
    };
    const h = await hold(
      [s.id],
      () => w.publish.mutateAsync(s.id) as Promise<{ notification_status?: string } | undefined>,
    );
    if (h.kind === 'done')
      say({
        id: s.id,
        kind: 'done',
        text: T(NOTICE[h.value?.notification_status ?? ''] ?? 'Publisert.'),
      });
    else if (h.kind === 'busy') say({ id: s.id, kind: 'refused', text: T(BUSY) });
    else if (h.kind === 'refused')
      say({
        id: s.id,
        kind: 'refused',
        text: `${T('Ikke publisert')}: ${h.message}`,
      });
    else {
      // only a successful read settles it; a failed one keeps the shift locked
      const data = await rereadShifts();
      const now = data?.shifts.find((x) => x.id === s.id);
      say(
        data == null || now == null
          ? {
              id: s.id,
              kind: 'unknown',
              text: T('Vi vet ikke om vakten ble publisert. Les på nytt før du prøver igjen.'),
            }
          : now.published
            ? {
                id: s.id,
                kind: 'done',
                text: T('Publisert. Vi vet ikke om den ansatte ble varslet.'),
              }
            : {
                id: s.id,
                kind: 'refused',
                text: T('Ikke publisert. Prøv igjen.'),
              },
      );
    }
  };
  const approveOne = (s: ApiShift, v: boolean) =>
    op([s.id], v ? T('Godkjent') : T('Godkjenning trukket'), async () => {
      await approve.mutateAsync({ id: s.id, approved: v, shift: s });
      return undefined;
    });
  /**
   * The card's own "Dupliser til i morgen": tomorrow's draft with the same plan
   * and the same task definitions, and nothing that was recorded. The new task
   * rows are new rows: they start at pending with their own ids, and the source
   * shift is not touched (TF-SCHEDULE-COMPLETE).
   */
  const dup = (s: ApiShift) => {
    const d = draftOf(s);
    const nx = {
      ...d,
      id: null,
      date: addDays(d.date, 1),
      punchIn: '',
      punchOut: '',
      lunchIn: '',
      lunchOut: '',
      // the copy is a plan, so it inherits none of the original's recorded instants either
      punchIn0: null,
      punchOut0: null,
      lunchIn0: null,
      lunchOut0: null,
      is_approved: false,
      published: false,
    };
    const tasks = (s.taskAssignments ?? []).flatMap((a) =>
      a.shiftTask?.name
        ? [
            {
              shiftTask: {
                name: a.shiftTask.name,
                description: a.shiftTask.description ?? null,
              },
              has_time_range: a.has_time_range,
              start_time: a.start_time,
              end_time: a.end_time,
            },
          ]
        : [],
    );
    let body: ShiftWrite;
    try {
      body = {
        ...writeBody(nx),
        ...(tasks.length ? { taskAssignments: tasks } : {}),
      };
    } catch (e) {
      toast(T('Kopiering feilet: {m}', { m: (e as Error).message }));
      return;
    }
    // under the source's lock: a copy of a shift whose last write nobody knows would copy a guess,
    // and an unknown copy is not sent twice
    return op([s.id], T('Kopiert til {date} som utkast', { date: fmtD(nx.date) }), async () => {
      await w.create.mutateAsync(body);
      return undefined;
    });
  };
  const free = async (s: ApiShift) => {
    const ok = await op([s.id], T('Vakten er ledig'), async () => {
      await w.update.mutateAsync({ id: s.id, body: { user_id: null } });
      return undefined;
    });
    if (ok) setChanged(s);
    return ok;
  };
  const remove = async (id: number, reason?: string) => {
    const before = shifts.find((x) => x.id === id) ?? null;
    const ok = await op([id], T('Vakten er slettet'), async () => {
      await w.remove.mutateAsync({ id, reason });
      return undefined;
    });
    if (ok) {
      setDraft(null);
      if (before) setChanged(before);
    }
  };
  /**
   * "Publiser N utkast": exactly the drafts on screen, filters and search included, one id at a
   * time with one outcome each (V19 decision "Publication scope", owner 2026-10-08). The date-only
   * publish-shifts route published drafts a team filter hid, so it is not used here.
   */
  /**
   * "Publiser N": exactly the drafts on screen, filters and search included, frozen when the panel
   * opens and listed there with any rule they break and the scope, before anything is written
   * (V19 decision "Publication scope"; handoff 2026-10-08). Breaches are said, never a block
   * (ruling 2026-09-08). One id at a time with one outcome each; publish-shifts is not used.
   */
  const publishWindow = () =>
    guard(T('Publiser'), () => setPanel({ kind: 'publish', ids: [...visibleDrafts] }));
  /**
   * Copying a week and applying a template both go through one server route that
   * reads the source itself, maps every shift onto the destination in the
   * company's own clock and says what it would create before anything is written
   * (TF-SCHEDULE-COMPLETE). The preview below is that answer; the button then
   * asks for exactly the same thing with a key, so a lost answer can be asked
   * again without making a second set of drafts.
   */
  const [saveForm, setSaveForm] = useState<SaveForm | null>(null);
  // the form and its panel go together: Avbryt or a save left the panel open and empty (owner 2026-10-25)
  const closeSave = () => {
    setSaveForm(null);
    setPanel((p) => (p?.kind === 'save' ? null : p));
  };
  // the review drawer's state, every change of it in copyMachine.ts (issue #77)
  const [{ plan }, dispatch] = useReducer(copyMachine, idle);
  /**
   * One key per piece of work, kept for as long as the page is open: closing the
   * drawer and opening it again on the same copy reuses it, so a request whose
   * answer was lost can be sent again without creating a second set
   * (TF-SCHEDULE-COMPLETE review, finding 3).
   */
  const keys = useRef(new Map<string, string>());
  const keyFor = (p: PlanState) => {
    const sig = signatureOf(p);
    const had = keys.current.get(sig);
    if (had) return had;
    const made = newKey();
    keys.current.set(sig, made);
    return made;
  };
  const preview = async (next: PlanState) => {
    dispatch({ type: 'preview', next });
    try {
      const body = next.kind === 'copy' ? next.copy : next.apply;
      const answer =
        next.kind === 'copy'
          ? await tw.copyWindow.mutateAsync({
              ...(body as CopyWindowBody),
              dry_run: true,
            })
          : await tw.apply.mutateAsync({
              id: next.templateId as number,
              body: { ...(body as ApplyTemplateBody), dry_run: true },
            });
      dispatch({ type: 'previewed', plan: answer });
    } catch (e) {
      dispatch({ type: 'problem', text: (e as Error).message });
    }
  };
  /**
   * What happened to a request whose answer never arrived, read only
   * (TF-SCHEDULE-COMPLETE acceptance review, finding 1). This never creates: a key
   * nothing is stored under means the request never reached the server, and the
   * person is offered a separate, explicitly labelled create.
   */
  const checkStatus = async () => {
    if (!plan || plan.busy) return;
    const key = keyFor(plan);
    const sig = signatureOf(plan);
    dispatch({ type: 'sending' });
    try {
      const r = await tw.requestStatus.mutateAsync({
        action: plan.kind === 'copy' ? 'copy_window' : 'template_apply',
        request_key: key,
        payload: plan.kind === 'copy' ? plan.copy : { ...plan.apply, template_id: plan.templateId },
      });
      if (r.state === 'completed') {
        dispatch({ type: 'done', sig });
        await shiftsQ.refetch();
        toast(
          T('Forespørselen var allerede utført. {count} vakter finnes.', {
            count: r.result?.created ?? 0,
          }),
        );
        return;
      }
      // it never arrived: creating is safe again, but only as its own action
      if (r.state === 'absent') return dispatch({ type: 'absent', sig });
      if (r.state === 'pending')
        return dispatch({
          type: 'info',
          text: T('Forespørselen er underveis. Sjekk igjen om litt.'),
        });
      dispatch({
        type: 'problem',
        text: T('Denne forespørselen tilhører noe annet.'),
      });
    } catch {
      // the check itself failed: nothing is known, and nothing may be created
      dispatch({
        type: 'problem',
        text: T('Kunne ikke sjekke status. Prøv igjen.'),
      });
    }
  };
  /** Send the reviewed plan, or ask again about one whose answer never came. */
  const confirmPlan = async () => {
    if (!plan || plan.busy || plan.unknown) return;
    const key = keyFor(plan);
    const token = plan.plan?.plan_token;
    dispatch({ type: 'sending' });
    try {
      const answer =
        plan.kind === 'copy'
          ? await tw.copyWindow.mutateAsync({
              ...(plan.copy as CopyWindowBody),
              request_key: key,
              ...(token ? { plan_token: token } : {}),
            })
          : await tw.apply.mutateAsync({
              id: plan.templateId as number,
              body: {
                ...(plan.apply as ApplyTemplateBody),
                request_key: key,
                ...(token ? { plan_token: token } : {}),
              },
            });
      dispatch({ type: 'close' });
      toast(
        answer.repeat
          ? T('Forespørselen var allerede utført. {count} vakter finnes.', {
              count: answer.created ?? 0,
            })
          : T('{count} vakter laget som utkast.', {
              count: answer.created ?? 0,
            }),
      );
    } catch (e) {
      const code = e instanceof ApiError ? e.status : undefined;
      if (code === 409) {
        // the schedule moved under the review, or the key belongs to something else
        dispatch({ type: 'problem', text: (e as Error).message });
        const why = e instanceof ApiError ? e.code : null;
        if (why === 'plan_changed' || why === 'precondition_failed') await preview({ ...plan, busy: false });
        return;
      }
      if (code != null && code >= 400 && code < 500 && code !== 499) {
        dispatch({ type: 'problem', text: (e as Error).message });
        return;
      }
      // nobody knows whether it was created: the same key is the only safe way to ask
      dispatch({ type: 'unknown', sig: signatureOf(plan) });
      await shiftsQ.refetch();
    }
  };
  /**
   * "Kopier forrige uke" is a named calendar week, whatever view is open, onto the
   * calendar week in view. Both periods are on the drawer and can be changed, and a
   * change asks for the review again (TF-SCHEDULE-COMPLETE review).
   */
  const copyWeek = () => {
    const target = mondayOf(monday);
    const source = addDays(target, -7);
    return preview({
      kind: 'copy',
      title: T('Kopier uke {week}', { week: isoWeek(source) }),
      copy: {
        source_start_date: source,
        source_end_date: addDays(source, 6),
        target_start_date: target,
        target_end_date: addDays(target, 6),
        create_open_shifts: false,
        published_sources_only: false,
      },
      busy: false,
      unknown: false,
      plan: null,
      problem: '',
      info: '',
    });
  };
  /**
   * Publish, approve and delete are one request per shift, so the outcome is one
   * per shift too (TF-SCHEDULE-COMPLETE). What succeeded leaves the selection,
   * what was already done is counted as unchanged, what the server refused stays
   * selected with its reason, and a request whose answer never arrived leaves
   * that shift unresolved: the actions stay disabled until a read of the window
   * actually succeeds, so nobody can repeat a write that may have happened.
   */
  /**
   * Every shift the grid is showing right now, filters and all. Selecting something hidden
   * behind a filter and then deleting it would be a nasty surprise.
   *
   * This exists because of a report from the product user (2026-09-21): applying a template to
   * a whole month leaves shifts that cannot be removed. The saved template can be deleted and
   * the shifts it created cannot, because nothing records which template made a shift and there
   * was no way to select more than a handful by hand. Selecting the period is the general answer
   * to that, and to "clear this week and start again".
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: inProject reads projF and byProject, listed
  const visibleShiftIds = useMemo(() => {
    const ids = new Set<number>();
    for (const r of rows)
      for (const d of days) for (const x of r.cells.get(d)?.shifts ?? []) if (inView(x)) ids.add(x.id);
    for (const d of days) for (const x of grid.open.get(d) ?? []) if (inView(x)) ids.add(x.id);
    return ids;
  }, [rows, days, grid, projF, byProject, statusF]);
  /** The period's hours on people and in open shifts, and the shifts shown (the foot's summary). */
  const footTotals = () => {
    const on = shifts.filter((x) => visibleShiftIds.has(x.id));
    // the period's days, each with the part of every shift on it (retest R05)
    const inPeriod = (list: ApiShift[]) =>
      days.reduce((a, d) => a + list.reduce((b, x) => b + plannedHoursOn(x, d), 0), 0);
    return {
      on,
      held: inPeriod(on.filter((x) => x.user_id != null)),
      open: inPeriod(on.filter((x) => x.user_id == null)),
    };
  };
  const visibleDrafts = useMemo(
    () => new Set(shifts.filter((x) => visibleShiftIds.has(x.id) && !x.published).map((x) => x.id)),
    [shifts, visibleShiftIds],
  );

  const selDo = async (what: 'publish' | 'approve' | 'delete', ids: Set<number> = sel) => {
    if (busyAction || unresolved.length) return;
    // Selecting a whole period is one click, so deleting one is too. Ask, and name the number:
    // there was no confirmation at all while the only way to select was card by card.
    if (
      what === 'delete' &&
      !window.confirm(T('Slette {count} vakter? Dette kan ikke angres.', { count: ids.size }))
    )
      return;
    const picked = shifts.filter((s) => ids.has(s.id));
    // one reason for every shift in the selection that holds registered time (work-time history)
    let delReason: string | undefined;
    if (what === 'delete') {
      const r = askDeleteReason({
        punch_in_time: picked.find((s) => s.punch_in_time || s.punch_out_time)?.punch_in_time ?? null,
        punch_out_time: picked.find((s) => s.punch_in_time || s.punch_out_time)?.punch_out_time ?? null,
      });
      if (r === null) return;
      delReason = r;
    }
    setBusyAction(what);
    setLedger(null);
    const ok: number[] = [];
    const unchanged: number[] = [];
    const failed: { id: number; message: string }[] = [];
    const unknown: number[] = [];
    let noticeFailed = 0;
    for (const s of picked) {
      if ((what === 'publish' && s.published) || (what === 'approve' && s.is_approved)) {
        unchanged.push(s.id);
        continue;
      }
      if (what === 'approve' && !(s.punch_in_time && s.punch_out_time)) {
        failed.push({ id: s.id, message: T('Mangler stempling') });
        continue;
      }
      // each shift under the same lock as a single write (retest R02)
      const r = await hold([s.id], async () => {
        if (what === 'publish') {
          const x = (await w.publish.mutateAsync(s.id)) as { notification_status?: string } | undefined;
          if (x?.notification_status === 'failed') noticeFailed += 1;
        } else if (what === 'approve') await approve.mutateAsync({ id: s.id, approved: true });
        else await w.remove.mutateAsync({ id: s.id, reason: delReason });
      });
      if (r.kind === 'done') ok.push(s.id);
      else if (r.kind === 'busy') failed.push({ id: s.id, message: T(BUSY) });
      else if (r.kind === 'refused') failed.push({ id: s.id, message: r.message });
      else unknown.push(s.id);
    }
    // what worked leaves the selection; everything unresolved stays inspectable
    setSel(new Set([...failed.map((x) => x.id), ...unknown]));
    setLedger({
      what,
      ok: ok.length,
      unchanged: unchanged.length,
      failed,
      unknown: unknown.length,
      noticeFailed,
    });
    setUnresolved(unknown);
    setBusyAction(null);
  };
  /** The only way out of an unknown outcome: read the window again and see what is actually there. */
  const rereadWindow = async () => {
    await rereadShifts();
  };
  /**
   * Drop a card on another cell: same wall-clock times on the new day, the new
   * person (or open), and published:false, as the old timeline's drag does
   * (timeline.md §8: drag unpublishes silently). A punched shift is not draggable.
   */
  const moveShift = async (id: number, toUser: number | null, toDate: string, toClock?: string) => {
    const s = shifts.find((x) => x.id === id);
    if (!s) return;
    const from = oslo(s.start_time);
    const to = oslo(s.end_time);
    if (!from || !to) return;
    const startClock = toClock ?? from.time;
    // the shift keeps its length, wherever it is dropped
    const lengthMin = (new Date(s.end_time).getTime() - new Date(s.start_time).getTime()) / 60000;
    const startMin = Number(startClock.slice(0, 2)) * 60 + Number(startClock.slice(3, 5));
    const endMin = startMin + lengthMin;
    const endDay = addDays(toDate, Math.floor(endMin / (24 * 60)));
    const endClock = `${String(Math.floor((endMin % (24 * 60)) / 60)).padStart(2, '0')}:${String(Math.round(endMin % 60)).padStart(2, '0')}`;
    if (dateOf(s) === toDate && s.user_id === toUser && startClock === from.time) return; // nothing moved
    // moving a shift says nothing about publication: the stored flag is kept (TF-PUBLISH-PRESERVE)
    const start = osloISO(toDate, startClock);
    const body = {
      user_id: toUser,
      start_time: start,
      end_time: osloISO(endDay, endClock),
      // a planned lunch goes with the shift, at the same place inside it
      ...movedLunch(s, Date.parse(start) - Date.parse(s.start_time)),
    };
    if (overlapping(shifts, id, toUser, body.start_time, body.end_time).length) {
      guard(T('Se overlapp'), () =>
        setPanel({
          kind: 'move-conflict',
          id,
          body,
          start: body.start_time,
          end: body.end_time,
          userId: toUser,
        }),
      );
      return;
    }
    await write(id, T('Vakten er flyttet'), body);
  };
  /**
   * The day view's edge drag. Only the times change: the person, the publication, the tasks and
   * everything else stay. Both ends arrive as full timestamps, the edge not dragged as stored, so
   * 22:00 to 06:00 moved to 21:00 stays nine hours (review F02).
   */
  const resizeShift = async (id: number, start: string, end: string) => {
    if (!shifts.some((x) => x.id === id)) return;
    const s = shifts.find((x) => x.id === id);
    if (!s) return;
    const body = { start_time: start, end_time: end };
    if (overlapping(shifts, id, s.user_id, start, end).length) {
      guard(T('Se overlapp'), () =>
        setPanel({
          kind: 'move-conflict',
          id,
          body,
          start,
          end,
          userId: s.user_id,
        }),
      );
      return;
    }
    await write(id, T('Vakten er endret'), body);
  };
  /** Templates: save the window under a name with its tasks and its real publication; apply one through the same review. */
  /** Saving a template: the form the original had, not a prompt (TF-SCHEDULE-COMPLETE review). */
  const openSave = () => {
    setSaveForm({
      name: `${T('Uke')} ${isoWeek(monday)}`,
      description: '',
      start_date: monday,
      end_date: last,
      scope: sel.size ? 'selected' : 'window',
      busy: false,
      problem: '',
    });
    // in the side panel on a desktop, like the rest of planning (owner 18.10); the phone keeps its sheet
    if (!phone) setPanel({ kind: 'save' });
  };
  const sourceShiftsFor = (form: SaveForm) =>
    form.scope === 'selected'
      ? shifts.filter((s) => sel.has(s.id))
      : shifts.filter((s) => dateOf(s) >= form.start_date && dateOf(s) <= form.end_date);
  const saveTemplate = async () => {
    if (!saveForm || saveForm.busy) return;
    const mine = sourceShiftsFor(saveForm);
    if (!saveForm.name.trim()) return setSaveForm({ ...saveForm, problem: T('Malen trenger et navn.') });
    if (!mine.length)
      return setSaveForm({
        ...saveForm,
        problem: T('Ingen vakter å lagre i denne perioden.'),
      });
    setSaveForm({ ...saveForm, busy: true, problem: '' });
    try {
      await tw.create.mutateAsync({
        name: saveForm.name.trim(),
        description: saveForm.description.trim() || null,
        // the existing pattern types are kept: the window decides which one this is
        template_type: view === 'month' ? 'monthly' : nd > 7 ? 'custom' : 'weekly',
        start_date: saveForm.start_date,
        end_date: saveForm.end_date,
        // the source's own publication is stored, so "bare publiserte kildevakter" can work
        // later; open shifts are part of the period and are stored with it
        shifts: mine.map((s) => ({
          user_id: s.user_id,
          shift_type_id: s.shift_type_id,
          start_time: s.start_time,
          end_time: s.end_time,
          description: s.description,
          published: s.published,
          taskAssignments: s.taskAssignments,
        })) as Partial<ApiShift>[],
      });
      closeSave();
      toast(`${T('Mal lagret')}: ${saveForm.name.trim()}`);
    } catch (e) {
      setSaveForm((f) => (f ? { ...f, busy: false, problem: (e as Error).message } : f));
    }
  };
  const applyTemplate = (id: number, name: string) =>
    preview({
      kind: 'template',
      templateId: id,
      title: `${T('Legg inn malen')}: ${name}`,
      apply: {
        target_start_date: monday,
        target_end_date: last,
        create_open_shifts: false,
        published_sources_only: false,
      },
      busy: false,
      unknown: false,
      plan: null,
      problem: '',
      info: '',
    });
  const removeTemplate = (id: number, name: string) =>
    window.confirm(`${T('Slette malen')} «${name}»?`) &&
    run(T('Malen er slettet'), () => tw.remove.mutateAsync(id));

  /**
   * A shift's actions, only those this role may take (handoff 2026-10-08: custom and read-only
   * roles). Planning (publish, duplicate, free, delete) needs vaktplan.endre; approving needs
   * timeliste.godkjenne. The server checks the same; this stops offering what would be refused.
   */
  const menuFor = (s: ApiShift) =>
    menuAll(s).filter((m) =>
      // «Uka» keeps opening, approving and deleting; publishing, copying and freeing are planning (#118)
      !planning && (m.key === 'pub' || m.key === 'dup' || m.key === 'free')
        ? false
        : m.key === 'open'
          ? true
          : m.key === 'appr' || m.key === 'unappr'
            ? can('timeliste.godkjenne')
            : can('vaktplan.endre'),
    );
  const menuAll = (s: ApiShift) => [
    { key: 'open', label: T('Åpne'), onSelect: () => openShift(s) },
    ...(!s.published ? [{ key: 'pub', label: T('Publiser'), onSelect: () => publishOne(s) }] : []),
    s.is_approved
      ? {
          key: 'unappr',
          label: T('Fjern godkjenning'),
          onSelect: () => approveOne(s, false),
        }
      : {
          key: 'appr',
          label: s.punch_in_time && s.punch_out_time ? T('Godkjenn') : T('Godkjenn, mangler stempling'),
          disabled: !(s.punch_in_time && s.punch_out_time),
          onSelect: () => approveOne(s, true),
        },
    { key: 'dup', label: T('Dupliser til i morgen'), onSelect: () => dup(s) },
    ...(s.user_id != null ? [{ key: 'free', label: T('Gjør ledig'), onSelect: () => free(s) }] : []),
    {
      key: 'del',
      label: T('Slett'),
      danger: true,
      onSelect: () => {
        const r = askDeleteReason(s);
        if (r !== null) remove(s.id, r);
      },
    },
  ];

  // planning's gestures (drag, resize, quick add, Planlegg, Ny vakt, Publiser) are Vaktplan's only
  const editable = can('vaktplan.endre') && planning;
  // the plan and the panel beside it, the panel's width the manager's (issue #153)
  const splitRef = useRef<HTMLDivElement | null>(null);
  const side = usePanelWidth(splitRef);
  const nameCol = useNameWidth();
  // no «Uka» week column in the week grid (owner 2026-11-01: "we dont need uka column"); the week's totals
  // stay in «Tall for uka» and the x av y figure by the name
  const weekCol = false;
  // on a phone the day view's editor is a sheet; the week's is in its list (phoneList)
  const daySheet = phone && view === 'day' && !!draft;
  const sakerOn = !phone && planning && sideOpen;
  // the sheet over the plan (≤1100 px) takes focus when it opens, as a sheet does (#272 A-03)
  const sideEl = useRef<HTMLElement | null>(null);
  const sideWasOpen = useRef(sakerOn);
  useEffect(() => {
    const was = sideWasOpen.current;
    sideWasOpen.current = sakerOn;
    if (!sakerOn || was || !window.matchMedia?.(SIDE_SHEET).matches) return;
    requestAnimationFrame(() => {
      const box = sideEl.current;
      if (box && !box.contains(document.activeElement))
        box.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')?.focus();
    });
  }, [sakerOn]);
  // Escape is listened for on the column itself, natively: the docked Hugin is a portal from the shell, so its keys
  // never reach a React handler here (#272 A-R10)
  const sideEscRef = useRef<(e: KeyboardEvent) => void>(() => {});
  useEffect(() => {
    const el = sideEl.current;
    if (!sakerOn || !el) return;
    const h = (e: KeyboardEvent) => sideEscRef.current(e);
    el.addEventListener('keydown', h);
    return () => el.removeEventListener('keydown', h);
  }, [sakerOn]);
  panelKind.current = panel?.kind ?? null;
  sideDock.current = !phone && planning;
  const splitOn =
    !!(panel || absDraft || (draft && !phone) || sakerOn) &&
    (!phone || panel?.kind === 'proposal' || panel?.kind === 'filter');
  // On a phone a tap opens the sheet; once something is selected, a tap selects, the way a
  // phone's own lists work, so "Merk flere" starts a selection rather than picking one (plan 0029)
  // guard() goes straight on when nothing is unsaved, so opening the shift already shown costs nothing
  const tapShift = (s: ApiShift) =>
    guard(`${s.user ? fullName(s.user) : T('Ledig vakt')} ${timeText(s)}`, () => openPanel(s));
  // a write that saves at once (approve, publish, free, a lunch) is shown in the open editor when
  // nothing in it is unsaved (issue #153): the badge and the hours follow the stored shift
  // biome-ignore lint/correctness/useExhaustiveDependencies: only when the shifts are read again
  useEffect(() => {
    if (draft?.id == null || hasDirty()) return;
    const s = shifts.find((x) => x.id === draft.id);
    if (!s) return;
    const fresh = draftOf(s);
    if (JSON.stringify(fresh) !== JSON.stringify(draft)) setDraft(fresh);
  }, [shifts]);
  const openPanel = (s: ApiShift) => {
    // on a phone, once something is selected a tap selects (plan 0029)
    if (phone && sel.size) return setSel(toggle(sel, s.id));
    if (pubOut?.id !== s.id) setPubOut(null);
    if (!panel && !draft && document.activeElement instanceof HTMLElement)
      origin.current = document.activeElement;
    // the same editable shift every time (issue #153 SP01): its sections hold what the old summary
    // panel showed, its warnings on the section they belong to
    setLunchPreset(null);
    setPanel(null);
    setAbsDraft(null);
    setEditFocus(undefined);
    setDraft(draftOf(s));
  };
  // a lunch dragged on the day lane opens in the panel's lunch row, unsaved, to be saved there
  const [lunchPreset, setLunchPreset] = useState<{
    id: number;
    start: string;
    minutes: number;
    n: number;
  } | null>(null);
  useEffect(() => {
    if (!panel && !draft) setLunchPreset(null);
  }, [panel, draft]);
  const laneChange = (s: ApiShift, c: LaneChange) =>
    'lunch' in c
      ? guard(T('Flytt lunsj'), () => {
          openPanel(s);
          setLunchPreset({
            id: s.id,
            start: c.lunch,
            minutes: c.minutes,
            n: Date.now(),
          });
        })
      : 'split' in c
        ? openShift(s, {
            task: c.split[0],
            end: c.at,
            also: { task: c.split[1], start: c.at },
          })
        : openShift(s, c);
  /** A week rule explained beside the plan (retest R07). */
  const openRule = (person: number, rule: Entry) =>
    guard(T('Regel'), () => {
      if (!panel && document.activeElement instanceof HTMLElement) origin.current = document.activeElement;
      setPanel({ kind: 'rule', person, rule });
    });
  const openPerson = (id: number) =>
    guard(T('Medarbeider'), () => {
      if (!panel && document.activeElement instanceof HTMLElement) origin.current = document.activeElement;
      setPanel({ kind: 'person', id });
    });
  // closing goes through the guard, and focus goes back to the card that opened the panel (review F05)
  const origin = useRef<HTMLElement | null>(null);
  // every way into a panel remembers its opener, so Escape never leaves focus on the page (#272 A-08)
  const rememberOrigin = () => {
    if (!panel && !draft && document.activeElement instanceof HTMLElement)
      origin.current = document.activeElement;
  };
  const closePanel = () => guard(T('Lukk'), () => setPanel(null));
  // closing the panel or the shift editor gives focus back to what opened it (review F05, #153)
  useEffect(() => {
    if (panel || draft || !origin.current) return;
    if (origin.current.isConnected) origin.current.focus();
    origin.current = null;
  }, [panel, draft]);
  // the selected card stays in view beside its panel (handoff 2026-10-08), but the week moves only
  // when the card is not already wholly in the grid's visible part, right of the fixed names
  // (issue #85: a Saturday that was on screen left Monday a sliver)
  const panelId = panel?.kind === 'shift' ? panel.id : (draft?.id ?? null);
  useEffect(() => {
    if (panelId == null) return;
    const el = document.querySelector<HTMLElement>(`[data-shift-id="${panelId}"]`);
    const box = scroller.current;
    if (el && box && box.contains(el)) {
      const c = el.getBoundingClientRect();
      const b = box.getBoundingClientRect();
      const names = box.querySelector<HTMLElement>('.gh > div')?.offsetWidth ?? 0;
      if (c.left >= b.left + names && c.right <= b.right && c.top >= b.top && c.bottom <= b.bottom) return;
    }
    el?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [panelId]);
  // the panel reads the shift live, so a refetch while it is open shows the new state
  const offers = pendingQ.data?.pendingRequests?.shiftOffers ?? [];
  const swaps = pendingQ.data?.pendingRequests?.shiftSwaps ?? [];
  /** Approve or decline an offer or a swap from the panel, through the Varsler page's own routes. */
  const decide = (kind: 'offer' | 'swap', id: number, ok: boolean) =>
    run(ok ? T('Godkjent') : T('Avslått'), () =>
      kind === 'offer'
        ? (ok ? rw.approveOffer : rw.rejectOffer).mutateAsync({ id })
        : (ok ? rw.approveSwap : rw.rejectSwap).mutateAsync({ id }),
    );
  /**
   * The week's review (V19): every rule entry on a shift on screen, each week rule once per person,
   * and the offers and swaps waiting on a manager for those shifts. Grouped, each one a way in.
   */
  /**
   * The week's review, grouped by the manager's job (handoff 2026-10-08): staffing, working-time
   * rules, attendance, absence, availability, requests. Severity is a badge inside a group. Every
   * item comes from what the page already holds (the server's rule entries, the punches, absences,
   * availability, the pending offers and swaps); nothing is inferred from an icon.
   */
  // loading is not "nobody planned": no review item until this window's shifts have arrived (review V05)
  const loading = !weekData && shiftsQ.isPending;
  const noData = !weekData;
  /** Assigned planned hours on one Oslo date: the part of each shift on it, a night included (retest R05). */
  const dayHours = (d: string) =>
    shifts.filter((x) => x.user_id != null).reduce((a, x) => a + plannedHoursOn(x, d), 0);
  const review = useMemo<ReviewItem[]>(
    () =>
      noData
        ? []
        : reviewOf({
            rows,
            days,
            grid,
            shifts,
            offers,
            swaps,
            visible: visibleShiftIds,
            need: labour.need,
            staffView,
            t,
          }),
    [rows, days, grid, offers, swaps, visibleShiftIds, t, shifts, labour.need, staffView, noData],
  );
  /**
   * The review bar (issue #147): every open period's work, from the same queue Oversikt →
   * Oppfølging builds (oversikt/useQueue.ts), worded as cases (reviewBarAdapter.ts). Last month's pay
   * month only for who may see pay.
   */
  const followUp = useFollowUpQueue(today, Date.now(), undefined, shown);
  const mayDecideAbsence = can('fravaer.godkjenne');
  const mayEditTime = can('timeliste.rette');
  const payYm = prevMonth(monthOf(today));
  // brief: the bar reads the month's state only, not the pay report behind it (0.5 to 1.1 s at BK, RC 2026-10-28)
  const payQ = usePayMonth(payYm, can('timerloenn.se') && shown, true);
  const reviewCases = useMemo(
    () =>
      casesOf({
        queue: followUp.queue,
        shifts: [...followUp.closeShifts, ...followUp.ahead],
        users,
        payMonth: payQ.data ? { ym: payYm, state: payQ.data.state } : null,
        week: { items: review, thisWeek: days.includes(today), shifts },
        decideAbsence: mayDecideAbsence,
        editTime: mayEditTime,
        editPlan: editable,
      }),
    [
      mayEditTime,
      editable,
      mayDecideAbsence,
      followUp.queue,
      followUp.closeShifts,
      followUp.ahead,
      users,
      payQ.data,
      payYm,
      review,
      days,
      today,
      shifts,
    ],
  );
  const [activeCase, setActiveCase] = useState<ReviewCase | null>(null);
  const [reviewFocus, setReviewFocus] = useState<{
    label: string;
    keys: string[];
    n: number;
    /** the shifts a day's or person's count is about, week rules included (owner 2026-10-25) */
    shiftIds?: number[];
  } | null>(null);
  // the cases Saker is filtered to: their shifts are outlined in the week, as in the prototype (owner 2026-10-25)
  const [scopeKeys, setScopeKeys] = useState<string[] | null>(null);
  const absW = useAbsenceWrites();
  /** A shift from a case, wherever it is: the plan moves to its period first, then the panel opens. */
  const openShiftOf = (s: ApiShift) => {
    const d = oslo(s.start_time)?.date;
    if (!d || days.includes(d)) return tapShift(s);
    guard(T('Vis vakten'), () => {
      setMonday(view === 'day' ? d : view === 'month' ? monthStart(d) : mondayOf(d));
      openPanel(s);
    });
  };
  // what approving the absence on screen deducts, read as Fravær reads it; the same amount is sent,
  // so the server refuses with the actual count if it moved since (Codex resolution 2026-09-14)
  const caseAbs = activeCase?.absence;
  const caseDeduction = useDeductionState(
    caseAbs ? { absence_type_id: caseAbs.typeId, start_date: caseAbs.start, end_date: caseAbs.end } : null,
    !!caseAbs?.deductible,
  );
  /** An action from the bar, through the page's own writes and locks; the bar draws the outcome. */
  const outcomeOf = (e: unknown): CaseOutcome =>
    e instanceof ApiError && e.status > 0 && e.status < 500
      ? { kind: 'refused', message: e.message }
      : { kind: 'unknown' };
  /** A shift as the server holds it now, for a conflict's comparison and an unknown outcome's check (S18). */
  const latestShift = async (id: number): Promise<ApiShift | null> => {
    try {
      return await get<ApiShift>(`/admin/shifts/${id}`);
    } catch {
      return null;
    }
  };
  const FIELD: Record<string, string> = {
    punch_in_time: T('Innstemplet'),
    punch_out_time: T('Utstemplet'),
    lunch_in_time: T('Pause start'),
    lunch_out_time: T('Pause slutt'),
    start_time: T('Planlagt start'),
    end_time: T('Planlagt slutt'),
    is_approved: T('Godkjent'),
    published: T('Publisert'),
    user_id: T('Ansatt'),
  };
  /** A refusal said as the server said it; a conflict also shows what each moved field holds now (S18). */
  const refusal = async (
    r: { kind: 'refused'; message: string; changed?: string[] | undefined },
    id: number,
  ): Promise<CaseOutcome> => {
    if (!r.changed?.length) return { kind: 'refused', message: r.message };
    const now = await latestShift(id);
    const said = (f: string) => {
      const v = now ? (now as unknown as Record<string, unknown>)[f] : undefined;
      if (v == null || v === '') return T('Tom');
      if (typeof v === 'boolean') return v ? T('Ja') : T('Nei');
      if (f === 'user_id') return fullName(users.find((u) => u.id === v)) || String(v);
      const o = oslo(String(v));
      return o ? `${fmtDs(o.date)} ${o.time}` : String(v);
    };
    return {
      kind: 'refused',
      message: T(
        'Hent siste versjon ved å se over verdiene under, og lagre på nytt hvis endringen fortsatt trengs.',
      ),
      changed: r.changed.map((f) => ({ label: FIELD[f] ?? f, value: now ? said(f) : T('Ukjent') })),
    };
  };
  const actOnCase = async (c: ReviewCase, a: CaseAction, input?: ActInput): Promise<CaseOutcome> => {
    if (a.kind === 'cover')
      return new Promise<CaseOutcome>((done) => openCover(a.shiftId, a.date, null, done));
    if (a.kind === 'decide' && a.what === 'absence') {
      const abs = c.absence;
      if (a.ok && abs?.deductible && (activeCase?.key !== c.key || caseDeduction.status !== 'ready'))
        return { kind: 'refused', message: T('Trekket er ikke hentet ennå. Prøv igjen om litt.') };
      try {
        if (a.ok)
          await absW.approve.mutateAsync({
            id: a.id,
            ...(abs?.deductible ? { expected_days: caseDeduction.days ?? undefined } : {}),
            change_reason: input?.reason,
          });
        else
          await absW.decline.mutateAsync({
            id: a.id,
            expected_status: 'pending',
            change_reason: input?.reason,
          });
        return {
          kind: 'saved',
          text: a.ok
            ? T('Forespørselen er innvilget: {t}.', { t: c.title })
            : T('Forespørselen er avslått: {t}.', { t: c.title }),
          detail: a.ok
            ? abs?.deductible && caseDeduction.days != null
              ? T('{n} dager er trukket fra saldoen. Den ansatte er varslet.', {
                  n: caseDeduction.days,
                })
              : T('Den ansatte er varslet.')
            : T('Ingen dager er trukket. Den ansatte er varslet med begrunnelsen.'),
        };
      } catch (e) {
        // 409: the deduction moved since it was shown; nothing was charged, and it is read again
        const days =
          e instanceof ApiError && e.status === 409
            ? (e.body as { days?: number } | undefined)?.days
            : undefined;
        if (days !== undefined)
          return {
            kind: 'refused',
            message: T('Trekket er endret siden du så det: {count} dag(er). Se over og godkjenn på nytt.', {
              count: days,
            }),
          };
        return outcomeOf(e);
      }
    }
    if (a.kind === 'decide' && a.what !== 'absence') {
      const call =
        a.what === 'offer'
          ? a.ok
            ? rw.approveOffer
            : rw.rejectOffer
          : a.ok
            ? rw.approveSwap
            : rw.rejectSwap;
      try {
        await call.mutateAsync({ id: a.id });
        // the answer is sent to the employees at once, so there is nothing to take back here
        return {
          kind: 'saved',
          text: a.ok ? T('Godkjent: {t}.', { t: c.title }) : T('Avslått: {t}.', { t: c.title }),
          detail: T('De ansatte er varslet. Svaret kan ikke angres herfra.'),
        };
      } catch (e) {
        return outcomeOf(e);
      }
    }
    const known = [...followUp.closeShifts, ...followUp.ahead, ...shifts];
    if (a.kind === 'correct-out' && input?.at) {
      const s = known.find((x) => x.id === a.shiftId);
      if (!s) return { kind: 'refused', message: T('Vakten finnes ikke lenger i perioden som er lest.') };
      const at = input.at;
      const o = oslo(at);
      const clockAt = o
        ? o.date !== oslo(s.punch_in_time)?.date
          ? `${fmtDs(o.date)} ${o.time}`
          : o.time
        : '';
      // the punch is written only if nobody has clocked out or moved the clock-in since it was read
      const r = await hold([s.id], () =>
        w.update.mutateAsync({
          id: s.id,
          body: {
            punch_out_time: at,
            change_reason: input.reason,
            if_unchanged: { punch_in_time: s.punch_in_time, punch_out_time: null },
          } as Partial<ShiftWrite>,
        }),
      );
      if (r.kind === 'busy') return { kind: 'refused', message: T('Vakten lagres allerede. Vent litt.') };
      if (r.kind === 'refused') return refusal(r, s.id);
      if (r.kind === 'unknown') {
        // no answer: what the server holds decides, before anything is sent again (S18)
        const now = await latestShift(s.id);
        if (!now || !now.punch_out_time || Date.parse(now.punch_out_time) !== Date.parse(at))
          return { kind: 'unknown' };
      }
      return {
        kind: 'saved',
        text: T(
          'Utstemplingen {t} er registrert med begrunnelse «{r}». Timene er ikke godkjent av denne rettingen.',
          {
            t: clockAt,
            r: input.reason ?? '',
          },
        ),
        detail: T('Endringen er logget, og den ansatte får beskjed om den rettede tiden.'),
      };
    }
    if (a.kind === 'correct-in' && input?.at) {
      const s = known.find((x) => x.id === a.shiftId);
      if (!s) return { kind: 'refused', message: T('Vakten finnes ikke lenger i perioden som er lest.') };
      const at = input.at;
      // the clock-in only if nobody has clocked in since it was read
      const r = await hold([s.id], () =>
        w.update.mutateAsync({
          id: s.id,
          body: {
            punch_in_time: at,
            ...(input.reason ? { change_reason: input.reason } : {}),
            if_unchanged: { punch_in_time: null },
          } as Partial<ShiftWrite>,
        }),
      );
      if (r.kind === 'busy') return { kind: 'refused', message: T('Vakten lagres allerede. Vent litt.') };
      if (r.kind === 'refused') return refusal(r, s.id);
      if (r.kind === 'unknown') {
        const now = await latestShift(s.id);
        if (!now?.punch_in_time || Date.parse(now.punch_in_time) !== Date.parse(at))
          return { kind: 'unknown' };
      }
      return {
        kind: 'saved',
        text: T(
          'Innstemplingen {t} er registrert med begrunnelse «{r}». Det er ikke det samme som å godkjenne timene.',
          {
            t: oslo(at)?.time ?? '',
            r: input.reason ?? '',
          },
        ),
      };
    }
    if (a.kind === 'pause' && input?.reason) {
      const s = known.find((x) => x.id === a.shiftId);
      const pz = c.pause;
      if (!s || !pz)
        return { kind: 'refused', message: T('Vakten finnes ikke lenger i perioden som er lest.') };
      const lunchIn = input.lunchIn ?? null;
      const lunchOut = input.lunchOut ?? null;
      // written only over the registration that was shown: the clock times, the break and the approval (S18)
      const r = await hold([s.id], () =>
        w.update.mutateAsync({
          id: s.id,
          body: {
            lunch_in_time: lunchIn,
            lunch_out_time: lunchOut,
            change_reason: input.reason,
            if_unchanged: {
              punch_in_time: pz.punchIn,
              punch_out_time: pz.punchOut,
              lunch_in_time: pz.lunchIn,
              lunch_out_time: pz.lunchOut,
              is_approved: false,
            },
          } as Partial<ShiftWrite>,
        }),
      );
      if (r.kind === 'busy') return { kind: 'refused', message: T('Vakten lagres allerede. Vent litt.') };
      if (r.kind === 'refused') return refusal(r, s.id);
      const same = (x: string | null | undefined, y: string | null) =>
        x == null ? y == null : y != null && Date.parse(x) === Date.parse(y);
      if (r.kind === 'unknown') {
        const now = await latestShift(s.id);
        if (!now || !same(now.lunch_in_time, lunchIn) || !same(now.lunch_out_time, lunchOut))
          return { kind: 'unknown' };
      }
      const min = lunchIn && lunchOut ? Math.round((Date.parse(lunchOut) - Date.parse(lunchIn)) / 60_000) : 0;
      const gross = Math.round((Date.parse(pz.punchOut) - Date.parse(pz.punchIn)) / 60_000);
      return {
        kind: 'saved',
        text: T(
          'Pausen er registrert som {n} min. Tid etter pause: {net}. Timene er ikke godkjent av rettingen.',
          {
            n: min,
            net: hoursMin(gross - min),
          },
        ),
        detail: T('Endringen er logget, og den ansatte får beskjed om den rettede tiden.'),
      };
    }
    if (a.kind === 'plan' && input?.start && input.end) {
      const s = known.find((x) => x.id === a.shiftId);
      if (!s || !c.rest)
        return { kind: 'refused', message: T('Vakten finnes ikke lenger i perioden som er lest.') };
      const start = input.start;
      const end = input.end;
      const r = await hold([s.id], () =>
        w.update.mutateAsync({
          id: s.id,
          body: {
            start_time: start,
            end_time: end,
            if_unchanged: {
              start_time: c.rest?.after.start,
              end_time: c.rest?.after.end,
              user_id: s.user_id,
              published: !!c.rest?.after.published,
            },
          } as Partial<ShiftWrite>,
        }),
      );
      if (r.kind === 'busy') return { kind: 'refused', message: T('Vakten lagres allerede. Vent litt.') };
      if (r.kind === 'refused') return refusal(r, s.id);
      if (r.kind === 'unknown') {
        const now = await latestShift(s.id);
        if (!now || Date.parse(now.start_time) !== Date.parse(start)) return { kind: 'unknown' };
      }
      const span = `${oslo(start)?.time ?? ''}–${oslo(end)?.time ?? ''}`;
      return {
        kind: 'saved',
        text: c.rest.after.published
          ? T(
              'Neste vakt er flyttet til {t}. Planendringen er lagret, og den ansatte får beskjed slik planen varsler.',
              { t: span },
            )
          : T(
              'Neste vakt er flyttet til {t}. Planendringen er lagret som utkast; publisering er en egen handling.',
              { t: span },
            ),
      };
    }
    if (a.kind === 'approve') {
      // only the chosen records of the scope on screen (S10); one PUT accept per shift, as Kontroll approves
      const ids = input?.ids?.length ? input.ids : (c.approve ?? a.shiftIds);
      let done = 0;
      for (const id of ids) {
        const r = await hold([id], () => approve.mutateAsync({ id, approved: true }));
        if (r.kind === 'done') done++;
        else if (!done) return r.kind === 'refused' ? refusal(r, id) : { kind: 'unknown' };
        else
          return {
            kind: 'partial',
            text: T('Godkjente {done} av {all}.', { done, all: ids.length }),
            message: r.kind === 'refused' ? r.message : T('Vi vet ikke om resten ble godkjent.'),
          };
      }
      const rest = (c.approve ?? a.shiftIds).length - ids.length;
      return {
        kind: 'saved',
        text: !c.members?.length
          ? T('Timene er godkjent som stemplet.')
          : ids.length === 1
            ? T('1 registrering er godkjent.')
            : T('{n} registreringer er godkjent.', { n: ids.length }),
        detail: [
          rest > 0
            ? rest === 1
              ? T('1 registrering i utvalget er ikke valgt og står åpen.')
              : T('{n} registreringer i utvalget er ikke valgt og står åpne.', { n: rest })
            : '',
          T('Godkjenning er ikke eksport til lønn.'),
        ]
          .filter(Boolean)
          .join(' '),
        // approving sends nothing to anyone, so it can be taken back (the same route, false)
        undo: async () => {
          for (const id of ids) {
            const u = await hold([id], () => approve.mutateAsync({ id, approved: false }));
            if (u.kind !== 'done')
              return u.kind === 'refused' ? { kind: 'refused', message: u.message } : { kind: 'unknown' };
          }
          return { kind: 'saved', text: T('Godkjenningen er tatt tilbake.') };
        },
      };
    }
    return { kind: 'refused', message: T('Denne handlingen finnes ikke her.') };
  };
  /**
   * Hugin's advice (issue #131): one context for the Varsler entries, the note after a change and
   * the suggestions above the week. Hours only for timesheet.view (the chat's gate); a choice only
   * where the reader may plan. Fixed rules, no model call (advice.ts).
   */
  const me = useAuth((st) => st.user?.id ?? null);
  const showHours = can('timeliste.se');
  const adviceCtx = useMemo(
    () => ({
      rows: grid.rows,
      shifts,
      offers,
      swaps,
      t,
      showHours,
      canGive: editable,
      now: Date.now(),
      me,
      avail: availQ.data,
    }),
    [grid.rows, shifts, offers, swaps, t, showHours, editable, me, availQ.data],
  );
  const evaluation = useMemo(
    () =>
      changed && changed.week === monday
        ? evaluate(changed.s, shifts.find((x) => x.id === changed.s.id) ?? null, adviceCtx)
        : null,
    [changed, monday, shifts, adviceCtx],
  );
  // a change with no new warning is a short message that fades, not a box over the plan (owner 18.10)
  const toasted = useRef<unknown>(null);
  useEffect(() => {
    if (!evaluation || evaluation.added.length || toasted.current === changed) return;
    toasted.current = changed;
    // a save's own message (with its Angre) is showing: Hugin's word joins it instead of replacing it
    const cur = useToast.getState();
    if (cur.msg) cur.show(`${cur.msg}, ${evaluation.headline}`, cur.undo ?? undefined);
    else toast(`${evaluation.headline}, ${evaluation.detail}`);
  }, [evaluation, changed]);
  const notes = useMemo(
    () => (noData ? [] : nudges({ ...adviceCtx, days, absences: absQ.data ?? [], planning, today })),
    [noData, adviceCtx, days, absQ.data, planning, today],
  );
  /**
   * Finn erstatter (issue #147 F1): one shift's candidates in the side panel (a sheet on a phone), a
   * preview of the one change, and the write only after the plan, the absences and the availability
   * are read again and the person is still judged as the manager saw them (UC13). From the review bar
   * the bar waits for the answer and shows it on its case; from anywhere else the panel shows it.
   */
  const [coverPick, setCoverPick] = useState<number | null>(null);
  const coverSeen = useRef<{ seen: Seen; verdict: string } | null>(null);
  const coverDone = useRef<((o: CaseOutcome) => void) | null>(null);
  const settleCover = (o: CaseOutcome) => {
    const f = coverDone.current;
    coverDone.current = null;
    f?.(o);
  };
  // the panel closed or became another one: the bar's case was not answered
  // biome-ignore lint/correctness/useExhaustiveDependencies: only when the panel's kind changes
  useEffect(() => {
    if (panel?.kind === 'cover') return;
    setCoverPick(null);
    coverSeen.current = null;
    settleCover({ kind: 'cancelled' });
  }, [panel?.kind]);
  const openCover = (
    shiftId: number,
    date: string | null,
    pick: number | null,
    done?: (o: CaseOutcome) => void,
  ) =>
    guard(
      T('Finn erstatter'),
      () => {
        settleCover({ kind: 'cancelled' });
        // the shift's own period, so its week is read and the preview is visible in the plan
        if (date && !days.includes(date))
          setMonday(view === 'day' ? date : view === 'month' ? monthStart(date) : mondayOf(date));
        if (!panel && document.activeElement instanceof HTMLElement) origin.current = document.activeElement;
        setDraft(null);
        setPanel({ kind: 'cover', shiftId });
        setCoverPick(pick);
        coverSeen.current = null;
        coverDone.current = done ?? null;
      },
      () => done?.({ kind: 'cancelled' }),
    );
  const coverShift = panel?.kind === 'cover' ? shifts.find((x) => x.id === panel.shiftId) : undefined;
  const coverReady =
    !!coverShift &&
    days.includes(dateOf(coverShift)) &&
    !(absQ.isPending && absQ.fetchStatus !== 'idle') &&
    !(availQ.isPending && availQ.fetchStatus !== 'idle');
  const coverList = useMemo(
    () =>
      coverShift && coverReady
        ? tiersOf(ranked(coverShift, grid.rows, shifts, t, showHours, me), availQ.data ?? [], coverShift)
        : null,
    [coverShift, coverReady, grid.rows, shifts, t, showHours, me, availQ.data],
  );
  const coverProblem =
    panel?.kind !== 'cover'
      ? null
      : shiftsQ.isError || absQ.isError || availQ.isError
        ? T('Kunne ikke lese planen rundt vakten.')
        : !coverShift && shiftsQ.data && !shiftsQ.isFetching
          ? T('Vakten finnes ikke i planen lenger. Den kan være slettet eller flyttet.')
          : null;
  const nameOf = (id: number | null | undefined) => {
    const u = id != null ? users.find((x) => x.id === id) : undefined;
    return u ? fullName(u) : null;
  };
  /** Read everything the verdict rests on again, then write only if nothing moved. */
  const applyCover = async (shiftId: number, userId: number): Promise<CoverResult> => {
    const snap = coverSeen.current;
    if (!snap) return { kind: 'stale', message: T('Velg personen på nytt.') };
    // the people (their agreed week) and the company are inputs to the verdict too (R1 follow-up);
    // a read that fails is not a check, so nothing is written
    const [data, a, v, u, tn] = await Promise.all([
      rereadShifts(),
      absQ.refetch(),
      availQ.refetch(),
      usersQ.refetch(),
      qc
        .fetchQuery({ queryKey: ['tenant'], queryFn: () => get<Tenant>('/tenant'), staleTime: 0 })
        .catch(() => null),
    ]);
    if (!data || a.isError || v.isError || u.isError || !u.data || !tn)
      return {
        kind: 'refused',
        message: T('Kunne ikke lese planen på nytt. Ingenting er endret. Prøv igjen.'),
      };
    const now = data.shifts.find((x) => x.id === shiftId);
    if (!now || changedSince(snap.seen, now))
      return {
        kind: 'stale',
        message: T('Vakten er endret siden du så den. Ingenting er lagret. Se kandidatene på nytt.'),
      };
    const fresh = buildGrid(days, data.shifts, u.data, a.data ?? [], v.data ?? [], tn);
    const again = tiersOf(ranked(now, fresh.rows, data.shifts, t, showHours, me), v.data ?? [], now).find(
      (c) => c.userId === userId,
    );
    if (verdictKey(again) !== snap.verdict)
      return {
        kind: 'stale',
        message: again
          ? T('Noe er endret for {name} siden du så det. Ingenting er lagret. Se over på nytt.', {
              name: again.name,
            })
          : T('Personen kan ikke velges lenger. Ingenting er lagret.'),
      };
    const old = now.user_id;
    const oldName = nameOf(old);
    const newName = again?.name ?? nameOf(userId) ?? '';
    const r = await hold([shiftId], () => w.update.mutateAsync({ id: shiftId, body: { user_id: userId } }));
    if (r.kind === 'busy') return { kind: 'refused', message: T(BUSY) };
    if (r.kind === 'refused') return { kind: 'refused', message: `${T('Ikke lagret')}: ${r.message}` };
    if (r.kind === 'unknown') {
      await rereadShifts();
      return { kind: 'unknown' };
    }
    setChanged(now);
    const told = now.published
      ? oldName
        ? T('{old} og {new} får varsel.', { old: oldName, new: newName })
        : T('{new} får varsel.', { new: newName })
      : T('Lagret som utkast. Ingen er varslet før du publiserer.');
    const after = seenOf({ ...now, user_id: userId });
    return {
      kind: 'saved',
      text: T('{name} har fått vakten {when}', {
        name: newName,
        when: `${fmtDs(dateOf(now))} ${timeText(now)}`,
      }),
      detail: told,
      // the inverse of this one change, and only while the shift is still as it was left (AI-09)
      undo: async () => {
        const back = await rereadShifts();
        const cur = back?.shifts.find((x) => x.id === shiftId);
        if (!back) return { kind: 'unknown' };
        if (!cur || cur.user_id !== userId || changedSince({ ...after, updated_at: undefined }, cur))
          return { kind: 'refused', message: T('Vakten er endret etterpå, så den kan ikke angres herfra.') };
        const u = await hold([shiftId], () => w.update.mutateAsync({ id: shiftId, body: { user_id: old } }));
        if (u.kind === 'done')
          return {
            kind: 'saved',
            text: oldName
              ? T('Angret: vakten er tilbake hos {name}', { name: oldName })
              : T('Angret: vakten er ledig igjen'),
            detail: now.published ? T('De berørte får varsel om endringen.') : undefined,
          };
        if (u.kind === 'unknown') {
          await rereadShifts();
          return { kind: 'unknown' };
        }
        return { kind: 'refused', message: u.kind === 'busy' ? T(BUSY) : u.message };
      },
    };
  };
  // what the manager is shown when a person is previewed: the write is judged against this (UC13)
  useEffect(() => {
    if (coverPick == null) {
      coverSeen.current = null;
      return;
    }
    if (coverSeen.current || !coverShift || !coverList) return;
    coverSeen.current = {
      seen: seenOf(coverShift),
      verdict: verdictKey(coverList.find((c) => c.userId === coverPick)),
    };
  }, [coverPick, coverShift, coverList]);
  /** The week item a case's advice is about: its own, or the absence over its shift (a cover case). */
  const itemOfCase = (c: ReviewCase) =>
    c.key.startsWith('wk-')
      ? review.find((y) => `wk-${y.key}` === c.key)
      : c.shiftId != null
        ? review.find((y) => y.group === 'absence' && y.shiftId === c.shiftId)
        : undefined;
  /**
   * The bar's cases, each one a person change would fix given «Finn erstatter» first, one per shift
   * Hugin would hand on (F1). Who may plan only; the reader sees the advice and no button.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: itemOfCase reads only review
  const barCases = useMemo(() => {
    const all = [...followUp.closeShifts, ...followUp.ahead, ...shifts];
    const shiftOf = (id: number) => all.find((y) => y.id === id);
    const ahead = (sh: ApiShift) => !sh.punch_in_time && Date.parse(sh.start_time) > Date.now();
    // the label names the shift it acts on; a shift nobody had gets «Finn ansatt» (owner's review 2026-10-28)
    const coverFor = (ids: number[]): CaseAction[] =>
      ids.map((id) => {
        const sh = shiftOf(id);
        const when = sh ? `${DN[dow(dateOf(sh))]?.toLowerCase()} ${timeText(sh)}` : '';
        return {
          kind: 'cover',
          shiftId: id,
          date: sh ? dateOf(sh) : null,
          label: !sh
            ? T('Finn erstatter')
            : sh.user_id == null
              ? T('Finn ansatt til {when}', { when })
              : T('Finn erstatter {when}', { when }),
        };
      });
    // the open shifts of the next seven days, the ones the queue's «ledige vakter» case counts
    const openAhead = followUp.ahead
      .filter((sh) => sh.user_id == null && ahead(sh))
      .sort((a, b) => Date.parse(a.start_time) - Date.parse(b.start_time));
    const withCover = (c: ReviewCase): ReviewCase => {
      if (!editable) return c;
      let ids: number[];
      // the first open shift; the next ones after it (prototype case-templates: at most three actions)
      if (c.key === 'open') ids = openAhead.slice(0, 1).map((sh) => sh.id);
      // approved absence on a shift ahead: that shift, in any period (the panel goes to its week)
      else if (c.key.startsWith('cover') && c.shiftId != null) {
        const sh = shiftOf(c.shiftId);
        ids = sh && ahead(sh) ? [c.shiftId] : [];
      } else {
        const x = itemOfCase(c);
        const advice = x ? adviceFor(x, adviceCtx) : null;
        ids = [
          ...new Set(
            (advice?.choices ?? []).flatMap((ch) => (ch.apply.kind === 'give' ? [ch.apply.shiftId] : [])),
          ),
        ];
      }
      if (!ids.length) return c;
      // the decision Saker settles in place (moving the later shift of a rest task) stays first (#286 S11)
      const [first, ...rest] = c.actions;
      return first?.kind === 'plan'
        ? { ...c, actions: [first, ...coverFor(ids), ...rest] }
        : { ...c, actions: [...coverFor(ids), ...c.actions] };
    };
    const cases = reviewCases.cases.map(withCover);
    // Hugin's unasked suggestions are cases now, not a second feed (review of #147, F3). An open
    // shift past the queue's seven days is its own case; a week over the agreed hours with a move
    // that eases it is one, unless a rule case already holds that person. A holiday is the queue's
    // request; under the agreed week and no shifts are in each row's hours, so they are not said twice.
    const covered = new Set([
      ...openAhead.map((sh) => sh.id),
      ...cases.flatMap((c) => c.actions.flatMap((a) => (a.kind === 'cover' ? [a.shiftId] : []))),
    ]);
    const extra: ReviewCase[] = [];
    for (const n of notes) {
      // only who may plan gets these: each is a choice to make, and the grid shows the facts to all
      if (!editable) break;
      if (n.key.startsWith('open-') && n.shiftId != null && !covered.has(n.shiftId)) {
        const sh = shiftOf(n.shiftId);
        if (!sh) continue;
        extra.push({
          key: `hg-${n.key}`,
          cat: 'planlegg',
          tone: 'w',
          date: dateOf(sh),
          dateLabel: `${DN[dow(dateOf(sh))]} ${fmtDs(dateOf(sh))}`,
          when: T('I perioden du ser'),
          title: T('Ledig vakt uten ansatt'),
          description: `${timeText(sh)}, ${typeLabel(sh.shiftType) ?? ''}`.replace(/, $/, ''),
          facts: [],
          actions: coverFor([sh.id]),
          shiftId: sh.id,
          records: undefined,
        });
      } else if (
        n.key.startsWith('bal-') &&
        n.choice?.apply.kind === 'give' &&
        n.person != null &&
        !cases.some((c) => c.key.startsWith('wk-w') && c.person?.id === n.person)
      ) {
        const u = users.find((y) => y.id === n.person);
        const sh = shiftOf(n.choice.apply.shiftId);
        extra.push({
          key: `hg-${n.key}`,
          cat: 'planlegg',
          tone: 'i',
          person: u ? { id: u.id, name: fullName(u) } : undefined,
          date: sh ? dateOf(sh) : undefined,
          dateLabel: sh ? `${DN[dow(dateOf(sh))]} ${fmtDs(dateOf(sh))}` : undefined,
          when: T('I perioden du ser'),
          title: T('Over avtalt uke'),
          description: n.text,
          facts: [],
          actions: coverFor([n.choice.apply.shiftId]),
          shiftId: sh?.id,
          records: undefined,
        });
      }
    }
    const rank = (c: ReviewCase) => CASE_CATS.findIndex(([k]) => k === c.cat);
    return {
      cases: [...cases, ...extra].sort((a, b) => rank(a) - rank(b)),
      waiting: reviewCases.waiting,
    };
  }, [reviewCases, review, adviceCtx, editable, shifts, notes, followUp.ahead, followUp.closeShifts, users]);
  /** A choice Hugin offered, pressed: a give opens its preview; nothing is written from a suggestion (F1). */
  const applyChoice = (a: Apply) => {
    if (a.kind === 'give') {
      const s = shifts.find((x) => x.id === a.shiftId);
      return openCover(a.shiftId, s ? dateOf(s) : null, a.userId);
    }
    return a.kind === 'decide' ? decide(a.what, a.id, a.ok) : undefined;
  };
  // ?ledig=1 (#131): once the settled week's shifts are in, the first open shift ahead opens with
  // its ranked takers; with none ahead, the week says so. The address arriving again while the page
  // is open (Hugin's chat link) asks again; a refetch never does.
  const askedOpen = useRef(link.openShift);
  const ledigParam = params.get('ledig') === '1';
  const ledigSeen = useRef(ledigParam);
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per ask, when the week's shifts have arrived
  useEffect(() => {
    if (ledigParam && !ledigSeen.current) askedOpen.current = true;
    ledigSeen.current = ledigParam;
    if (!askedOpen.current || !shiftsQ.data || settling.current) return;
    askedOpen.current = false;
    const first = shiftsQ.data.shifts
      .filter((x) => x.user_id == null && Date.parse(x.start_time) > Date.now())
      .sort((a, b) => Date.parse(a.start_time) - Date.parse(b.start_time))[0];
    // «Fyll en ledig vakt»: the first open shift ahead, straight into Finn erstatter (issue #153)
    if (first) openCover(first.id, dateOf(first), null);
    else toast(T('Ingen ledige vakter fremover denne uka.'));
  }, [shiftsQ.data, monday, ledigParam]);
  /** Legg til vakt: the draft as a shift nobody holds yet, ranked like an open one (#131). */
  const matchDraft = (d: DraftShift) => {
    if (!/^\d\d:\d\d$/.test(d.from) || !/^\d\d:\d\d$/.test(d.to) || !d.date) return [];
    const start = osloISO(d.date, d.from);
    const end = osloISO(d.to <= d.from ? addDays(d.date, 1) : d.date, d.to);
    const ty = types.find((x) => x.id === d.shift_type_id) ?? null;
    const s = {
      id: d.id ?? -1,
      user_id: null,
      start_time: start,
      end_time: end,
      shift_type_id: d.shift_type_id,
      shiftType: ty,
    } as ApiShift;
    return ranked(s, grid.rows, shifts, t, showHours, me);
  };
  const huginOpen = (n: { shiftId?: number; person?: number }) => {
    const x = n.shiftId != null ? shifts.find((y) => y.id === n.shiftId) : undefined;
    if (x) tapShift(x);
    else if (n.person != null) openPerson(n.person);
  };
  // a shift the shown proposal changes opens the proposal's own editor: a new version, nothing saved (P08)
  const editInPlan = (id: number) =>
    guard(T('Endre vakten i forslaget'), () => {
      rememberOrigin();
      aiPlan.beginEdit(id);
      setPanel({ kind: 'plan' });
    });
  const scopedShifts = new Set(
    scopeKeys
      ? [
          ...barCases.cases
            .filter((c) => scopeKeys.includes(c.key) && c.shiftId != null)
            .map((c) => c.shiftId),
          ...(reviewFocus?.shiftIds ?? []),
        ]
      : [],
  );
  const card1 = (s: ApiShift, _i: number, list: ApiShift[]) => (
    <ShiftCard
      key={s.id}
      s={s}
      entries={grid.entries.get(s.id) ?? []}
      picked={sel.has(s.id)}
      scoped={scopedShifts.has(s.id)}
      onOpen={() => (aiPlan.marks.has(s.id) ? editInPlan(s.id) : tapShift(s))}
      onPick={() => setSel(toggle(sel, s.id))}
      menu={menuFor(s)}
      onApprove={can('timeliste.godkjenne') ? () => approveOne(s, true) : null}
      compact={list.length > 1}
      v19
      tasks={showTasks}
      tmode={tmode}
      // the shift of the case open in the review bar is marked too (issue #147, UC9)
      selected={
        (panel?.kind === 'shift' && panel.id === s.id) ||
        draft?.id === s.id ||
        (panel?.kind === 'cover' && panel.shiftId === s.id) ||
        activeCase?.shiftId === s.id
      }
      project={byProject ? projectOf(s) : undefined}
      mark={aiPlan.marks.get(s.id)}
      ruleMark={(icon) =>
        warnPop(
          { kind: 'shift', shiftId: s.id },
          `${s.user ? fullName(s.user) : T('Ledig vakt')}, ${planned(s).replace(/ \(.*\)$/, '')}`,
          // a night says both dates (WP-03)
          (oslo(s.end_time)?.date ?? dateOf(s)) !== dateOf(s)
            ? T('{a} til {b}', {
                a: dayTitle(dateOf(s)),
                b: dayTitle(oslo(s.end_time)?.date ?? '').toLowerCase(),
              })
            : dayTitle(dateOf(s)),
          (n) => (
            <button
              type="button"
              data-testid="shift-alerts"
              aria-label={`${n} ${n === 1 ? 'varsel' : 'varsler'} ${s.user ? fullName(s.user) : T('Ledig vakt')} ${timeText(s)}`}
            >
              {icon}
            </button>
          ),
        ) ?? icon
      }
      note={
        panel?.kind === 'cover' && panel.shiftId === s.id && coverPick != null
          ? T('Forhåndsvisning: til {name}, ikke lagret', { name: nameOf(coverPick) ?? '' })
          : hg.marks.get(s.id)?.what === 'moves'
            ? T('Forslag: flyttes')
            : hg.marks.get(s.id)?.what === 'publishes'
              ? T('Forslag: publiseres')
              : undefined
      }
    />
  );
  const cell = (c: Cell | undefined, row: Row | null, d: string, extra?: ReactNode) => {
    const we = dow(d) >= 5;
    const list = (c?.shifts ?? grid.open.get(d) ?? []).filter(inView);
    // «Uka»: a day split on a project switch is one block with its parts inside (#118), by Timer's rule
    const parts = !planning && row ? splitParts(list) : null;
    const av = c?.avail ?? null;
    const abs = c?.absence ?? null;
    const empty = !list.length && !abs;
    const cls = [
      'cell',
      we ? 'we' : '',
      weekLine(d) ? 'mon' : '',
      av && showAvail ? (av.kind === 'unavail' ? 'unavail' : 'partly') : '',
      list.length + (abs ? 1 : 0) > 1 ? 'multi' : '',
      empty ? 'empty' : '',
    ]
      .filter(Boolean)
      .join(' ');
    return (
      <div
        className={cls}
        key={d}
        data-testid={row ? 'cell' : 'open-cell'}
        onDragOver={(e) => {
          if (editable && e.dataTransfer.types.includes('text/shift-id')) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            // the cell it would land in lights up; a class, not state, so the plan does not re-render (owner 18.10)
            e.currentTarget.classList.add('drop');
          }
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null))
            e.currentTarget.classList.remove('drop');
        }}
        onDrop={(e) => {
          e.currentTarget.classList.remove('drop');
          const id = Number(e.dataTransfer.getData('text/shift-id'));
          if (id) {
            e.preventDefault();
            moveShift(id, row?.user.id ?? null, d);
          }
        }}
      >
        <div className={`pad ${cardCss.stack}`}>
          {abs && <AbsenceCard a={abs} onOpen={() => openAbsence(draftOfAbsence(abs))} />}
          {parts && (
            <div
              className="splitday"
              data-testid="split-day"
              aria-label={T('{n} deler, byttet {what}', {
                n: parts.length,
                what: projectWord.lower,
              })}
            >
              <span className="sdh">
                <b>
                  {oslo(parts[0]?.punch_in_time ?? '')?.time}–
                  {oslo(parts.at(-1)?.punch_out_time ?? '')?.time ?? '?'}
                </b>
                <small>
                  {T('{n} deler, byttet {what}', {
                    n: parts.length,
                    what: projectWord.lower,
                  })}
                </small>
              </span>
              {parts.map(card1)}
            </div>
          )}
          {list.filter((s) => !parts?.includes(s)).map(card1)}
          {hg.ghosts
            .filter((g) => g.date === d && g.userId === (row?.user.id ?? null))
            .map((g, i) => (
              <button
                type="button"
                key={`${g.actionId}-${i}`}
                className="ghost"
                data-testid="proposal"
                onClick={() =>
                  guard('Hugin', () => {
                    setAiOut('');
                    setPanel({ kind: 'proposal', id: g.actionId });
                  })
                }
              >
                <b>
                  {g.start}–{g.end}
                </b>
                <span>{T('Forslag, ikke lagret')}</span>
              </button>
            ))}
          {empty && mayRegister && row && (
            <button
              type="button"
              className="addcell"
              aria-label={T('Legg til registrering for {who} {d}', {
                who: fullName(row.user),
                d: fmtDs(d),
              })}
              onClick={() => addRegistration(d, row.user.id)}
            />
          )}
          {empty && editable && (
            <button
              type="button"
              className="addcell"
              aria-label={T('Ny vakt {who} {d}', { who: row ? fullName(row.user) : T('ledig'), d: fmtDs(d) })}
              onClick={(e) => {
                const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                emptyCell({
                  userId: row?.user.id ?? null,
                  date: d,
                  x: r.left,
                  y: r.bottom + 4,
                  who: row ? fullName(row.user) : T('Ledig vakt'),
                });
              }}
            />
          )}
        </div>
        {av && empty && showAvail && (
          <span className="availtxt">
            {av.kind === 'unavail'
              ? T('Ikke tilgjengelig')
              : T('Kun {from}–{to}', { from: av.from, to: av.to })}
          </span>
        )}
        {extra}
      </div>
    );
  };
  /**
   * The open row's gap chip (handoff 2026-10-24 §4.5): from today on, the hours the staffing need asks for
   * and nobody covers, «Mangler 1 22–23» or «3 hull»; a press asks Hugin who can take it, by the app's rules.
   */
  const gapChip = (d: string) => {
    // no shifts yet is not an empty day: no gap until they have answered (#272 A-02)
    if (noData || !planning || phone || nd > 7 || d < today || !labour.need) return null;
    const gaps = hourGaps(labour.need, d, shifts);
    const g = gaps[0];
    if (!g) return null;
    const hm = (t: string) => (t.endsWith(':00') ? t.slice(0, 2) : t);
    const span = (x: { from: string; to: string }) => `${hm(x.from)}–${hm(x.to)}`;
    const day = `${DNL[dow(d)]} ${fmtDs(d)}`;
    const label =
      gaps.length === 1
        ? T('Mangler {n} {span}', { n: g.need - g.have, span: span(g) })
        : T('{n} hull', { n: gaps.length });
    return (
      <button
        type="button"
        className={gc.chip}
        data-testid="gap-chip"
        title={gaps.map((x) => T('Mangler {n} kl {span}', { n: x.need - x.have, span: span(x) })).join(', ')}
        aria-label={`${day}: ${gaps.map((x) => T('mangler {n} kl {span}', { n: x.need - x.have, span: span(x) })).join(', ')}`}
        onClick={() =>
          huginDock
            ? askInHugin(
                gaps.length === 1
                  ? T('Hvem kan ta {day} {span}? Det mangler {n}.', {
                      day,
                      span: span(g),
                      n: g.need - g.have,
                    })
                  : T('Hvem kan dekke hullene {day}: {list}?', { day, list: gaps.map(span).join(', ') }),
              )
            : setPanel({ kind: 'day-details', date: d })
        }
      >
        {label}
      </button>
    );
  };
  const gapDays = days.filter((d) => gapChip(d) != null);

  const openAll = days.flatMap((d) => grid.open.get(d) ?? []);
  const showKr = can('vaktplan.kost');
  // V19: money shows only when this reader may see it and has ticked "Vis budsjett og kostnader"
  const money = showKr && showMoney;
  /**
   * What the shifts cost the employer, not what they pay out.
   *
   * This was gross wages only: no arbeidsgiveravgift, no pension, and `hourly_rate ?? 0`, so a
   * monthly-salaried person was free. Bemanning mot omsetning applied the employer's
   * contribution, so the two pages said "kr" and meant figures about 16% apart (designer's
   * report, 2026-09-21). One model now, in rules/labour.ts, with the company's own rates.
   */
  const rates = ratesOf(labour.rates);
  const cost = (list: ApiShift[]) =>
    costOf(
      list.reduce(
        (a, s) => a + paidHours(s, breakPolicy(tenant ?? undefined)) * hourlyRateOf(s.user as Paid | null),
        0,
      ),
      rates,
    ).total;
  const kr = (n: number) => Math.round(n).toLocaleString('nb-NO');
  // the bottom row's shifts in the switch's time (#201): planned, or clocked with a running one up to now
  const mshifts = tmode
    ? shifts.flatMap((s) => {
        const m = asMode(s, tmode);
        return m ? [m] : [];
      })
    : shifts;
  // over a week each day is today's 124px and the grid scrolls; a week shares the width (plan 0029)
  const dayTitle = (d: string) => `${DNL[dow(d)]?.replace(/^./, (c) => c.toUpperCase())} ${fmtDs(d)}`;
  /**
   * The shifts a list of issues is about: a shift's own (`s{id}-n`), and for a week rule (`w{user}-…`) that
   * person's shifts on screen. A week rule is about the week, so each shift in it is part of the cause.
   * ponytail: every day on screen, not only the rule's week; narrow by week when two weeks show.
   */
  const shiftsOfIssues = (list: Issue[]) => {
    const ids = new Set<number>();
    for (const x of list) {
      const one = /^s(\d+)-/.exec(x.key);
      if (one) ids.add(Number(one[1]));
      const week = /^w(\d+)-/.exec(x.key);
      if (week)
        for (const y of shifts) if (y.user_id === Number(week[1]) && days.includes(dateOf(y))) ids.add(y.id);
    }
    return [...ids];
  };
  /**
   * The Saker cases a list of rule hits is in. A punch rule (late in, early out) has no case of its own: it is a
   * record in the month-close worklist «vakter med avvik fra plan» (`time-deviation`), so it points there.
   */
  const casesOfIssues = (list: Issue[]) => {
    const keys = new Set(
      casesForKeys(
        list.map((x) => x.key),
        barCases.cases,
      ),
    );
    const worklist = barCases.cases.find((c) => c.key === 'time-deviation');
    if (worklist)
      for (const x of list) {
        const m = /^s(\d+)-(\d+)$/.exec(x.key);
        const code = m ? grid.entries.get(Number(m[1]))?.[Number(m[2])]?.code : undefined;
        if (code?.startsWith('punch_') && !casesForKeys([x.key], barCases.cases).length)
          keys.add(worklist.key);
      }
    return [...keys];
  };
  /** One warning popover, employee, day or shift (issue #146): the badge counts the list it opens. */
  const wpCtx: AdapterContext = {
    grid,
    days,
    shifts,
    t,
    editable,
    canPerson: can('ansatte.se'),
    canSettings: can('innstillinger.se'),
  };
  // the popover that is open, by scope: while it is, its badge stays even when the last case resolves
  // underneath it, so it can say so rather than vanish (WP-15)
  const [openPop, setOpenPop] = useState<string | null>(null);
  /** Where an issue's button leads: the correction where this reader may make it, through the guard. */
  const warnAction = (x: Issue) => {
    const a = x.action;
    if (a.kind === 'person') return openPerson(a.person);
    if (a.kind === 'week') {
      const rule = grid.rows
        .find((r) => r.user.id === a.person)
        ?.week.entries.find((e) => e.code === a.code && e.week === a.week);
      if (rule) openRule(a.person, rule);
      return;
    }
    const s = shifts.find((y) => y.id === a.shiftId);
    // deleted or moved out of the window since the list was drawn (WP-15)
    if (!s) return toast(T('Vakten finnes ikke lenger i planen. Listen er oppdatert.'));
    if (a.kind === 'punch') openShift(s, { punch: a.field });
    else if (a.kind === 'edit') openShift(s);
    else if (a.kind === 'lunch') {
      // the editor on Vakt, where the lunch row is since 2026-10-25, focused once it is drawn (#153 SP29)
      openShift(s, { section: 'vakt' });
      requestAnimationFrame(() => {
        const box = document.querySelector<HTMLElement>('[data-testid=vlunch]');
        box?.scrollIntoView?.({ block: 'nearest' });
        box?.querySelector<HTMLElement>('input, select, button')?.focus();
      });
    } else tapShift(s);
  };
  const warnPop = (
    of: Scope,
    title: string,
    scope: string | ((issues: Issue[]) => string),
    trigger: (n: number, sev: 'd' | 'w') => ReactElement,
  ) => {
    const issues = issuesOf(of, wpCtx);
    const id = JSON.stringify(of);
    if (!issues.length && openPop !== id) return null;
    // with Saker beside the week a count is a filter: the panel shows that day's, person's or shift's
    // cases (owner 2026-10-25 Q1); the popover stays for «Uka» and the phone
    if (!phone && planning) {
      // the count is the cases Saker shows when it is pressed, not the rule hits under them (owner 2026-10-25,
      // option A): two hits on one shift are one case, a week rule over five shifts is one
      const keys = casesOfIssues(issues);
      if (!keys.length && openPop !== id) return null;
      const held = barCases.cases.filter((c) => keys.includes(c.key));
      const t = trigger(keys.length, held.some((c) => c.tone === 'd') ? 'd' : 'w');
      // every count says what it is and what a press does (owner 2026-10-28: tooltips on every indicator)
      const tip =
        keys.length === 1
          ? T('1 sak å se over. Trykk for å vise den i Saker.')
          : T('{n} saker å se over. Trykk for å vise dem i Saker.', { n: keys.length });
      return (
        <Tip text={tip}>
          {cloneElement(t, {
            onClick: () =>
              guard(T('Saker'), () => {
                rememberSideOpener();
                setPanel(null);
                setDraft(null);
                setSideOpen(true);
                setSideTab('saker');
                setReviewFocus({
                  label: title,
                  keys,
                  shiftIds: shiftsOfIssues(issues),
                  // a counter, not the clock: two presses in one millisecond are still two filters
                  n: (reviewFocus?.n ?? 0) + 1,
                });
              }),
          })}
        </Tip>
      );
    }
    return (
      <WarningPopover
        // a new week, view or company is a new popover: it starts closed (WP-20)
        key={`${monday}-${view}-${tenant?.id ?? ''}`}
        onOpenChange={(o) => setOpenPop(o ? id : null)}
        trigger={trigger(issues.length, issues.some((x) => x.sev === 'd') ? 'd' : 'w')}
        title={title}
        scope={typeof scope === 'function' ? scope(issues) : scope}
        issues={issues}
        sheet={warnSheet}
        onAction={warnAction}
        onSetup={(to) => navigate(to)}
        onReview={(keys, cat) => {
          const label = cat
            ? `${title}, ${T(CATS.find(([c]) => c === cat)?.[1] ?? '').toLowerCase()}`
            : title;
          guard(T('Gjennomgå'), () =>
            // in planning the review bar opens on exactly those cases (#147 F4); «Uka» has no bar,
            // so its scoped panel stays
            planning
              ? setReviewFocus({ label, keys: casesForKeys(keys, barCases.cases), n: Date.now() })
              : setPanel({ kind: 'review', scope: { label, of, keys } }),
          );
        }}
      />
    );
  };
  // The band above the column already said a day had a problem; it could not say how many or whose,
  // so it was a colour and an errand (owner, 2026-09-21). Red when anything is a breach, yellow
  // when all of it is a warning, absent when the day is clean.
  const dayAlerts = (d: string) =>
    warnPop(
      { kind: 'day', day: d },
      dayTitle(d),
      // the people the day's cases are about, as the reference counts them (WP-02)
      (xs) => {
        const n = new Set(xs.map((x) => x.who)).size;
        return n === 1 ? T('1 medarbeider') : T('{n} medarbeidere', { n });
      },
      (n, sev) => (
        <button
          type="button"
          className={`alert ${sev} ${ab.badge} ${sev === 'd' ? ab.d : ab.w}`}
          data-testid="day-alerts"
          aria-label={`${n} ${n === 1 ? 'varsel' : 'varsler'} ${DNL[dow(d)]} ${fmtD(d)}`}
        >
          <b>{n}</b>
        </button>
      ),
    );
  // evenings and weekends over the last four weeks, against the team's average (#207, prototype fairOf); the team is
  // the people with agreed hours, as in the prototype, or everyone on screen when nobody's are known
  const fair =
    view === 'week' && planning && fairQ.data
      ? (() => {
          const by = fairSpread([...fairQ.data.shifts, ...shifts], addDays(monday, -21), addDays(monday, 6));
          // the whole staff, so a search or a filter does not change the colours
          const withNorm = grid.rows.filter((r) => (r.week.norm ?? 0) > 0);
          const team = withNorm.length ? withNorm : grid.rows;
          const avg = (k: 'eve' | 'wkd') =>
            team.reduce((a, r) => a + (by.get(r.user.id)?.[k] ?? 0), 0) / (team.length || 1);
          return { by, eve: avg('eve'), wkd: avg('wkd') };
        })()
      : null;
  // #199: the rows by department (the person's team), each under a heading that folds it to one line
  // off until Filter's «Grupper etter avdeling» turns it on (owner 2026-10-27), remembered in this browser
  const [grouped, setGroupedState] = useState(() => {
    try {
      return localStorage.getItem('vaktplan.group') === '1';
    } catch {
      return false;
    }
  });
  const setGrouped = (v: boolean) => {
    setGroupedState(v);
    try {
      localStorage.setItem('vaktplan.group', v ? '1' : '0');
    } catch {}
  };
  const teamed = useMemo(() => sectionsOf(rows, T('Uten team')), [rows]);
  const sections = grouped ? teamed : null;
  const [folded, setFolded] = useState<Set<string>>(new Set());
  const storeBudget = (d: string) => {
    const v = salesFor(forecast.days[d], d < today).value;
    return v == null ? null : (v * labour.target) / 100;
  };
  const deptHead = (sec: Section) => {
    // in Faktisk the clocked hours and cost, as the cards (prototype)
    const f = sectionDays(sec, days, mshifts, cost, storeBudget);
    const shut = folded.has(sec.key);
    return (
      <div className="grow dhead" style={gridStyle} key={`h${sec.key}`} data-testid="dept-head">
        <button
          type="button"
          className="rowh dhb"
          aria-expanded={!shut}
          onClick={() =>
            setFolded((x) => {
              const n = new Set(x);
              if (!n.delete(sec.key)) n.add(sec.key);
              return n;
            })
          }
        >
          <Icon name={shut ? 'chevronRight' : 'chevronDown'} size={14} />
          <span className="nm">
            <b>{sec.name}</b>
            <span>
              {T(sec.rows.length === 1 ? '{n} ansatt, {h} t' : '{n} ansatte, {h} t', {
                n: sec.rows.length,
                h: num(Math.round(f.hours * 10) / 10),
              })}
            </span>
          </span>
        </button>
        {days.map((d) => {
          const x = f.days.get(d);
          const over = money && x?.budget != null && x.cost > x.budget;
          return (
            <div key={d} className={`dsum ${over ? 'over' : ''}`} data-testid="dept-day">
              <b>{T('{h} t', { h: num(Math.round((x?.hours ?? 0) * 10) / 10) })}</b>
              {money && x && (
                <span>
                  {x.budget == null
                    ? `${kr(x.cost)} kr`
                    : T('{a} av {b} kr', { a: kr(x.cost), b: kr(Math.round(x.budget / 100) * 100) })}
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  };
  /** A row's week in the mode the switch reads (#201); the rule entries stay the week's own. */
  const weekOf = (row: Row) =>
    tmode
      ? {
          ...row.week,
          hours: days.reduce(
            (a, d) =>
              a +
              (row.cells.get(d)?.shifts ?? []).reduce((b, s) => {
                const m = asMode(s, tmode);
                return b + (m ? hours(m, row.person) : 0);
              }, 0),
            0,
          ),
        }
      : row.week;
  const rowHead = (row: Row) => {
    const wk = weekOf(row);
    const fr = fair ? (fair.by.get(row.user.id) ?? { eve: 0, wkd: 0 }) : null;
    // before the shifts answer the hours are unknown, not 0 under the agreed week (#272 A-02)
    const cls = noData
      ? ''
      : wk.entries.length
        ? wk.entries[0]?.sev === 'd'
          ? 'd'
          : 'w'
        : // over the agreed week, or well under it: amber, as the person panel colours «x av y» (#272 A-10)
          wk.norm != null && (wk.hours - wk.norm > 0.05 || wk.hours - wk.norm < -8)
          ? 'w'
          : '';
    return (
      <div className="rowh">
        <span className={`av ${avTone(row.user.id)} ${nc.av}`}>{initials(fullName(row.user))}</span>
        <span className="nm">
          <span className={nc.line}>
            <b title={fullName(row.user)}>
              {/* the name opens the person (owner, 2026-09-27), for whoever may see Ansatte: first the
                overview beside the plan, with the full profile a link away (V19 personPanel, review) */}
              {can('ansatte.se') ? (
                <button type="button" className="nmlink" onClick={() => openPerson(row.user.id)}>
                  {fullName(row.user)}
                </button>
              ) : (
                fullName(row.user)
              )}
            </b>
            {/* This showed wk.entries[0] and nothing else: one weekly rule, in full,
              with no sign that a second existed and nothing at all about the day
              rules the person's own shifts break. Same badge as the day column
              now, counting everything, with the list behind it (owner,
              2026-09-21). */}
            {warnPop(
              { kind: 'person', person: row.user.id },
              fullName(row.user),
              days.length > 7
                ? `${fmtDs(days[0] ?? '')}–${fmtDs(days.at(-1) ?? '')}`
                : T('Uke {n}', { n: isoWeek(monday) }),
              (n, sev) => (
                <button
                  type="button"
                  className={`alert ${sev} ${ab.badge} ${sev === 'd' ? ab.d : ab.w}`}
                  data-testid="row-alerts"
                  aria-label={T('{n} regelbrudd {name}', { n, name: fullName(row.user) })}
                >
                  <b>{n}</b>
                </button>
              ),
            )}
          </span>
          {/* the week's hours against the agreed week, and the amount over only when there is one (handoff 2026-10-24 §4.3) */}
          {(() => {
            // the hours against the agreed week, in words on hover (owner 2026-10-28: tooltips on every indicator)
            const what = tmode === 'fakt' ? T('stemplet') : T('planlagt');
            const tip = noData
              ? T('Timene er ikke hentet')
              : wk.norm == null
                ? T('{h} t {m} denne uken', { h: num(Math.round(wk.hours * 10) / 10), m: what })
                : wk.hours - wk.norm > 0.05
                  ? T('{h} t {m} av {n} t avtalt: {o} t over', {
                      h: num(Math.round(wk.hours * 10) / 10),
                      m: what,
                      n: num(wk.norm),
                      o: num(Math.round((wk.hours - wk.norm) * 10) / 10),
                    })
                  : T('{h} t {m} av {n} t avtalt', {
                      h: num(Math.round(wk.hours * 10) / 10),
                      m: what,
                      n: num(wk.norm),
                    });
            return (
              <Tip text={tip}>
                <span className={nc.hwrap}>
                  <span className={`v19h ${cls} ${nc.hours}`} data-testid="row-hours">
                    {noData ? NO_VALUE : num(wk.hours, 1)}
                    {wk.norm != null ? (
                      <span className={nc.norm}>
                        {' '}
                        {T('av')} {num(wk.norm)} t
                      </span>
                    ) : (
                      ' t'
                    )}
                    {!noData && wk.norm != null && wk.hours - wk.norm > 0.05 && (
                      <em className={nc.over} data-testid="row-over">
                        +{num(Math.round((wk.hours - wk.norm) * 10) / 10, 1)} t
                      </em>
                    )}
                  </span>
                  {/* hours against the agreed week as a thin bar, the part over it in amber (#207, owner 2026-10-27);
              its own line beside the figure, not inside its flex, so it spans the column at any width */}
                  {!noData && wk.norm != null && wk.norm > 0 && (
                    <span className={nc.hbar} data-testid="row-hbar" aria-hidden="true">
                      <i
                        style={{
                          width: `${(Math.min(wk.hours, wk.norm) / Math.max(wk.hours, wk.norm)) * 100}%`,
                        }}
                      />
                      {wk.hours > wk.norm && (
                        <i
                          className={nc.ot}
                          style={{ width: `${((wk.hours - wk.norm) / wk.hours) * 100}%` }}
                        />
                      )}
                    </span>
                  )}
                </span>
              </Tip>
            );
          })()}
          {fr && fair && (
            <Tip
              text={T('Siste 4 uker: {e} kveldsvakter (snitt {ae}), {w} helgevakter (snitt {aw})', {
                e: fr.eve,
                ae: num(Math.round(fair.eve * 10) / 10),
                w: fr.wkd,
                aw: num(Math.round(fair.wkd * 10) / 10),
              })}
            >
              <span className={nc.fair} data-testid="row-fair">
                <span className={nc[fairMark(fr.eve, fair.eve)]}>{T('{n} kveld', { n: fr.eve })}</span>
                <span className={nc[fairMark(fr.wkd, fair.wkd)]}>{T('{n} helg', { n: fr.wkd })}</span>
              </span>
            </Tip>
          )}
        </span>
      </div>
    );
  };
  // V19: the corner of the grid is the order, "Ansatt, N" when it is by name
  const sortMenu = (
    <select
      className="v19sort"
      aria-label={T('Sorter medarbeidere')}
      value={sort}
      onChange={(e) => {
        setSort(e.target.value);
        writeSort(e.target.value);
      }}
    >
      {SORT.map(([k, n]) => (
        <option key={k} value={k}>
          {k === 'navn' ? `${T('Ansatt')}, ${rows.length}` : T(n)}
        </option>
      ))}
    </select>
  );
  /** The phone's day list (plan 0029 round 3): the grid's rows, cells and cards for one day. */
  const phoneList = () => {
    const pd = focus();
    const onDay = (d: string) => shifts.filter((s) => dateOf(s) === d && s.user_id != null);
    const open = grid.open.get(pd) ?? [];
    const all = shifts.filter((s) => days.includes(dateOf(s)) && s.user_id != null);
    // hours on a date are the part of each shift on it, as the day timeline counts (retest R05)
    const hoursOn = (d: string) =>
      shifts.filter((s) => s.user_id != null).reduce((a, s) => a + plannedHoursOn(s, d), 0);
    const sev = (d: string) => (grid.colSev.get(d) ?? '') as '' | 'd' | 'w';
    return (
      // its own key: without it React reuses the grid's node, and the grid's measured max-height
      // squeezed the list to a few pixels after a resize to phone width (review F04)
      <div className="pphone" key="phone" data-testid="vaktplan-phone">
        {view === 'month' ? (
          <MonthCal
            days={days}
            pick={pd}
            today={today}
            sev={sev}
            count={(d) => (noData ? NO_VALUE : onDay(d).length + (grid.open.get(d)?.length ?? 0))}
            onPick={setPday}
          />
        ) : (
          <DayStrip days={days} pick={pd} today={today} sev={sev} onPick={setPday} />
        )}
        <div className="pdh">
          <b>
            {DN[dow(pd)]} {fmtDs(pd)}
          </b>
          <small data-testid="day-summary">
            {/* before the shifts answer the day is unknown, not «0 vakter, 0 t» (#272 A-02) */}
            {noData ? NO_VALUE : daySummary([...open, ...rows.flatMap((r) => r.cells.get(pd)?.shifts ?? [])])}
          </small>
          {dayAlerts(pd)}
          <span className="sp" />
          {sortMenu}
        </div>
        {(planning || open.length > 0) && <div className="psec">{T('Ledige vakter')}</div>}
        {!planning && !open.length ? null : noData ? (
          <div className="popen none" data-testid="open-row-empty">
            <span>{NO_VALUE}</span>
          </div>
        ) : open.length ? (
          <>
            <div className="popen" data-testid="open-row">
              {cell(undefined, null, pd)}
            </div>
            {/* the editor right under the shift it edits (issue #153, mobile) */}
            {draft && draft.user_id == null && editor}
          </>
        ) : (
          <div className="popen none" data-testid="open-row-empty">
            <span>{T('Ingen ledige vakter')}</span>
            {editable && (
              <Button small ghost onClick={() => newShift(pd, null)}>
                + {T('Ledig vakt')}
              </Button>
            )}
          </div>
        )}
        <div className="psec">
          {T('Ansatte')}, {rows.length}
        </div>
        <div className="ppeople">
          {rows.map((row) => (
            <div className="pperson r" key={row.user.id} data-testid="person-row">
              {rowHead(row)}
              {cell(row.cells.get(pd), row, pd)}
              {/* a night shift from the day before reaches into this day: said, and it opens (retest R05) */}
              {shifts
                .filter(
                  (x) =>
                    x.user_id === row.user.id &&
                    dateOf(x) < pd &&
                    Date.parse(x.end_time) > Date.parse(osloISO(pd, '00:00')),
                )
                .map((x) => (
                  <button
                    type="button"
                    key={x.id}
                    className="pcont"
                    data-testid="phone-cont"
                    onClick={() => tapShift(x)}
                  >
                    {T('Fortsetter fra {d}: {t}, {h} t i dag', {
                      d: `${DN[dow(dateOf(x))]} ${fmtDs(dateOf(x))}`,
                      t: timeText(x),
                      h: num(Math.round(plannedHoursOn(x, pd) * 10) / 10),
                    })}
                  </button>
                ))}
              {draft && draft.user_id === row.user.id && editor}
            </div>
          ))}
          {/* a new open shift on a day without an open row, or a person filtered away: under the list */}
          {draft &&
            ((draft.user_id == null && !open.length) ||
              (draft.user_id != null && !rows.some((r) => r.user.id === draft.user_id))) &&
            editor}
          {!rows.length && !usersQ.isPending && <p className="hint">{T('Ingen rader matcher filteret.')}</p>}
        </div>
        <div className="psum" data-testid="phone-sum">
          <div>
            <span className="cap">
              {DN[dow(pd)]} {fmtDs(pd)}
            </span>
            <b>{noData ? NO_VALUE : `${num(Math.round(hoursOn(pd) * 10) / 10)} t`}</b>
            {money && !noData && <small>ca. {kr(cost(onDay(pd)))} kr</small>}
          </div>
          <span className="v" />
          <div>
            <span className="cap">
              {view === 'month'
                ? MONTHS[Number(monday.slice(5, 7)) - 1]
                : view === 'two'
                  ? `${T('Uke')} ${isoWeek(monday)}–${isoWeek(last)}`
                  : `${T('Uke')} ${isoWeek(monday)}`}
            </span>
            <b>
              {noData ? NO_VALUE : `${num(Math.round(days.reduce((a, d) => a + hoursOn(d), 0) * 10) / 10)} t`}
            </b>
            {money && !noData && <small>ca. {kr(cost(all))} kr</small>}
          </div>
          {/* no Hugin pill here: it covered a shift and the top bar's Hugin button opens the same panel (#272 A-16) */}
        </div>
      </div>
    );
  };
  const gridStyle = {
    '--nd': nd,
    ...(nd > 7 ? { '--dw': '124px' } : {}),
  } as React.CSSProperties;

  // the shift editor: on a phone the drawer as before; beside the plan it is the panel itself, so the
  // week or the day stays in view while a shift or its activities are edited (review V02)
  // what the editor of a saved shift shows around it and may do: the old summary panel's, in its
  // sections, every write through the page's own route (issue #153)
  const editorActs = (() => {
    const s = draft?.id != null ? shifts.find((x) => x.id === draft.id) : undefined;
    if (!s) return undefined;
    const ctx = panelContextOf(s, grid, offers, swaps, shifts, { t, showHours, me });
    return {
      shift: s,
      around: ctx,
      busy: busyShift(s.id),
      outcome: pubOut?.id === s.id ? pubOut : null,
      lunch: planning
        ? {
            minutes: breakPolicy(tenant)?.autoMinutes ?? 30,
            preset: lunchPreset?.id === s.id ? lunchPreset : null,
            onLunch: (start: string | null, minutes: number | null) =>
              write(s.id, start ? T('Lunsj lagret') : T('Lunsj fjernet'), {
                planned_lunch_start: start,
                planned_lunch_minutes: minutes,
              }),
          }
        : undefined,
      onDecide: decide,
      onOpenAbsence: (a: ApiAbsence) => openAbsence(draftOfAbsence(a)),
      onCover: () => openCover(s.id, dateOf(s), null),
      onApprove: can('timeliste.godkjenne')
        ? (v: boolean, saved?: ApiShift) => void approveOne(saved ?? s, v)
        : undefined,
      onPublish: () => void publishOne(s),
      onUnpublish: () =>
        void write(s.id, T('Publiseringen er trukket. Den ansatte ser ikke vakten lenger.'), {
          published: false,
        }),
      onFree: () => void free(s),
      // the same permission the empty cell's absence choices ask for
      onAbsence:
        s.user_id != null && mayAbsence
          ? () => openAbsence(newAbsenceDraft(s.user_id as number, dateOf(s)), T('Registrer fravær'))
          : undefined,
      // a selection is for publishing, approving or deleting several: offered to who may do one
      onPick:
        editable || can('timeliste.godkjenne')
          ? () =>
              guard(T('Merk flere'), () => {
                setSel(new Set(sel).add(s.id));
                setDraft(null);
              })
          : undefined,
      onMoveTask: (id: number, to: number) =>
        op([s.id, to], T('Aktiviteten er flyttet'), async () => {
          await w.moveTask.mutateAsync({ id, to });
          return undefined;
        }),
      onBoundary: (earlier_id: number, later_id: number, at: string) =>
        op([earlier_id, later_id], T('Skiftet er flyttet'), async () => {
          const r = await w.boundary.mutateAsync({ earlier_id, later_id, at });
          const failed = Object.values(r?.notification_status ?? {}).filter((x) => x === 'failed').length;
          return failed
            ? T('Skiftet er flyttet, men varselet til {n} ansatte feilet.', { n: failed })
            : undefined;
        }),
    };
  })();
  const editor = (
    <ShiftDrawer
      draft={draft}
      tasks={draft?.id != null ? shifts.find((x) => x.id === draft.id)?.taskAssignments : undefined}
      submit={async (id, f) => {
        const before = shifts.find((x) => x.id === id) ?? null;
        const ok = await op([id], T('Lagret'), async () => {
          await f();
          return undefined;
        });
        if (ok && before) setChanged(before);
        return ok;
      }}
      locked={draft?.id != null && busyShift(draft.id)}
      others={
        draft
          ? shifts.filter(
              (x) =>
                Date.parse(x.end_time) > Date.parse(osloISO(draft.date, '00:00')) &&
                Date.parse(x.start_time) < Date.parse(osloISO(addDays(draft.date, 2), '00:00')),
            )
          : []
      }
      // the one reread: it settles a publish outcome and lifts the write locks together
      onReread={async () => {
        if (await rereadShifts()) setPubOut(null);
      }}
      lunch={(() => {
        const s = draft?.id != null ? shifts.find((x) => x.id === draft.id) : undefined;
        return s ? plannedLunch(s) : null;
      })()}
      entries={draft?.id != null ? (grid.entries.get(draft.id) ?? []) : []}
      types={types}
      users={users.filter((u) => u.active_status)}
      onClose={() => guard(T('Avbryt'), () => setDraft(null))}
      onDelete={remove}
      onDuplicate={(id) => {
        // the stored shift, as the card menu copies it; unsaved drawer edits are not part of the copy
        const s = shifts.find((x) => x.id === id);
        if (s) dup(s);
      }}
      inline
      focus={editFocus}
      match={planning ? matchDraft : undefined}
      planning={planning}
      act={editorActs}
    />
  );
  const panelTitle =
    panel?.kind === 'save'
      ? T('Lagre som mal')
      : panel?.kind === 'plan'
        ? T('Planlegg uke {week}', { week: isoWeek(mondayOf(monday)) })
        : panel?.kind === 'move-conflict'
          ? T('Vakten overlapper')
          : panel?.kind === 'day-details'
            ? T('Bemanning')
            : panel?.kind === 'shift'
              ? T('Valgt vakt')
              : panel?.kind === 'review'
                ? T('Gjennomgå')
                : panel?.kind === 'cover'
                  ? T('Finn erstatter')
                  : panel?.kind === 'filter'
                    ? T('Filter')
                    : panel?.kind === 'publish'
                      ? T('Publiser')
                      : panel?.kind === 'person'
                        ? T('Medarbeider')
                        : panel?.kind === 'rule'
                          ? T('Regel')
                          : 'Hugin';
  // copy and templates: Planlegg on a wide screen, folded into ⋯ up to 700 px (issue #86)
  const planItems = [
    {
      key: 'copy',
      label: T('Kopier uke {week}', {
        week: isoWeek(addDays(mondayOf(monday), -7)),
      }),
      onSelect: copyWeek,
    },
    { key: 'save', label: T('Lagre uken som mal'), onSelect: openSave },
    ...(templatesQ.data ?? []).flatMap((t: ApiTemplate) => [
      {
        key: `apply${t.id}`,
        label: `${T('Bruk mal')} «${t.name}» (${t.shifts_count})`,
        onSelect: () => applyTemplate(t.id, t.name),
      },
      {
        key: `del${t.id}`,
        label: `${T('Slett mal')} «${t.name}»`,
        danger: true,
        onSelect: () => removeTemplate(t.id, t.name),
      },
    ]),
  ];
  // up to 700 px the search lives in Filter, so a name typed there counts as a filter on the button
  const filterCount =
    teamF.size + statusF.size + (narrow && q ? 1 : 0) + (byProject && projF != null ? 1 : 0);
  const viaPanel = (it: (typeof planItems)[number]) => ({
    ...it,
    onSelect: () => {
      setPanel(null);
      it.onSelect();
    },
  });
  // Hugin in the planning panel (#163): the person may use the assistant, and the company is a demo, the
  // only companies the subscription engine answers; elsewhere the chat entries are not drawn at all
  const planChat = can('assistent.bruke') && !!tenant?.is_demo;
  const openPlan = () =>
    guard(T('Planlegg'), () => {
      rememberOrigin();
      aiPlan.open();
      aiPlan.setTab('plan');
      setPanel({ kind: 'plan' });
    });
  // the template form's fields and actions, in the side panel on a desktop and the sheet on a phone
  const saveActions = (
    <div style={{ display: 'flex', gap: 8 }}>
      <span className="sp" />
      <Button onClick={closeSave}>{T('Avbryt')}</Button>
      <Button tone="pri" disabled={!!saveForm?.busy} onClick={saveTemplate}>
        {T('Lagre malen')}
        {saveForm ? ` (${sourceShiftsFor(saveForm).length})` : ''}
      </Button>
    </div>
  );
  const saveFields = saveForm ? (
    <>
      <div className="fld">
        <span className="lbl">{T('Navn')}</span>
        <input
          className="inp w"
          aria-label={T('Navn på malen')}
          value={saveForm.name}
          onChange={(e) => setSaveForm({ ...saveForm, name: e.target.value })}
        />
      </div>
      <div className="fld">
        <span className="lbl">{T('Beskrivelse (valgfritt)')}</span>
        <input
          className="inp w"
          aria-label={T('Beskrivelse')}
          value={saveForm.description}
          onChange={(e) => setSaveForm({ ...saveForm, description: e.target.value })}
        />
      </div>
      <div className="fld">
        <span className="lbl">{T('Kildeperiode')}</span>
        <div className="two">
          <DateInput
            className="inp"
            aria-label={T('Kilde fra')}
            value={saveForm.start_date}
            onValueChange={(value) => setSaveForm({ ...saveForm, start_date: value })}
          />
          <DateInput
            className="inp"
            aria-label={T('Kilde til')}
            value={saveForm.end_date}
            onValueChange={(value) => setSaveForm({ ...saveForm, end_date: value })}
          />
        </div>
      </div>
      <div className="fld" data-testid="save-scope">
        <span className="lbl">{T('Hva skal med')}</span>
        <select
          className="inp w"
          aria-label={T('Hva skal med')}
          value={saveForm.scope}
          onChange={(e) =>
            setSaveForm({
              ...saveForm,
              scope: e.target.value as 'window' | 'selected',
            })
          }
        >
          <option value="window">{T('Hele perioden')}</option>
          <option value="selected">{T('Bare de valgte vaktene ({count})', { count: sel.size })}</option>
        </select>
      </div>
      <p className="hint" data-testid="save-scope-count">
        {T('{count} vakter', { count: sourceShiftsFor(saveForm).length })},{' '}
        {T('{count} ledige', { count: sourceShiftsFor(saveForm).filter((x) => x.user_id == null).length })},{' '}
        {T('{count} aktiviteter', {
          count: sourceShiftsFor(saveForm).reduce((n, x) => n + (x.taskAssignments?.length ?? 0), 0),
        })}
      </p>
      {saveForm.problem ? (
        <span role="alert" style={{ color: 'var(--dgr-i)' }}>
          {saveForm.problem}
        </span>
      ) : null}
    </>
  ) : null;
  const panelBody = (
    <>
      {panel?.kind === 'save' && saveForm && (
        <aside className="vpanel" aria-label={T('Lagre som mal')} data-testid="save-panel">
          <div className="vphd">
            <div>
              <h2>{T('Lagre som mal')}</h2>
              <p className="sub">{`${fmtD(saveForm.start_date)} – ${fmtD(saveForm.end_date)}`}</p>
            </div>
            <button type="button" className="link" aria-label={T('Lukk')} onClick={closeSave}>
              <Icon name="close" />
            </button>
          </div>
          {saveFields}
          {saveActions}
        </aside>
      )}
      {panel?.kind === 'plan' && (
        <PlanningPanel
          plan={aiPlan}
          week={isoWeek(mondayOf(monday))}
          scope={[
            tenant?.name,
            `${fmtDs(mondayOf(monday))}–${fmtDs(addDays(mondayOf(monday), 6))}`,
            teamF.size === 1 ? (teams.find((x) => teamF.has(x.id))?.name ?? '') : T('hele teamet'),
          ]
            .filter(Boolean)
            .join(', ')}
          // the ways that were the Planlegg menu, unchanged; the panel steps aside for their own review
          copyItem={viaPanel(planItems[0] as (typeof planItems)[number])}
          templateItems={planItems.slice(1).map(viaPanel)}
          typeName={(id) => types.find((x) => x.id === id)?.name ?? T('Vakt')}
          onSelf={() => {
            setPanel(null);
            newShift(focus(), null);
          }}
          onClose={closePanel}
          onShown={phone ? () => setPanel(null) : undefined}
          chatOn={planChat}
          huginTab={huginDock}
        />
      )}
      {panel?.kind === 'cover' && (
        <CoverPanel
          key={panel.shiftId}
          title={
            coverShift
              ? `${DN[dow(dateOf(coverShift))]} ${fmtDs(dateOf(coverShift))}, ${timeText(coverShift)}`
              : T('Vakten')
          }
          holder={nameOf(coverShift?.user_id)}
          published={!!coverShift?.published}
          candidates={coverList}
          problem={coverProblem}
          onRetry={() => {
            void shiftsQ.refetch();
            void absQ.refetch();
            void availQ.refetch();
          }}
          showHours={showHours}
          editable={editable}
          pick={coverPick}
          onPick={(id) => {
            coverSeen.current = null;
            setCoverPick(id);
          }}
          onApply={async (userId) => {
            const r = await applyCover(panel.shiftId, userId);
            // from the bar: the bar shows the answer on its case and goes on; the panel closes
            if (r.kind === 'saved' && coverDone.current) {
              settleCover(r);
              setPanel(null);
            } else if (r.kind === 'saved') toast(r.text);
            return r;
          }}
          onClose={closePanel}
        />
      )}
      {panel?.kind === 'move-conflict' && (
        <MoveConflict
          key={`${panel.id}-${panel.start}-${panel.end}`}
          id={panel.id}
          userId={panel.userId}
          start={panel.start}
          end={panel.end}
          shifts={shifts}
          onClose={closePanel}
          onEdit={() => {
            const s = shifts.find((x) => x.id === panel.id);
            if (s) openShift(s);
          }}
          onApply={async (start, end) => {
            const source = shifts.find((x) => x.id === panel.id);
            if (!source || !editable) return false;
            if (overlapping(shifts, panel.id, panel.userId, start, end).length) {
              toast(T('Planen er endret. Dette tidsrommet overlapper nå.'));
              return false;
            }
            const ok = await write(panel.id, T('Vakten er flyttet'), {
              ...panel.body,
              start_time: start,
              end_time: end,
              ...movedLunch(source, Date.parse(start) - Date.parse(source.start_time)),
            });
            if (ok) setPanel(null);
            return ok;
          }}
        />
      )}
      {panel?.kind === 'day-details' &&
        (() => {
          const d = panel.date;
          const past = d < today;
          const fig = forecast.days[d];
          const sales = salesFor(fig, past);
          return (
            <DayDetails
              date={d}
              planned={dayHours(d)}
              need={needHours(labour.need, d)}
              gaps={hourGaps(labour.need, d, shifts)}
              budget={
                money
                  ? {
                      cost: cost(shifts.filter((x) => dateOf(x) === d && x.user_id != null)),
                      sales: sales.value,
                      source: past
                        ? T('Omsetning')
                        : sales.from === 'estimate'
                          ? T('Anslag fra tidligere uker')
                          : T('Forventet omsetning'),
                      own: past ? (fig?.actual ?? null) : (fig?.forecast ?? null),
                      past,
                      onSave: (value) => {
                        if (
                          value != null &&
                          (!Number.isFinite(value) || value < 0 || (value > 0 && value < 1000))
                        ) {
                          toast(T('Skriv hele kronebeløpet, f.eks. 32 000.'));
                          return;
                        }
                        saveDaySales.mutate(
                          { day: d, value, forecast: !past },
                          { onError: (err) => toast(errorText(err)) },
                        );
                      },
                    }
                  : undefined
              }
              onDay={() =>
                guard(T('Åpne dagsplan'), () => {
                  setView('day');
                  setMonday(d);
                  setPanel(null);
                })
              }
              onNew={
                editable
                  ? (gap) =>
                      guard(T('Ny vakt'), () => {
                        shownPanel.current = null;
                        setPanel(null);
                        setEditFocus(undefined);
                        setDraft({
                          ...newDraft(d, null, defaultType),
                          ...(gap
                            ? {
                                from: gap.from,
                                to: gap.to === '24:00' ? '00:00' : gap.to,
                              }
                            : {}),
                        });
                      })
                  : undefined
              }
              onClose={closePanel}
            />
          );
        })()}
      {panel?.kind === 'filter' && (
        <FilterPanel
          teams={teams}
          team={[...teamF][0] ?? null}
          onTeam={(id) => setTeamF(id == null ? new Set() : new Set([id]))}
          statuses={planning ? STATUS : STATUS.filter(([k]) => k !== 'upub')}
          status={[...statusF][0] ?? ''}
          onStatus={(k) => setStatusF(k ? new Set([k]) : new Set())}
          sorts={SORT}
          sort={sort}
          onSort={(k) => {
            setSort(k);
            writeSort(k);
          }}
          search={narrow ? { q, onQ: setQ } : undefined}
          showTasks={showTasks}
          onTasks={setShowTasks}
          showAvail={showAvail}
          onAvail={setShowAvail}
          money={showKr}
          showMoney={showMoney}
          onMoney={setShowMoney}
          planning={planning}
          group={teamed ? { on: grouped, onChange: setGrouped } : undefined}
          project={
            byProject
              ? {
                  projects: projects.map((p) => ({
                    id: p.id,
                    label: projectLabel(p),
                  })),
                  value: projF,
                  onChange: setProjF,
                  word: projectWord,
                }
              : undefined
          }
          onReset={() => {
            setTeamF(new Set());
            setStatusF(new Set());
            setQ('');
            setProjF(null);
          }}
          onClose={() => setPanel(null)}
        />
      )}
      {panel?.kind === 'publish' && (
        <PublishPanel
          shifts={shifts.filter((x) => panel.ids.includes(x.id))}
          breaches={
            new Map(
              grid.draftBreaches.map(({ shift: x, entries }) => [x.id, entries.map((e) => ruleText(t, e))]),
            )
          }
          scope={[
            teamF.size
              ? teams
                  .filter((x) => teamF.has(x.id))
                  .map((x) => x.name)
                  .join(', ')
              : T('Hele teamet'),
            ...(statusF.size ? [T(STATUS.find(([k]) => statusF.has(k))?.[1] ?? '')] : []),
            ...(q.trim() ? [`${T('søk')} «${q.trim()}»`] : []),
          ].join(', ')}
          busy={!!busyAction}
          onPublish={async () => {
            await selDo('publish', new Set(panel.ids));
            setPanel(null);
          }}
          onClose={() => setPanel(null)}
        />
      )}
      {panel?.kind === 'ai-all' && (
        <AllProposalsPanel
          cards={(aiQ.data ?? []).filter((c: ActionCard) => hg.gridActions.includes(c.action_id))}
          outside={hg.outside}
          editable={editable}
          busy={aiBusy}
          onUse={async () => {
            await decideAllAi(true);
            setPanel(null);
          }}
          onDrop={async () => {
            await decideAllAi(false);
            setPanel(null);
          }}
          onClose={() => setPanel(null)}
        />
      )}
      {panel?.kind === 'proposal' &&
        (() => {
          const card = (aiQ.data ?? []).find((c: ActionCard) => c.action_id === panel.id);
          return card ? (
            <ProposalPanel
              card={card}
              outside={hg.outside.get(card.action_id) ?? 0}
              editable={editable}
              busy={aiBusy}
              outcome={aiOut}
              onUse={() => decideAi(card.action_id, true)}
              onDrop={() => decideAi(card.action_id, false)}
              onClose={() => setPanel(null)}
            />
          ) : null;
        })()}
      {panel?.kind === 'rule' &&
        (() => {
          const u = users.find((x) => x.id === panel.person);
          if (!u) return null;
          const week = panel.rule.week ?? monday;
          return (
            <RulePanel
              user={u}
              rule={panel.rule}
              shown={monday}
              shifts={shifts.filter(
                (x) => x.user_id === u.id && dateOf(x) >= week && dateOf(x) <= addDays(week, 6),
              )}
              onShift={tapShift}
              onWeek={(m) => guard(T('uke {n}', { n: isoWeek(m) }), () => setMonday(m))}
              onOnly={() => setQ(fullName(u))}
              onClose={closePanel}
            />
          );
        })()}
      {panel?.kind === 'person' &&
        (() => {
          const row = grid.rows.find((r) => r.user.id === panel.id);
          if (!row) return null;
          const u = row.user;
          const fr = fair ? (fair.by.get(u.id) ?? { eve: 0, wkd: 0 }) : null;
          return (
            <PersonPanel
              user={u}
              person={row.person}
              week={mondayOf(days.includes(today) ? today : (days[0] ?? monday))}
              hours={view === 'week' ? weekOf(row).hours : undefined}
              loaded={{ from: addDays(days[0] ?? monday, -1), to: addDays(last, 1) }}
              shifts={shifts.filter((x) => x.user_id === u.id)}
              absences={(absQ.data ?? []).filter((a) => a.user_id === u.id)}
              avail={availQ.data ?? []}
              fair={fr && fair ? { eve: fr.eve, wkd: fr.wkd, avgEve: fair.eve, avgWkd: fair.wkd } : null}
              requests={{ offers, swaps }}
              canPay={can('ansatte.loenn')}
              canMessage={can('vaktplan.endre')}
              canEdit={can('ansatte.endre')}
              editable={editable}
              today={today}
              onShift={tapShift}
              onAbsence={(a) => openAbsence(draftOfAbsence(a))}
              onRule={(e) => openRule(u.id, e)}
              onNew={(d) => newShift(d, u.id, T('Ny vakt for {name}', { name: u.name }))}
              onOnly={() => setQ(fullName(u))}
              onClose={closePanel}
            />
          );
        })()}
      {panel?.kind === 'review' && (
        <ReviewPanel
          items={panel.scope ? reviewItemsOf(panel.scope.of, panel.scope.keys, wpCtx) : review}
          scope={panel.scope?.label}
          onAll={() => setPanel({ kind: 'review' })}
          onOpen={(id) => {
            const x = shifts.find((y) => y.id === id);
            if (x) tapShift(x);
          }}
          onDay={(d) => {
            setView('day');
            setMonday(d);
          }}
          onRule={(person, rule) => openRule(person, rule)}
          advise={(x) => adviceFor(x, adviceCtx)}
          onChoice={applyChoice}
          onPerson={(id) => {
            const u = users.find((x) => x.id === id);
            if (u) setQ(fullName(u));
          }}
          onClose={() => setPanel(null)}
        />
      )}
    </>
  );

  /** whose Saker this is on a shared browser: the company and the person (security review of #286) */
  const sakerOwner = tenant?.id != null && me != null ? `${tenant.id}-${me}` : undefined;
  /** the reads Saker's counts rest on that have not answered or failed: not known clear (RF1, #286 S17) */
  const sakerGaps = gapsOf(followUp.unsure, {
    enabled: can('timerloenn.se'),
    isError: payQ.isError,
    hasData: !!payQ.data,
    loading: payQ.isPending && payQ.fetchStatus !== 'idle',
  });
  /** the team Saker counts, the plan's own team filter (S03, S10) */
  const sakerTeam =
    teamF.size === 1
      ? {
          name: teams.find((x) => teamF.has(x.id))?.name ?? T('Teamet'),
          has: (uid: number) => !!users.find((u) => u.id === uid)?.teams?.some((x) => teamF.has(x.id)),
        }
      : null;
  const reviewEl = (layout: 'bar' | 'panel') => (
    <ReviewBar
      layout={layout}
      laterKey={sakerOwner}
      onAskHugin={huginDock ? (text) => askInHugin(text) : undefined}
      // every open period's tasks; Saker scopes them to the period and team on screen (saker.ts, #286)
      cases={barCases.cases}
      days={days}
      periodLabel={rangeText}
      periodShort={sakerScope.replace(/^(i|den) /, '')}
      team={sakerTeam}
      teams={teams.map((x) => ({ id: x.id, name: x.name }))}
      teamId={teamF.size === 1 ? ([...teamF][0] ?? null) : null}
      weeks={[-2, -1, 0, 1, 2].map((n) => {
        const m = addDays(mondayOf(monday), n * 7);
        return { monday: m, label: weekLabel(m) };
      })}
      monday={mondayOf(monday)}
      onScopeChange={(v) =>
        guard(T('en annen periode'), () => {
          setView('week');
          setMonday(v.monday);
          setTeamF(v.teamId == null ? new Set() : new Set([v.teamId]));
        })
      }
      readOnly={!editable && !can('timeliste.rette') && !can('timeliste.godkjenne') && !mayDecideAbsence}
      firstUse={
        usersQ.isSuccess &&
        !sakerGaps.length &&
        users.length <= 1 &&
        !shifts.length &&
        !barCases.cases.length &&
        !barCases.waiting.length
      }
      today={today}
      nav={sakerNav0}
      onNav={(n) =>
        writeSakerNav(n, {
          monday: mondayOf(monday),
          teamId: teamF.size === 1 ? ([...teamF][0] ?? null) : null,
        })
      }
      onKontroll={(c) =>
        guard(T('Kontroll'), () =>
          navigate(
            kontrollOf(c, teamF.size === 1 ? (teams.find((x) => teamF.has(x.id))?.name ?? null) : null),
          ),
        )
      }
      waiting={barCases.waiting}
      breakdown={breakdown(barCases.cases.filter((c) => !c.date || days.includes(c.date)))}
      loading={followUp.loading}
      // the plan's own read failed: the queue's shift reads wait for it and never start, so nothing about shifts is
      // known; said as a failed read, never as zero (#286 S17)
      failed={followUp.allFailed || (shiftsQ.isError && !shiftsQ.data)}
      // what was read, said as it is: the queue's dates, every pending request, and the period on
      // screen; not "every open period" (review of PR #150, F3)
      scope={T('I perioden du ser')}
      gaps={sakerGaps}
      onRetry={() => {
        followUp.refetch();
        if (payQ.isError) void payQ.refetch();
        // the plan's own read, which the queue's shift reads wait for (#286 S17)
        if (shiftsQ.isError) void shiftsQ.refetch();
      }}
      onAct={actOnCase}
      onShift={(id) => {
        const sh = shifts.find((y) => y.id === id);
        if (sh) openShift(sh, sh.punch_in_time ? { section: 'stempling' } : { punch: 'both' });
      }}
      focus={reviewFocus}
      onScope={setScopeKeys}
      onLink={(to) =>
        guard(T('Åpne'), () => {
          // a link to another week or day of this page (a case in another period, «Se vakten»): the page read its
          // address once, so moving the address alone did nothing (owner 2026-10-28); move the view itself
          const [path, query = ''] = to.split('?');
          const l = path === '/vaktplan' ? fromSearch(new URLSearchParams(query)) : null;
          if (l && (l.week || l.day) && !l.newShift) {
            setView(l.day ? 'day' : 'week');
            setMonday((l.day ?? l.week) as string);
            return;
          }
          navigate(to);
        })
      }
      onOpen={(key) => {
        const x = review.find((y) => y.key === key);
        if (!x) return;
        // the item's own way in, as the review panel opens it
        if (x.shiftId != null) {
          const sh = shifts.find((y) => y.id === x.shiftId);
          if (sh) tapShift(sh);
        } else if (x.day)
          guard(T('Dag'), () => {
            setView('day');
            setMonday(x.day as string);
          });
        else if (x.person != null && x.rule) openRule(x.person, x.rule);
        else if (x.person != null) openPerson(x.person);
      }}
      impact={(c) =>
        c.absence ? <AbsenceImpact c={{ ...c, absence: c.absence }} onShift={openShiftOf} /> : null
      }
      about={(c) => (c.absence ? <AbsenceAbout c={{ ...c, absence: c.absence }} /> : null)}
      extra={(c) => {
        // a week item's own advice; a cover case gets the advice on the absence over its shift.
        // Its hand-ons are «Finn erstatter» above, so only the words stay here (F1)
        const x = itemOfCase(c);
        const advice = x ? adviceFor(x, adviceCtx) : null;
        return x && advice ? (
          <AdviceLine
            advice={{ ...advice, choices: advice.choices.filter((ch) => ch.apply.kind !== 'give') }}
            absence={x.absence}
            onChoice={applyChoice}
          />
        ) : null;
      }}
      onPerson={openPerson}
      onDay={(d) =>
        guard(T('Dag'), () => {
          setView('day');
          setMonday(d);
        })
      }
      onActive={setActiveCase}
      outside={(c) => !!c.date && !days.includes(c.date)}
      onShowPeriod={(c) =>
        c.date &&
        guard(T('Vis riktig periode'), () =>
          setMonday(
            view === 'day'
              ? (c.date as string)
              : view === 'month'
                ? monthStart(c.date as string)
                : mondayOf(c.date as string),
          ),
        )
      }
    />
  );
  // Escape in the side column (#272 A-03, A-R10): narrower than 1101 px it is a sheet over the plan and closes;
  // beside the plan the docked Hugin goes back to Saker, as the floating Hugin closes; focus returns to the opener
  sideEscRef.current = (e: KeyboardEvent) => {
    // the docked Hugin is the column's last stop: Tab from its text box goes round the column, not to the page's
    // skip link at the top (#272 A-S04)
    // as a drawer over the plan (1100 px and down) the keyboard stays inside it too, as a modal's does (#286 S20)
    if (
      e.key === 'Tab' &&
      (sideTab === 'hugin' || window.matchMedia?.(SIDE_SHEET).matches) &&
      sideEl.current &&
      !e.defaultPrevented
    )
      return trapTab(e, sideEl.current);
    if (e.key !== 'Escape' || e.defaultPrevented) return;
    const sheet = !!window.matchMedia?.(SIDE_SHEET).matches;
    if (!sheet && sideTab !== 'hugin') return;
    e.preventDefault();
    const tab = sideTab;
    if (sheet) setSideOpen(false);
    else setSideTab('saker');
    // after the close has drawn: the toolbar's «Saker» or «Spør Hugin» comes back in place of the one pressed
    requestAnimationFrame(() => {
      const o = sideOpener.current;
      (
        (o?.isConnected ? o : null) ??
        document.querySelector<HTMLElement>(`[data-testid="side-open-${tab}"]`) ??
        sideEl.current?.querySelector<HTMLElement>('[role="tab"]')
      )?.focus();
    });
  };
  // the same tasks Saker's «Å følge opp» counts: the period and team on screen, nothing put off, no information (#286)
  const sakerHere = listsOf(
    { cases: barCases.cases, waiting: barCases.waiting },
    { days, team: sakerTeam },
    'current',
    dueDeferrals(deferralsOf(sakerStatesQ.data), today),
  ).open;
  const sakerCount = sakerHere.length;
  // the count wears the most urgent case it holds: red for a breach or blocked pay, amber for a look (owner 2026-10-25)
  // the count is of every case, most of them not a broken rule: amber, never red; the breaches are counted red
  // once, in «Status, n brudd» (#272 area A/D colour)
  const sakerTone = sakerHere.some((c) => c.tone === 'd' || c.tone === 'w') ? 'w' : '';
  // the count is of the cases in the window shown, so it says which (#272 A-22: 30, 37 and 26 with no scope)
  const sakerScope =
    view === 'month'
      ? T('i {month}', { month: MONTHS[Number(monday.slice(5, 7)) - 1] ?? '' })
      : view === 'two'
        ? T('i uke {a}–{b}', { a: isoWeek(monday), b: isoWeek(last) })
        : view === 'day'
          ? T('den {d}', { d: fmtDs(monday) })
          : T('i uke {n}', { n: isoWeek(monday) });
  const sideShell = (
    <aside
      className={`vpanel ${sp.panel}`}
      aria-label={T('Saker og Hugin')}
      data-testid="side-panel"
      ref={sideEl}
      {...(sideSheet ? { role: 'dialog', 'aria-modal': true } : {})}
    >
      <div className={sp.tabs} role="tablist" aria-label={T('Saker eller Hugin')}>
        <button
          type="button"
          role="tab"
          aria-selected={sideTab === 'saker'}
          onClick={() => setSideTab('saker')}
          data-testid="side-tab-saker"
        >
          {T('Saker')}{' '}
          {sakerCount > 0 && (
            <>
              <span className={`${sp.n} ${sakerTone ? sp[sakerTone] : ''}`}>{sakerCount}</span>
              <span className={sp.scope} data-testid="side-saker-scope">
                {sakerScope}
              </span>
            </>
          )}
        </button>
        {huginDock && (
          <button
            type="button"
            role="tab"
            aria-selected={sideTab === 'hugin'}
            onClick={() => setSideTab('hugin')}
            data-testid="side-tab-hugin"
          >
            <HuginMark size={18} />
            {T('Hugin')}
          </button>
        )}
        <button
          type="button"
          className={sp.close}
          aria-label={T('Lukk Saker og Hugin')}
          onClick={() => setSideOpen(false)}
          data-testid="side-close"
        >
          <Icon name="close" />
        </button>
      </div>
      {sideTab === 'hugin' && huginDock ? (
        // the one Hugin conversation (poc-chat PocChatPanel) is drawn here while this tab shows
        <div
          className={sp.dock}
          ref={(el) => useDock.getState().set({ target: el })}
          data-testid="side-hugin-dock"
        />
      ) : (
        <>
          <div className={sp.body}>{reviewEl('panel')}</div>
          {huginDock && (
            <form
              className={sp.ask}
              onSubmit={(e) => {
                e.preventDefault();
                const input = e.currentTarget.elements.namedItem('ask') as HTMLInputElement;
                const text = input.value.trim();
                if (!text) return;
                input.value = '';
                // typed by the manager, so it is sent; the tab opens first, as for askInHugin
                setSideTab('hugin');
                setTimeout(() => openHugin(text), 120);
              }}
            >
              <label className={sp.askIn}>
                <Icon name="sparkle" size={16} />
                <input
                  name="ask"
                  placeholder={T('Spør Hugin, f.eks. «lag neste uke»')}
                  aria-label={T('Spør Hugin')}
                />
              </label>
            </form>
          )}
        </>
      )}
    </aside>
  );

  // «?»: what the marks mean, in a panel over the plan that pushes nothing (owner 2026-10-28: "wtf is this mess")
  const legendBody = (
    <div className="legendpanel">
      {/* The card's colour IS the shift type, and the legend said so in words without ever
                saying which colour was which. The company's own types, from the same query the
                drawer picks from (owner's mockup, 2026-09-21). */}
      {types.length > 0 && (
        <section className="lgcol">
          <h6>{T('Vakttyper')}</h6>
          <div className={types.length > 4 ? 'lgtypes two' : 'lgtypes'}>
            {types.map((ty) => (
              <div className="lg2" key={ty.id}>
                <span className="sw2">
                  <s
                    className="lgs"
                    style={{
                      background: ty.color_code ?? 'var(--blue-f)',
                      borderColor: ty.color_code ?? 'var(--blue-l)',
                    }}
                  />
                </span>
                <span className="lgname" title={ty.name}>
                  {ty.name}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
      <section className="lgcol">
        <h6>{T('Kort')}</h6>
        <div className="lg2">
          <span className="sw2">
            <s className="lgs" style={{ borderLeftColor: 'var(--blue-l)' }} />
          </span>
          {T('Vakt, farge er vakttypen')}
        </div>
        {planning && (
          <>
            <div className="lg2">
              <span className="sw2">
                <s className="lgs open" />
              </span>
              {T('Ledig vakt, ingen har den')}
            </div>
            <div className="lg2">
              <span className="sw2">
                <s className="lgs draft" />
              </span>
              {T('Utkast, ikke publisert')}
            </div>
          </>
        )}
        <div className="lg2">
          <span className="sw2">
            <s
              className="lgs"
              style={{
                background: 'var(--slate-f)',
                borderColor: 'var(--slate-f)',
              }}
            />
          </span>
          {T('Ikke tilgjengelig')}
        </div>
      </section>
      {/* the marks the cards, rows and days draw today (owner 2026-10-28: "update the question mark") */}
      <section className="lgcol">
        <h6>{T('Merker på kortet')}</h6>
        <p className="lgnote">
          {T('Ett merke per kort, i hjørnet. Hold over et merke for å se hva det betyr.')}
        </p>
        <div className="lg2">
          <span className="sw2">
            <i className="lgdot" style={{ background: 'var(--dgr-l)' }} />
          </span>
          {T('Rød prikk: regelbrudd, eller vakten er over uten stempling. Trykk for å se saken.')}
        </div>
        <div className="lg2">
          <span className="sw2">
            <i className="lgdot" style={{ border: '2px solid var(--warn-i)' }} />
          </span>
          {T('Gul ring: vakten er jobbet og venter på godkjenning. Hold over kortet for ✓.')}
        </div>
        <div className="lg2">
          <span className="sw2" style={{ color: 'var(--ok-l)' }}>
            <StatusIcon kind="approved" />
          </span>
          {T('Godkjent, timene går til lønn')}
        </div>
        <div className="lg2">
          <span className="sw2">
            <StatusIcon kind="offered" />
          </span>
          {T('Tilbudt eller Bytte: den ansatte vil gi bort eller bytte vakten')}
        </div>
        <div className="lg2">
          <span className="sw2">
            <Icon name="list" />
          </span>
          {T('Aktiviteter på vakten, med antall')}
        </div>
        {staffView && (
          <div className="lg2">
            <span className="sw2" style={{ width: 'auto', minWidth: 24, whiteSpace: 'nowrap' }}>
              <b style={{ color: 'var(--warn-i)', fontSize: 11 }}>+39 min</b>
            </span>
            {T('Faktisk: stemplet tid mot planen')}
          </div>
        )}
      </section>
      <section className="lgcol">
        <h6>{T('Dager og ansatte')}</h6>
        <div className="lg2">
          <span className="sw2">
            <b
              style={{
                padding: '0 5px',
                border: '1px solid var(--dgr-l)',
                borderRadius: 5,
                background: 'var(--dgr-f)',
                color: 'var(--dgr-i)',
                fontSize: 11,
              }}
            >
              2
            </b>
          </span>
          {T('Saker på dagen eller den ansatte. Trykk for å se dem i Saker.')}
        </div>
        <div className="lg2">
          <span className="sw2">
            <i className="lgbar" />
          </span>
          {T('Timer mot avtalt uke, oransje er over')}
        </div>
        <div className="lg2">
          <span className="sw2" style={{ width: 'auto', minWidth: 24, whiteSpace: 'nowrap', fontSize: 11 }}>
            {T('{n} kveld', { n: 3 })}
          </span>
          {T('Kvelds- og helgevakter siste 4 uker, oransje er mer enn snittet')}
        </div>
      </section>
      <section className="lgcol">
        <h6>{T('Reglene som sjekkes')}</h6>
        {RULES.map(([a, b]) => (
          <div className="pr" key={a}>
            <span>{T(a)}</span> <b>{T(b)}</b>
          </div>
        ))}
      </section>
    </div>
  );

  return (
    <div className="v19" style={{ '--vpw': `${side.width}px` } as React.CSSProperties}>
      {printer.on && (
        <PrintSheet
          title={planning ? T('Vaktplan') : T('Uka')}
          company={tenant?.name ?? ''}
          days={days}
          portrait={view === 'month'}
          rows={paperRows(days, rows, sections, grid.open, shifts)}
        />
      )}
      {/* V19 (owner, 2026-10-08): the title with the company under it; the week, I dag and the view on the right */}
      {/* v19bar: only print's hook (ui/print hides it), so the whole toolbar stays off paper */}
      <div className={`v19bar ${tb.toolbar}`} data-testid="vtoolbar">
        <div className={`vt ${tb.title}`}>
          <h1>{planning ? T('Vaktplan') : T('Uka')}</h1>
          <span className="sub sr-only">{tenant?.name ?? ''}</span>
        </div>
        {/* Vaktplan C (#280, owner 2026-11-01): the period, the views and the actions in one deck; the filters in
          their own band under it */}
        <div className={tb.deck}>
          <div className={tb.period}>
            {/* the shared period control (#154), as on every page that steps through time */}
            <PeriodControl
              className={tb.stepper ?? ''}
              period={view}
              month={view === 'month'}
              value={view === 'month' ? monday.slice(0, 7) : monday}
              onChange={(v) =>
                guard(T('en annen periode'), () =>
                  setMonday(view === 'month' ? `${v}-01` : view === 'day' ? v : mondayOf(v)),
                )
              }
              prevLabel={T('Forrige')}
              nextLabel={T('Neste')}
              onPrev={() => step(-1)}
              onNext={() => step(1)}
            >
              <span className="lbl" data-testid="range-label">
                {rangeText}
              </span>
            </PeriodControl>
            <button type="button" className={tb.tool} onClick={goToday} title={T('I dag')}>
              <Icon name="calendar" />
              <span className={tb.lbl}>{T('I dag')}</span>
            </button>
            {narrow || splitOn ? (
              <select
                className={`inp ${tb.view}`}
                data-testid="viewpick"
                aria-label={T('Visning')}
                value={view}
                onChange={(e) => switchView(e.target.value as 'day' | 'week' | 'two' | 'month')}
              >
                <option value="day">{T('Dag')}</option>
                <option value="week">{T('Uke')}</option>
                <option value="two">{T('To uker')}</option>
                <option value="month">{T('Måned')}</option>
              </select>
            ) : (
              // the four views side by side (handoff 2026-10-24 §4.1, owner 2026-10-25 Q5: To uker stays)
              // biome-ignore lint/a11y/useSemanticElements: a row of toggle buttons, not a form's fieldset
              <div className={tb.seg} role="group" aria-label={T('Visning')} data-testid="viewpick">
                {(
                  [
                    ['day', T('Dag')],
                    ['week', T('Uke')],
                    ['two', T('To uker')],
                    ['month', T('Måned')],
                  ] as const
                ).map(([k, label]) => (
                  <button
                    type="button"
                    key={k}
                    data-view={k}
                    aria-pressed={view === k}
                    onClick={() => view !== k && switchView(k)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            {tmode && (
              // biome-ignore lint/a11y/useSemanticElements: a row of toggle buttons, not a form's fieldset
              <div className={tb.seg} role="group" aria-label={T('Tid')} data-testid="tmode">
                {(
                  [
                    ['plan', T('Plan'), T('Planlagt tid')],
                    ['fakt', T('Faktisk'), T('Stemplet tid')],
                  ] as const
                ).map(([k, label, tip]) => (
                  <button
                    type="button"
                    key={k}
                    data-tmode={k}
                    title={tip}
                    aria-pressed={tmode === k}
                    onClick={() => setTMode(k)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* V19 toolbar: find, Filter and availability on the left; Planlegg, Ny vakt, Publiser and ⋯ on the right */}
          <div className={tb.bar} data-testid="filters">
            {/* up to 700 px (issue #86) the search sits in Filter, availability is Filter's checkbox and
            Planlegg's items are in ⋯, so the toolbar is one row: Filter, Ny vakt, Publiser, ⋯ */}
            {narrow && (
              <div className={tb.filters}>
                <button
                  type="button"
                  className={`${tb.tool} ${panel?.kind === 'filter' || filterCount ? tb.on : tb.quiet}`}
                  aria-expanded={panel?.kind === 'filter'}
                  onClick={() =>
                    guard(T('Filter'), () => setPanel(panel?.kind === 'filter' ? null : { kind: 'filter' }))
                  }
                >
                  <Icon name="sliders" />
                  {T('Filter')}
                  {filterCount > 0 && <span className={tb.count}>{filterCount}</span>}
                </button>
              </div>
            )}
            <div className={tb.actions}>
              {/* no Hugin button here (issue #84): the floating Spør Hugin launcher is the one entry, as on every page */}
              {editable && !narrow && (
                <button
                  type="button"
                  className={`${tb.tool} ${panel?.kind === 'plan' ? tb.on : ''}`}
                  aria-expanded={panel?.kind === 'plan'}
                  onClick={openPlan}
                  title={T('Planlegg')}
                >
                  <Icon name="calendar" />
                  <span className={tb.lbl}>{T('Planlegg')}</span>
                </button>
              )}
              {editable && (
                <span className={tb.split}>
                  <button
                    type="button"
                    className={tb.tool}
                    onClick={() => newShift(focus(), null)}
                    title={T('Ny vakt')}
                  >
                    <Icon name="plus" />
                    <span className={tb.lbl}>{T('Ny vakt')}</span>
                  </button>
                  {huginDock && (
                    <button
                      type="button"
                      className={`${tb.tool} ${tb.hug}`}
                      aria-label={T('Lag vakter med Hugin')}
                      title={T('Lag vakter med Hugin')}
                      data-testid="new-with-hugin"
                      onClick={() =>
                        askInHugin(T('Lag vakter for uke {n} etter reglene', { n: isoWeek(monday) }))
                      }
                    >
                      <Icon name="sparkle" />
                    </button>
                  )}
                </span>
              )}
              {mayRegister && (
                <button type="button" className={tb.tool} onClick={() => addRegistration(focus(), null)}>
                  <Icon name="plus" />
                  {T('Legg til registrering')}
                </button>
              )}
              {editable && visibleDrafts.size > 0 && (
                <button
                  type="button"
                  className={`${tb.tool} ${tb.pub}`}
                  onClick={publishWindow}
                  disabled={!!busyAction || unresolved.length > 0}
                  aria-label={T('Publiser {n} utkast', { n: visibleDrafts.size })}
                  title={(() => {
                    const n = grid.draftBreaches.filter(({ shift: x }) => visibleDrafts.has(x.id)).length;
                    return n > 0 ? T('{n} med brudd', { n }) : undefined;
                  })()}
                >
                  <Icon name="upload" />
                  {T('Publiser {n}', { n: visibleDrafts.size })}
                </button>
              )}
              {sideDock.current && !sideOpen && (
                <>
                  {huginDock && (
                    <button
                      type="button"
                      className={tb.tool}
                      data-testid="side-open-hugin"
                      onClick={() => {
                        rememberSideOpener();
                        setSideOpen(true);
                        setSideTab('hugin');
                      }}
                    >
                      <Icon name="sparkle" />
                      {T('Spør Hugin')}
                    </button>
                  )}
                  <button
                    type="button"
                    className={tb.tool}
                    data-testid="side-open-saker"
                    onClick={() => {
                      rememberSideOpener();
                      setSideOpen(true);
                      setSideTab('saker');
                    }}
                  >
                    {T('Saker')}
                    {sakerCount > 0 && (
                      <span
                        className={`${tb.count} ${sakerTone ? tb[sakerTone] : ''}`}
                        data-tone={sakerTone || 'i'}
                      >
                        {sakerCount}
                      </span>
                    )}
                  </button>
                </>
              )}
              {/* the schedule's less frequent actions, in one menu (plan 0029) */}
              <Menu
                trigger={
                  <button type="button" className={`${tb.tool} ${tb.more}`} aria-label={T('Mer')}>
                    <Icon name="more" />
                  </button>
                }
                items={[
                  ...(editable && narrow
                    ? [{ key: 'plan', label: T('Planlegg uka'), onSelect: openPlan }, ...planItems]
                    : []),
                  {
                    key: 'legend',
                    label: legend ? T('Skjul tegnforklaring') : T('Tegnforklaring'),
                    // after the menu has closed, so its closing does not dismiss the legend's panel
                    onSelect: () => setTimeout(() => setLegend(!legend), 0),
                  },
                  // the days and rows on screen, on paper (issue #49)
                  { key: 'print', label: T('Skriv ut'), onSelect: printer.print },
                  ...((editable || (!planning && can('timeliste.godkjenne'))) && visibleShiftIds.size > 0
                    ? [
                        {
                          key: 'all',
                          label:
                            sel.size === visibleShiftIds.size
                              ? T('Fjern merking')
                              : T('Merk alle {count}', {
                                  count: visibleShiftIds.size,
                                }),
                          onSelect: () =>
                            setSel(sel.size === visibleShiftIds.size ? new Set() : new Set(visibleShiftIds)),
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          </div>
        </div>
        {!narrow && (
          // biome-ignore lint/a11y/useSemanticElements: a band of filter controls, not a form's fieldset
          <div className={tb.band} role="group" aria-labelledby="vp-filters-label">
            <span className={tb.bandLabel} id="vp-filters-label">
              <Icon name="sliders" />
              {T('Filtre')}
            </span>
            <FilterBar
              statuses={planning ? STATUS : STATUS.filter(([k]) => k !== 'upub')}
              status={statusF}
              counts={noData ? undefined : statusCounts}
              onStatus={(k) => {
                const next = new Set(statusF);
                if (!next.delete(k)) next.add(k);
                setStatusF(next);
              }}
              teams={teams}
              team={[...teamF][0] ?? null}
              onTeam={(id) => setTeamF(id == null ? new Set() : new Set([id]))}
              project={
                byProject
                  ? {
                      projects: projects.map((p) => ({ id: p.id, label: projectLabel(p) })),
                      value: projF,
                      onChange: setProjF,
                      word: projectWord,
                    }
                  : undefined
              }
              sorts={SORT}
              sort={sort}
              onSort={(k) => {
                setSort(k);
                writeSort(k);
              }}
              view={[
                { key: 'tasks', label: T('Vis aktiviteter'), on: showTasks, onChange: setShowTasks },
                ...(teamed
                  ? [{ key: 'group', label: T('Grupper etter avdeling'), on: grouped, onChange: setGrouped }]
                  : []),
                ...(planning
                  ? [
                      {
                        key: 'avail',
                        label: T('Vis tilgjengelighet'),
                        on: showAvail,
                        onChange: setShowAvail,
                        count: availCount,
                      },
                    ]
                  : []),
                ...(showKr
                  ? [
                      {
                        key: 'money',
                        label: T('Vis budsjett og kostnader'),
                        on: showMoney,
                        onChange: setShowMoney,
                      },
                    ]
                  : []),
              ]}
              active={filterCount}
              onClear={() => {
                setTeamF(new Set());
                setStatusF(new Set());
                setProjF(null);
              }}
            />
          </div>
        )}
      </div>
      <FirstPlanHint on="vaktplan" published={shifts.some((x) => x.published)} />
      {/* announced, and in Norwegian: the server's own word for a 500 («Internal») said nothing (#272 A-S08) */}
      {shiftsQ.isError && (
        <div className="al dgr" role="alert">
          <span>
            <Icon name="stop" />
          </span>
          <span>{T('Kunne ikke hente vaktene.')}</span>
          <Button small onClick={() => shiftsQ.refetch()}>
            {T('Prøv igjen')}
          </Button>
        </div>
      )}

      <div className="vp">
        {/* a phone has no «?» in the grid's corner: the legend opens in the page there */}
        {legend && phone && legendBody}
        {hg.gridActions.length > 0 && (
          <div className="al info vreviewbar" data-testid="ai-bar">
            <span>
              <HuginMark size={18} />
            </span>
            <span>
              <b>{T('Hugins forslag')}</b> {T('{n} endringer, ikke lagret', { n: hg.gridActions.length })}
              {hg.elsewhere > 0 ? `, ${T('{n} til i Hugin', { n: hg.elsewhere })}` : ''}
              {hg.unplaced > 0 ? `, ${T('{n} vises bare i Hugin', { n: hg.unplaced })}` : ''}
              {(() => {
                const n = [...hg.outside.values()].reduce((a, b) => a + b, 0);
                return n > 0 ? `, ${T('{n} utenfor det du ser nå', { n })}` : '';
              })()}
            </span>
            <span className="sp" />
            <Button small onClick={() => window.dispatchEvent(new Event('timefly:ai-open'))}>
              {T('Se i Hugin')}
            </Button>
            {editable && (
              <>
                <Button small disabled={aiBusy} onClick={() => decideAllAi(false)}>
                  {T('Forkast')}
                </Button>
                <Button
                  small
                  tone="pri"
                  disabled={aiBusy}
                  onClick={() => guard('Hugin', () => setPanel({ kind: 'ai-all' }))}
                >
                  {T('Bruk endringene')}
                </Button>
              </>
            )}
          </div>
        )}
        {/* new shifts whose save got no answer, from this browser or another (#157) */}
        {can('vaktplan.endre') && tenant?.id != null && <PendingCreates tenant={tenant.id} users={users} />}
        {/* one attention bar for the whole company's open work (issue #147); the #146 popovers stay
            the scoped way in, beside the cards */}
        {planning && (phone || !sideDock.current) && reviewEl('bar')}
        {view !== 'day' && !phone ? <p className="vphint">{T('Sveip sidelengs for flere dager')} ›</p> : null}
        <div
          onKeyDown={(e) => {
            // a shift, a person or Planlegg beside the plan keeps Tab inside it until Escape (#272 A-R09)
            const vside = [...e.currentTarget.children].find((c) => c.classList.contains('vside')) as
              | HTMLElement
              | undefined;
            if (e.key === 'Tab' && (panel || draft) && !phone && vside?.contains(e.target as Node))
              return trapTab(e, vside);
            if (e.key !== 'Escape' || !(panel || draft) || phone || e.defaultPrevented) return;
            if (!e.currentTarget.contains(e.target as Node)) return;
            e.preventDefault();
            // the shift editor closes the same way as a panel, asking first when something is unsaved
            if (panel) closePanel();
            else guard(T('Lukk'), () => setDraft(null));
          }}
          ref={splitRef}
          style={{ '--vpw': `${side.width}px` } as React.CSSProperties}
          className={`vsplit${splitOn ? ' on' : ''}`}
        >
          <div className={`vmain${noData ? ' loading' : ''}`} aria-busy={loading}>
            {loading && <output className="vloading">{T('Henter vaktene …')}</output>}
            {/* inside the plan's column, so the Saker column beside it never covers them (V19-10, AP-desktop) */}
            {/* one place at a time: while a proposal is on the grid its strip speaks for the plan (owner 18.10) */}
            {evaluation && evaluation.added.length > 0 && !aiPlan.shown && (
              <HuginEval ev={evaluation} onChoice={applyChoice} onClose={() => setChanged(null)} />
            )}
            {/* in planning Hugin's suggestions are the review bar's cases (#147 F3); «Uka» has no bar, so
                its holiday and ended-week notes stay here */}
            {!planning && (
              <HuginNotes list={notes} onChoice={applyChoice} onOpen={huginOpen} folded={phone} />
            )}
            {planning && (
              <ChangeStrip
                plan={aiPlan}
                week={isoWeek(mondayOf(monday))}
                onChanges={() => {
                  rememberOrigin();
                  aiPlan.setStep('changes');
                  setPanel({ kind: 'plan' });
                }}
              />
            )}
            {view === 'day' ? (
              <>
                <DayView
                  date={monday}
                  users={rows.map((r) => r.user)}
                  shifts={byProject ? shifts.filter(inProject) : shifts}
                  editable={editable}
                  onMove={moveShift}
                  onResize={resizeShift}
                  busy={busyShift}
                  onOpen={(s, f) => (f && editable ? openShift(s, f) : tapShift(s))}
                  onEdit={(s) => openShift(s, { add: true })}
                  onActivity={laneChange}
                  selected={draft?.id ?? (panel?.kind === 'shift' ? panel.id : null)}
                  lunchMinutes={breakPolicy(tenant)?.autoMinutes ?? 30}
                  phone={phone}
                  squeezed={splitOn && !phone}
                  // the suggested lunch made the planned one (owner, 2026-10-18): the same write as the
                  // editor's lunch row, planned_lunch_start and planned_lunch_minutes
                  onPlanLunch={(s, start, minutes) =>
                    void write(s.id, T('Lunsj lagret'), {
                      planned_lunch_start: start,
                      planned_lunch_minutes: minutes,
                    })
                  }
                  // two overlapping activities parted in one click (owner, 2026-10-18): the task's new
                  // times in the same PUT and taskAssignments body the editor saves, under the same lock
                  onFix={(s, id, start, end) => {
                    const t = (s.taskAssignments ?? []).find((x) => x.id === id);
                    if (!t) return;
                    void write(s.id, T('Aktiviteten er flyttet'), {
                      taskAssignments: tasksBodyOf(s.taskAssignments ?? [], {
                        changed: {
                          [id]: { ...defOf(t), has_time_range: true, start_time: start, end_time: end },
                        },
                        added: [],
                      }),
                    });
                  }}
                  band={
                    draft && editFocus?.start && editFocus?.end
                      ? { start: editFocus.start, end: editFocus.end }
                      : null
                  }
                  // the + on an empty row (gap A1): the company's shift types, one tap places the
                  // type's times as a draft through the same create and its recovery (createOnce)
                  onAdd={(userId, p) =>
                    emptyCell({ userId, date: monday, x: p.x, y: p.y, who: nameOf(userId) ?? '' })
                  }
                  entriesFor={(s) => (
                    <ShiftCard
                      s={s}
                      entries={grid.entries.get(s.id) ?? []}
                      picked={sel.has(s.id)}
                      onOpen={() => tapShift(s)}
                      onPick={() => setSel(toggle(sel, s.id))}
                      menu={menuFor(s)}
                      compact
                      v19
                      tasks={showTasks}
                      selected={(panel?.kind === 'shift' && panel.id === s.id) || draft?.id === s.id}
                      project={byProject ? projectOf(s) : undefined}
                    />
                  )}
                />
                {planning && !phone && (
                  // «Tall for dagen» (owner 2026-10-27): the week's and the month's rules, one line, the hours chart
                  <DayFigures
                    date={monday}
                    today={today}
                    need={labour.need}
                    target={labour.target}
                    targetSet={!!labour.settings?.wage_target_pct?.set}
                    shifts={shifts}
                    cost={costWeek.days}
                    sales={forecast.days}
                    money={showKr}
                    tenantId={tenant?.id}
                    onSales={() => switchView('week')}
                  />
                )}
              </>
            ) : phone ? (
              phoneList()
            ) : (
              <>
                <div
                  className="gscroll"
                  key="grid"
                  ref={scroller}
                  onScroll={(e) => moreDays(e.currentTarget)}
                  style={
                    {
                      '--nw': `${nameCol.width}px`,
                      '--wkcol': weekCol ? '150px' : '0px',
                    } as React.CSSProperties
                  }
                >
                  <div className={nd > 7 ? 'grid narrow' : 'grid'} data-testid="vaktplan">
                    <div className="grow gh" style={gridStyle}>
                      {/* the search in the grid's corner (handoff 2026-10-24 §4.2); the order is Filter's */}
                      <div className={tb.corner}>
                        <label className={tb.find}>
                          <Icon name="search" />
                          <input
                            ref={searchRef}
                            aria-keyshortcuts="/"
                            type="search"
                            placeholder={T('Finn ansatt')}
                            aria-label={T('Finn ansatt')}
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                          />
                          <kbd>/</kbd>
                        </label>
                        <Popover.Root open={legend} onOpenChange={setLegend}>
                          <Popover.Trigger asChild>
                            <button
                              type="button"
                              className={tb.help}
                              aria-label={legend ? T('Skjul tegnforklaring') : T('Tegnforklaring')}
                              title={T('Tegnforklaring')}
                              aria-pressed={legend}
                            >
                              ?
                            </button>
                          </Popover.Trigger>
                          <Popover.Portal>
                            <Popover.Content
                              className="lgpop"
                              // opened from ⋯, the menu hands focus back to its button as it closes; that move
                              // must not close the legend (a click outside still does)
                              onFocusOutside={(e) => e.preventDefault()}
                              align="start"
                              sideOffset={8}
                              collisionPadding={16}
                              aria-label={T('Tegnforklaring')}
                            >
                              {legendBody}
                            </Popover.Content>
                          </Popover.Portal>
                        </Popover.Root>
                        <NameDivider width={nameCol.width} onWidth={nameCol.set} />
                      </div>
                      {days.map((d) => {
                        const cs = grid.colSev.get(d) ?? '';
                        return (
                          <div
                            key={d}
                            data-day={d}
                            className={`${dow(d) >= 5 ? 'we' : ''}${d === today ? ' today' : ''}${weekLine(d) ? ' mon' : ''}${nd <= 7 ? ` ${dh.cell}` : ''}`}
                          >
                            <span
                              className={`colstrip ${cs === 'd' ? 'danger' : cs === 'w' ? 'warn' : ''}`}
                            />
                            {nd > 7 && (dow(d) === 0 || d === days[0]) && (
                              <span className="wkno">{T('Uke {n}', { n: isoWeek(d) })}</span>
                            )}
                            {/* V19: the day over its date, and the rule count beside them */}
                            {nd <= 7 ? (
                              <span className={`v19day ${dh.day}`}>
                                {DN[dow(d)]} {Number(d.slice(8, 10))}.
                              </span>
                            ) : (
                              <span className="v19day">
                                <small>{DN[dow(d)]}</small>
                                <b>{fmtDs(d)}</b>
                              </span>
                            )}
                            {dayAlerts(d)}
                          </div>
                        );
                      })}
                      {weekCol && <div className="v19wk">{T('Uka')}</div>}
                    </div>
                    {/* A week with nothing open is one slim line, not a row of empty cells; the full
                  row returns as soon as one shift has no person (plan 0025, Vaktplan). */}
                    {openAll.length || hg.ghosts.some((g) => g.userId == null) || gapDays.length ? (
                      <div className="grow r" style={gridStyle} data-testid="open-row">
                        <div className="rowh">
                          <span className="av openav" aria-hidden="true">
                            <Icon name="user" />
                          </span>
                          <span className="nm">
                            <b>{T('Ledige vakter')}</b>
                            <span>
                              {openAll.length
                                ? T('{n} å dekke', { n: openAll.length })
                                : T('Hull i bemanningen vises her')}
                            </span>
                          </span>
                        </div>
                        {days.map((d) => cell(undefined, null, d, gapChip(d)))}
                      </div>
                    ) : !planning || noData ? null : (
                      <div className="openslim" data-testid="open-row-empty">
                        <span className="stick">
                          <span>{T('Ingen ledige vakter denne uken')}</span>
                          {editable && (
                            <Button small ghost onClick={() => newShift(focus(), null)}>
                              + {T('Ledig vakt')}
                            </Button>
                          )}
                        </span>
                      </div>
                    )}
                    {(sections ?? [{ key: 'all', name: '', rows }]).map((sec) => (
                      <Fragment key={sec.key}>
                        {sections && deptHead(sec)}
                        {!folded.has(sec.key) &&
                          sec.rows.map((row) => (
                            <div
                              className="grow r pr"
                              style={gridStyle}
                              key={row.user.id}
                              data-testid="person-row"
                            >
                              {rowHead(row)}
                              {days.map((d) => cell(row.cells.get(d), row, d))}
                              {weekCol && (
                                <div className="v19wk">
                                  {noData ? NO_VALUE : `${num(Math.round(weekOf(row).hours * 10) / 10)} t`}
                                </div>
                              )}
                            </div>
                          ))}
                      </Fragment>
                    ))}
                    {!rows.length && !usersQ.isPending && (
                      // no one left: said and undone in one place (#280 VC-05); before, `.v19 .grp` hid the line;
                      // with no search and no filter there is nobody to plan yet, which is not a miss (VC-06)
                      <output className="vnomatch" data-testid="no-match">
                        {q || filterCount ? (
                          <>
                            <b>
                              {q ? T('Ingen ansatte passer søket.') : T('Ingen ansatte passer filteret.')}
                            </b>
                            <Button
                              small
                              onClick={() => {
                                setQ('');
                                setTeamF(new Set());
                                setStatusF(new Set());
                                setProjF(null);
                              }}
                            >
                              {T('Tøm søk og filtre')}
                            </Button>
                          </>
                        ) : (
                          <b>{T('Ingen ansatte å vise.')}</b>
                        )}
                      </output>
                    )}

                    {planning && <BasisRow plan={aiPlan} days={days} style={gridStyle} />}
                    {staffView && planning ? (
                      // «Tall for uka» (owner 2026-10-27, prototype foot-e): replaces the week charts and the bottom row
                      <WeekFigures
                        unknown={noData}
                        days={days}
                        today={today}
                        tmode={tmode}
                        need={labour.need}
                        target={labour.target}
                        targetSet={!!labour.settings?.wage_target_pct?.set}
                        shifts={mshifts}
                        planHours={dayHours}
                        openHours={footTotals().open}
                        weekCol={weekCol}
                        cost={costWeek.days}
                        sales={forecast.days}
                        lastWeekPct={
                          view === 'week' && costPrev.ready
                            ? weekFigures(
                                Array.from({ length: 7 }, (_, i) => addDays(prevFrom, i)),
                                today,
                                costPrev.days,
                                salesPrev.days,
                              ).forecast
                            : null
                        }
                        money={showKr}
                        tenantId={tenant?.id}
                        style={gridStyle}
                        dayClass={(d) => (weekLine(d) ? 'mon' : undefined)}
                        onSales={(day, value, isForecast) =>
                          saveDaySales.mutate(
                            { day, value, forecast: isForecast },
                            { onError: (err) => toast(errorText(err)) },
                          )
                        }
                        onOpenDay={(d) =>
                          guard(T('Bemanning'), () => setPanel({ kind: 'day-details', date: d }))
                        }
                        onHugin={
                          huginDock
                            ? () =>
                                askInHugin(
                                  T(
                                    'Hvordan får jeg uke {n} ned til {p} % lønn av omsetning etter reglene?',
                                    {
                                      n: isoWeek(monday),
                                      p: labour.target,
                                    },
                                  ),
                                )
                            : null
                        }
                      />
                    ) : (
                      <div className="grow totals" style={gridStyle}>
                        <div>
                          <span className="lab">{T('Sum')}</span>
                          <span className="big">
                            {noData
                              ? NO_VALUE
                              : `${num(Math.round(days.reduce((a, d) => a + dayHours(d), 0) * 10) / 10)} t`}
                          </span>
                          {money && !noData && (
                            <span
                              className="sub"
                              title={T(
                                'Lønn pluss {hol} % feriepenger, {aga} % arbeidsgiveravgift og {pen} % pensjon',
                                {
                                  hol: rates.holidayPct,
                                  aga: rates.agaPct,
                                  pen: rates.pensionPct,
                                },
                              )}
                            >
                              ca.{' '}
                              {kr(cost(shifts.filter((s) => days.includes(dateOf(s)) && s.user_id != null)))}{' '}
                              kr
                            </span>
                          )}
                        </div>
                        {days.map((d) => {
                          const ds = shifts.filter((s) => dateOf(s) === d && s.user_id != null);
                          return (
                            <div key={d} className={weekLine(d) ? 'mon' : undefined}>
                              <span className="big">
                                {noData ? NO_VALUE : `${num(Math.round(dayHours(d) * 10) / 10)} t`}
                              </span>
                              {money && !noData && <span className="sub">{kr(cost(ds))} kr</span>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
                {view === 'month' && planning && (
                  // «Tall for måneden» (owner 2026-10-27): the week's rules, one column per week
                  <MonthFigures
                    days={days}
                    today={today}
                    need={labour.need}
                    target={labour.target}
                    targetSet={!!labour.settings?.wage_target_pct?.set}
                    shifts={shifts}
                    cost={costMonth.days}
                    sales={forecast.days}
                    money={showKr}
                    tenantId={tenant?.id}
                    onSales={() => switchView('week')}
                  />
                )}
                {/* PowerOffice's budget keeps its own chart; the word «budsjett» is PowerOffice's (owner) */}
                {view === 'month' && planning && showKr && monthBudget != null && (
                  <MonthBurn
                    days={days}
                    today={today}
                    cost={costMonth.days}
                    budget={monthBudget}
                    open={burnOpen}
                    onFold={() => setBurnOpen(!burnOpen)}
                  />
                )}
              </>
            )}
            {view !== 'day' && !phone && !(staffView && planning) && (
              // V19 footer: the week in hours, in words, and the legend; in the planning week its figures are
              // in the bottom row's first cell and the legend behind «?» in the grid's corner
              <div className="v19foot" data-testid="week-foot">
                {(() => {
                  const { on, held, open } = footTotals();
                  const t1 = (h: number) => (noData ? NO_VALUE : `${num(Math.round(h * 10) / 10)} t`);
                  // handoff 2026-10-08: aligned totals with labels, not a sentence
                  return (
                    <dl>
                      <div>
                        <dt>{nd > 7 ? T('Perioden, på ansatte') : T('Uken, på ansatte')}</dt>
                        <dd>{t1(held)}</dd>
                      </div>
                      {planning && (
                        <div>
                          <dt>{T('I ledige vakter')}</dt>
                          <dd>{t1(open)}</dd>
                        </div>
                      )}
                      <div>
                        <dt>{T('Totalt')}</dt>
                        <dd>{t1(held + open)}</dd>
                      </div>
                      <div>
                        <dt>{T('Viste vakter')}</dt>
                        <dd>{noData ? NO_VALUE : on.length}</dd>
                      </div>
                      <div>
                        <dt>{T('Utvalg')}</dt>
                        <dd>
                          {[
                            teamF.size
                              ? teams
                                  .filter((x) => teamF.has(x.id))
                                  .map((x) => x.name)
                                  .join(', ')
                              : T('Hele teamet'),
                            ...(q.trim() ? [`«${q.trim()}»`] : []),
                          ].join(', ')}
                        </dd>
                      </div>
                      {money && (
                        <div>
                          <dt>{T('Lønnskostnad, ansatte')}</dt>
                          <dd>ca. {kr(cost(on.filter((x) => x.user_id != null)))} kr</dd>
                        </div>
                      )}
                    </dl>
                  );
                })()}
                <button type="button" className="link" onClick={() => setLegend(!legend)}>
                  {legend ? T('Skjul tegnforklaring') : T('Tegnforklaring')}
                </button>
              </div>
            )}
          </div>
          {/* the line between plan and panel: drag or keys to resize (issue #153, SP22 to SP24) */}
          {splitOn && !phone && (
            <PanelDivider width={side.width} min={side.min} max={side.max} onWidth={side.set} />
          )}
          {/* on a phone the same panels are a modal sheet with a title, a focus trap and focus
              return; beside the plan they stay nonmodal (review F05). Beside the plan the panel is
              as tall as the plan and scrolls inside it (SP24 to SP27) */}
          <div className="vside">
            {absDraft ? (
              <AbsenceDrawer docked draft={absDraft} shifts={shifts} onClose={() => setAbsDraft(null)} />
            ) : phone ? (
              // the day view has no list to put the editor under, so on a phone it is the same sheet
              // as the panels: a tapped shift, «Ny vakt» and «+ Legg til aktivitet» all open it (M1)
              <Dialog.Root
                open={!!panel || daySheet}
                onOpenChange={(o) => {
                  if (o) return;
                  if (panel) closePanel();
                  else guard(T('Lukk'), () => setDraft(null));
                }}
              >
                <Dialog.Portal>
                  <Dialog.Overlay className="scrim" />
                  <Dialog.Content
                    className="vsheet"
                    aria-describedby={undefined}
                    onOpenAutoFocus={(e) => {
                      e.preventDefault();
                      const box = e.currentTarget as HTMLElement;
                      box.querySelector<HTMLElement>('.vphd [tabindex="-1"], .vphd button')?.focus();
                    }}
                  >
                    <Dialog.Title className="sr">
                      {daySheet && !panel
                        ? draft?.id != null
                          ? T('Rediger vakt')
                          : T('Ny vakt')
                        : panelTitle}
                    </Dialog.Title>
                    {daySheet && !panel ? editor : panelBody}
                  </Dialog.Content>
                </Dialog.Portal>
              </Dialog.Root>
            ) : (
              <>
                {sakerOn && sideSheet && !(panel || draft) && (
                  // the drawer's scrim: a press beside it closes it, as a modal's does; Escape is the keyboard's way (#286)
                  // a span: the sheet rule in styles.css positions every div in the column
                  // biome-ignore lint/a11y/useKeyWithClickEvents: a pointer-only backdrop; Escape closes the drawer
                  <span className={sp.scrim} aria-hidden="true" onClick={() => setSideOpen(false)} />
                )}
                {sakerOn && (
                  <div className={sp.keep} hidden={!!(panel || draft)}>
                    {sideShell}
                  </div>
                )}
                {draft ? editor : panelBody}
              </>
            )}
          </div>
        </div>
      </div>
      {lock.unsure.size > 0 && (
        <div className="selbar" data-testid="unsurebar">
          <output>
            {lock.unsure.size === 1
              ? T('1 vakt uten svar. Les på nytt før du endrer den igjen.')
              : T('{count} vakter uten svar. Les på nytt før du endrer dem igjen.', {
                  count: lock.unsure.size,
                })}
          </output>
          <span className="sp" />
          <Button onClick={rereadShifts}>
            <Icon name="refresh" />
            {T('Les på nytt')}
          </Button>
        </div>
      )}
      {(sel.size > 0 || ledger) && (
        <div className="selbar" data-testid="selbar">
          <b>{T('{count} valgt', { count: sel.size })}</b>
          {ledger ? (
            <output data-testid="bulk-result">
              {T('{count} utført', { count: ledger.ok })}
              {ledger.unchanged ? `, ${T('{count} uendret', { count: ledger.unchanged })}` : ''}
              {ledger.failed.length
                ? `, ${T('{count} feilet', { count: ledger.failed.length })}: ${ledger.failed
                    .slice(0, 3)
                    .map((x) => `#${x.id} ${x.message}`)
                    .join('; ')}`
                : ''}
              {ledger.unknown
                ? `, ${T('{count} uten svar. Les på nytt før du prøver igjen.', { count: ledger.unknown })}`
                : ''}
              {ledger.noticeFailed
                ? `. ${T('{count} publisert, men varselet til den ansatte feilet.', { count: ledger.noticeFailed })}`
                : ''}
            </output>
          ) : null}
          <span className="sp" />
          {unresolved.length ? (
            <Button onClick={rereadWindow}>
              <Icon name="refresh" />
              {T('Les på nytt')}
            </Button>
          ) : null}
          <Button
            onClick={() => {
              setSel(new Set());
              setLedger(null);
            }}
          >
            {T('Avbryt')}
          </Button>
          {planning && (
            <Button disabled={!!busyAction || unresolved.length > 0} onClick={() => selDo('publish')}>
              <Icon name="upload" />
              {T('Publiser')}
            </Button>
          )}
          <Button disabled={!!busyAction || unresolved.length > 0} onClick={() => selDo('approve')}>
            <Icon name="check" />
            {T('Godkjenn')}
          </Button>
          <Button disabled={!!busyAction || unresolved.length > 0} onClick={() => selDo('delete')}>
            <Icon name="trash" />
            {T('Slett')}
          </Button>
        </div>
      )}
      {quick && (
        <Quick
          at={quick}
          types={types}
          usage={usage}
          onPick={quickMake}
          project={
            projectsOn
              ? {
                  projects: projects.filter((p) => p.active),
                  required: projectRequired,
                  word: projectWord,
                }
              : undefined
          }
          onMore={() => {
            setQuick(null);
            newShift(
              quick.date,
              quick.userId,
              T('ny vakt for {who} {d}', {
                who: quick.who,
                d: fmtDs(quick.date),
              }),
            );
          }}
          {...(quick.userId != null && mayAbsence
            ? {
                absenceTypes: absenceTypes,
                onAbsence: (t: ApiAbsenceType) => {
                  openAbsence(
                    newAbsenceDraft(quick.userId as number, quick.date, t.id),
                    T('Registrer fravær'),
                  );
                  setQuick(null);
                },
              }
            : {})}
          onClose={closeQuick}
        />
      )}
      <Drawer
        open={!!saveForm && phone}
        onClose={() => setSaveForm(null)}
        title={T('Lagre som mal')}
        sub={saveForm ? `${fmtD(saveForm.start_date)} – ${fmtD(saveForm.end_date)}` : ''}
        footer={saveActions}
      >
        {saveFields}
      </Drawer>
      <PlanDrawer
        open={!!plan}
        title={plan?.title ?? ''}
        sub={
          plan
            ? `${fmtD(plan.kind === 'copy' ? (plan.copy as CopyWindowBody).target_start_date : (plan.apply as ApplyTemplateBody).target_start_date)} – ${fmtD(plan.kind === 'copy' ? (plan.copy as CopyWindowBody).target_end_date : (plan.apply as ApplyTemplateBody).target_end_date)}`
            : ''
        }
        plan={plan?.plan ?? null}
        busy={!!plan?.busy}
        info={plan?.info}
        problem={plan?.problem}
        confirmLabel={
          plan?.confirmed_absent
            ? T('Lag kopien nå')
            : plan?.kind === 'copy'
              ? T('Kopier som utkast')
              : T('Legg inn som utkast')
        }
        onCheck={plan?.unknown ? checkStatus : undefined}
        onConfirm={confirmPlan}
        onClose={() => dispatch({ type: 'close' })}
        options={
          plan ? (
            <>
              {plan.kind === 'copy' && plan.copy ? (
                <div className="fld" data-testid="plan-source-dates">
                  <span className="lbl">{T('Kopier fra')}</span>
                  <div className="two">
                    <DateInput
                      className="inp"
                      aria-label={T('Kilde fra')}
                      value={plan.copy.source_start_date}
                      onValueChange={(value) =>
                        preview({
                          ...plan,
                          copy: {
                            ...(plan.copy as CopyWindowBody),
                            source_start_date: value,
                          },
                        })
                      }
                    />
                    <DateInput
                      className="inp"
                      aria-label={T('Kilde til')}
                      value={plan.copy.source_end_date}
                      onValueChange={(value) =>
                        preview({
                          ...plan,
                          copy: {
                            ...(plan.copy as CopyWindowBody),
                            source_end_date: value,
                          },
                        })
                      }
                    />
                  </div>
                </div>
              ) : null}
              <div className="fld" data-testid="plan-target-dates">
                <span className="lbl">{T('Legg inn i')}</span>
                <div className="two">
                  <DateInput
                    className="inp"
                    aria-label={T('Mål fra')}
                    value={
                      plan.kind === 'copy'
                        ? (plan.copy as CopyWindowBody).target_start_date
                        : (plan.apply as ApplyTemplateBody).target_start_date
                    }
                    onValueChange={(value) =>
                      preview(
                        plan.kind === 'copy'
                          ? {
                              ...plan,
                              copy: {
                                ...(plan.copy as CopyWindowBody),
                                target_start_date: value,
                              },
                            }
                          : {
                              ...plan,
                              apply: {
                                ...(plan.apply as ApplyTemplateBody),
                                target_start_date: value,
                              },
                            },
                      )
                    }
                  />
                  <DateInput
                    className="inp"
                    aria-label={T('Mål til')}
                    value={
                      plan.kind === 'copy'
                        ? (plan.copy as CopyWindowBody).target_end_date
                        : (plan.apply as ApplyTemplateBody).target_end_date
                    }
                    onValueChange={(value) =>
                      preview(
                        plan.kind === 'copy'
                          ? {
                              ...plan,
                              copy: {
                                ...(plan.copy as CopyWindowBody),
                                target_end_date: value,
                              },
                            }
                          : {
                              ...plan,
                              apply: {
                                ...(plan.apply as ApplyTemplateBody),
                                target_end_date: value,
                              },
                            },
                      )
                    }
                  />
                </div>
              </div>
              <div className="fld" data-testid="plan-options">
                <label>
                  <input
                    className="cbx"
                    type="checkbox"
                    checked={
                      !!(plan.kind === 'copy'
                        ? plan.copy?.create_open_shifts
                        : plan.apply?.create_open_shifts)
                    }
                    onChange={(e) =>
                      preview(
                        plan.kind === 'copy'
                          ? {
                              ...plan,
                              copy: {
                                ...(plan.copy as CopyWindowBody),
                                create_open_shifts: e.target.checked,
                              },
                            }
                          : {
                              ...plan,
                              apply: {
                                ...(plan.apply as ApplyTemplateBody),
                                create_open_shifts: e.target.checked,
                              },
                            },
                      )
                    }
                  />
                  {T('Opprett ledige vakter')}
                </label>
                <label>
                  <input
                    className="cbx"
                    type="checkbox"
                    checked={
                      !!(plan.kind === 'copy'
                        ? plan.copy?.published_sources_only
                        : plan.apply?.published_sources_only)
                    }
                    onChange={(e) =>
                      preview(
                        plan.kind === 'copy'
                          ? {
                              ...plan,
                              copy: {
                                ...(plan.copy as CopyWindowBody),
                                published_sources_only: e.target.checked,
                              },
                            }
                          : {
                              ...plan,
                              apply: {
                                ...(plan.apply as ApplyTemplateBody),
                                published_sources_only: e.target.checked,
                              },
                            },
                      )
                    }
                  />
                  {T('Bruk bare publiserte kildevakter')}
                </label>
              </div>
            </>
          ) : null
        }
      />
    </div>
  );
}
